import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import {
  alpha,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Card,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import * as XLSX from 'xlsx';
import { useHistory, useLocation } from 'react-router-dom';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import RouteRoundedIcon from '@mui/icons-material/RouteRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import LocalGasStationRoundedIcon from '@mui/icons-material/LocalGasStationRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import DirectionsRoundedIcon from '@mui/icons-material/DirectionsRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import SkipNextRoundedIcon from '@mui/icons-material/SkipNextRounded';
import SkipPreviousRoundedIcon from '@mui/icons-material/SkipPreviousRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import GroupWorkRoundedIcon from '@mui/icons-material/GroupWorkRounded';
import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import SummarizeRoundedIcon from '@mui/icons-material/SummarizeRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ScreenHeader from 'src/common/ScreenHeader';
import ReactECharts from 'src/common/ReactECharts';
import { APIService } from 'src/api/api';
import ROUTE from 'src/utils/routeConstants';
import { DATE_FORMAT } from 'src/utils/constants';
import {
  DEFAULT_TIMEZONE,
  getStoredUserTimezone,
  normalizeTimezone,
  nowInTimezone,
  parseLocalDateTimeToUnix
} from 'src/utils/timezoneUtils';

const STATUS_META = {
  active: {
    label: 'Active Vehicles',
    shortLabel: 'Active',
    color: '#10B981',
    accent: '#065F46'
  },
  idle: {
    label: 'Idle Vehicles',
    shortLabel: 'Idle',
    color: '#F59E0B',
    accent: '#92400E'
  },
  offline: {
    label: 'Offline Vehicles',
    shortLabel: 'Offline',
    color: '#EF4444',
    accent: '#991B1B'
  }
};

const formatRelativeMinutes = (minutes) => {
  if (!Number.isFinite(minutes)) return 'No sync';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours < 24) return remainingMinutes ? `${hours}h ${remainingMinutes}m ago` : `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const formatDuration = (minutes) => {
  if (!Number.isFinite(minutes) || !minutes) return '0 min';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

const formatRefreshStatusLabel = (timestamp, nowValue) => {
  if (!timestamp) return 'Live data not refreshed yet';
  const lastRefreshMoment = moment(timestamp);
  const currentMoment = moment(nowValue);
  const elapsedSeconds = Math.max(0, currentMoment.diff(lastRefreshMoment, 'seconds'));

  if (elapsedSeconds < 15) return 'Updated just now';
  if (elapsedSeconds < 60) return `Updated ${elapsedSeconds}s ago`;

  const elapsedMinutes = currentMoment.diff(lastRefreshMoment, 'minutes');
  if (elapsedMinutes < 60) return `Updated ${elapsedMinutes} min ago`;

  return `Updated ${lastRefreshMoment.format('DD MMM, hh:mm A')}`;
};

const FLEET_MANAGEMENT_CONTEXT_STORAGE_KEY = 'fleet-management:selected-context:v1';
const FLEET_CONTEXT_QUERY_KEYS = {
  vehicle: 'fleetVehicle',
  report: 'fleetTab',
  preset: 'fleetPreset',
  from: 'fleetFrom',
  to: 'fleetTo'
};

const REPORT_TABS = [
  {
    value: 'analytics',
    label: 'Dashboard',
    icon: InsightsRoundedIcon,
    requiresDateFilter: false
  },
  {
    value: 'statistics',
    label: 'Statistics',
    icon: SummarizeRoundedIcon,
    requiresDateFilter: true
  },
  {
    value: 'track',
    label: 'Track',
    icon: RouteRoundedIcon,
    requiresDateFilter: true
  },
  {
    value: 'trackFuel',
    label: 'Track + Fuel',
    icon: TimelineRoundedIcon,
    requiresDateFilter: true
  },
  {
    value: 'location',
    label: 'Location',
    icon: PlaceRoundedIcon,
    requiresDateFilter: false
  },
  {
    value: 'fuel',
    label: 'Refueling and Draining',
    icon: LocalGasStationRoundedIcon,
    requiresDateFilter: true
  },
  {
    value: 'tripReport',
    label: 'Trip Report',
    icon: ReceiptLongRoundedIcon,
    requiresDateFilter: true
  },
  {
    value: 'currentRuns',
    label: 'Current Runs',
    icon: DirectionsRoundedIcon,
    requiresDateFilter: false
  },
  {
    value: 'events',
    label: 'Events',
    icon: EventRoundedIcon,
    requiresDateFilter: true
  },
  {
    value: 'groupWork',
    label: 'Group Work',
    icon: GroupWorkRoundedIcon,
    requiresDateFilter: true
  },
  {
    value: 'logReport',
    label: 'Log Report',
    icon: ReceiptLongRoundedIcon,
    requiresDateFilter: true
  },
  {
    value: 'dailyReadingSummary',
    label: 'Summary',
    icon: SummarizeRoundedIcon,
    requiresDateFilter: true
  }
];

const DEFAULT_REPORT_TAB = 'analytics';
const DATE_TIME_INPUT_FORMAT = 'YYYY-MM-DDTHH:mm';
const MAX_DATE_RANGE_DAYS = 60;
const ANALYTICS_PERIOD_OPTIONS = [7, 15, 30];
const ANALYTICS_DASHBOARD_RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: '7', label: '7 days', days: 7 },
  { value: '15', label: '15 days', days: 15 },
  { value: '30', label: '30 days', days: 30 }
];
const ANALYTICS_AUTO_REFRESH_INTERVAL_MS = 120000;
const DATE_RANGE_PRESETS = [
  { value: 'lastHour', label: 'Last 1 hour' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7Days', label: 'Last 7 Days' },
  { value: 'last30Days', label: 'Last 30 Days' }
];

const OFFLINE_SYNC_THRESHOLD_MINUTES = 30;

const normalizeRegistrationNumber = (value) => String(value || '').trim().toUpperCase();

const normalizeCurrentStateStatus = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && !value.trim()) return null;
  if (Number(value) === 1) return 'active';
  if (Number(value) === 2) return 'idle';
  if ([0, 3].includes(Number(value))) return 'offline';
  const normalizedValue = String(value).trim().toLowerCase();

  if (['active', 'moving', 'move', 'running', 'load', 'green'].includes(normalizedValue)) return 'active';
  if (['idle', 'idling', 'orange'].includes(normalizedValue)) return 'idle';
  if (['offline', 'inactive', 'no_data', 'no data', 'nodata', '-', 'gray', 'grey', 'red'].includes(normalizedValue)) return 'offline';
  return null;
};

const getDateTimeRangeForPreset = (preset, timezoneName = getStoredUserTimezone()) => {
  const now = nowInTimezone(timezoneName);
  if (preset === 'lastHour') {
    return {
      fromDateTime: now.clone().subtract(1, 'hour').startOf('minute').format(DATE_TIME_INPUT_FORMAT),
      toDateTime: now.format(DATE_TIME_INPUT_FORMAT)
    };
  }
  if (preset === 'yesterday') {
    const yesterday = now.clone().subtract(1, 'day');
    return {
      fromDateTime: yesterday.clone().startOf('day').format(DATE_TIME_INPUT_FORMAT),
      toDateTime: yesterday.endOf('day').format(DATE_TIME_INPUT_FORMAT)
    };
  }
  if (preset === 'last7Days') {
    return {
      fromDateTime: now.clone().subtract(7, 'days').startOf('minute').format(DATE_TIME_INPUT_FORMAT),
      toDateTime: now.format(DATE_TIME_INPUT_FORMAT)
    };
  }
  if (preset === 'last30Days') {
    return {
      fromDateTime: now.clone().subtract(30, 'days').startOf('minute').format(DATE_TIME_INPUT_FORMAT),
      toDateTime: now.format(DATE_TIME_INPUT_FORMAT)
    };
  }
  return {
    fromDateTime: now.clone().startOf('day').format(DATE_TIME_INPUT_FORMAT),
    toDateTime: now.format(DATE_TIME_INPUT_FORMAT)
  };
};

const getAnalyticsDashboardDateRange = (rangeValue, timezoneName = getStoredUserTimezone()) => {
  const now = nowInTimezone(timezoneName);
  const rangeConfig = ANALYTICS_DASHBOARD_RANGE_OPTIONS.find((option) => option.value === rangeValue);
  if (!rangeConfig?.days) {
    return {
      fromDateTime: now.clone().startOf('day').format(DATE_TIME_INPUT_FORMAT),
      toDateTime: now.format(DATE_TIME_INPUT_FORMAT)
    };
  }

  return {
    fromDateTime: now.clone().subtract(rangeConfig.days, 'days').startOf('day').format(DATE_TIME_INPUT_FORMAT),
    toDateTime: now.format(DATE_TIME_INPUT_FORMAT)
  };
};

const getAnalyticsDashboardRangeLabel = (rangeValue) => (
  ANALYTICS_DASHBOARD_RANGE_OPTIONS.find((option) => option.value === rangeValue)?.label || 'Today'
);

const toUnixSeconds = (dateTimeValue, timezoneName = getStoredUserTimezone()) => (
  parseLocalDateTimeToUnix(dateTimeValue, DATE_TIME_INPUT_FORMAT, timezoneName)
);

const getEffectiveFleetTimezone = (selectedVehicle, backendVehicle) => normalizeTimezone(
  selectedVehicle?.timezone
    || selectedVehicle?.siteTimezone
    || selectedVehicle?.site_timezone
    || backendVehicle?.timezone
    || backendVehicle?.siteTimezone
    || backendVehicle?.site_timezone
    || backendVehicle?.site?.timezone
    || getStoredUserTimezone()
    || DEFAULT_TIMEZONE
);

const isUnavailableMetric = (value) => {
  if (value === null || value === undefined || value === '') return true;
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? (parsedValue === -10000 || parsedValue === -1) : false;
};

const formatDecimalValue = (value, decimals = 1) => {
  if (isUnavailableMetric(value)) return '--';
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return '--';
  return parsedValue.toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

const formatTenthsValue = (value, decimals = 1) => {
  if (isUnavailableMetric(value)) return '--';
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return '--';
  return formatDecimalValue(parsedValue / 10, decimals);
};

const formatHhMmSs = (valueInSeconds) => {
  if (isUnavailableMetric(valueInSeconds)) return '--';
  const totalSeconds = Math.max(0, Math.floor(Number(valueInSeconds)));
  if (!Number.isFinite(totalSeconds)) return '--';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const formatDurationWithPercent = (seconds, percent) => {
  const durationText = formatHhMmSs(seconds);
  const percentText = formatDecimalValue(percent, 1);
  if (durationText === '--') return '--';
  if (percentText === '--') return durationText;
  return `${durationText} (${percentText} %)`;
};

const resolveStatisticsDurationPercent = ({
  seconds,
  rawPercent,
  fromDateTime,
  toDateTime
}) => {
  if (isUnavailableMetric(seconds)) return rawPercent;

  const fromMoment = moment(fromDateTime, DATE_TIME_INPUT_FORMAT, true);
  const toMoment = moment(toDateTime, DATE_TIME_INPUT_FORMAT, true);
  if (!fromMoment.isValid() || !toMoment.isValid()) return rawPercent;
  if (!fromMoment.isSame(toMoment, 'day')) return rawPercent;
  if (!fromMoment.clone().startOf('day').isSame(fromMoment)) return rawPercent;

  const fullDaySeconds = fromMoment.clone().endOf('day').diff(fromMoment, 'seconds') + 1;
  if (!Number.isFinite(fullDaySeconds) || fullDaySeconds <= 0) return rawPercent;

  const parsedSeconds = Number(seconds);
  if (!Number.isFinite(parsedSeconds) || parsedSeconds < 0) return rawPercent;
  return (parsedSeconds / fullDaySeconds) * 100;
};

const normalizeFleetStatistics = (response) => {
  const responseRoot = response?.result || {};
  const periodMileage = responseRoot?.cmw || null;
  const vehicleType = responseRoot?.vehicleType || '';
  const derivedStartReading = responseRoot?.derivedStartReading ?? null;
  const derivedEndReading = responseRoot?.derivedEndReading ?? null;
  const derivedReadingUnit = responseRoot?.derivedReadingUnit || '';
  const legacyRoot = response?.result?.data || {};
  const newRoot = response?.result?.results?.data?.[0] || null;

  if (newRoot && Object.keys(newRoot).length) {
    const vehicleData = newRoot?.vehicleDataList?.[0] || null;
    const hasVehicleData = Boolean(vehicleData && Object.keys(vehicleData).length);
    return {
      hasData: hasVehicleData || Boolean(Object.keys(newRoot).length),
      vehicleData: hasVehicleData ? vehicleData : null,
      totals: newRoot,
      periodMileage: periodMileage || newRoot?.cmw || null,
      vehicleType: vehicleType || newRoot?.vehicleType || '',
      derivedStartReading: derivedStartReading ?? newRoot?.derivedStartReading ?? null,
      derivedEndReading: derivedEndReading ?? newRoot?.derivedEndReading ?? null,
      derivedReadingUnit: derivedReadingUnit || newRoot?.derivedReadingUnit || ''
    };
  }

  const vehicleData = legacyRoot?.vehicleDataList?.[0] || null;
  const totals = legacyRoot?.totalFuel || null;
  const hasVehicleData = Boolean(vehicleData && Object.keys(vehicleData).length);
  const hasTotalsData = Boolean(totals && Object.keys(totals).length);

  return {
    hasData: hasVehicleData || hasTotalsData,
    vehicleData: hasVehicleData ? vehicleData : null,
    totals: hasTotalsData ? totals : null,
    periodMileage: periodMileage || vehicleData?.cmw || legacyRoot?.cmw || null,
    vehicleType,
    derivedStartReading,
    derivedEndReading,
    derivedReadingUnit
  };
};

const DEFAULT_LOCATION_MAP_CENTER = { lat: 13.0827, lng: 80.2707 };
const LOCATION_MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };
const TRACK_MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };
const TRACK_CLICKABLE_MARKERS_LIMIT = 48;
const TRACK_EVENT_MARKERS_LIMIT = 24;
const TRACK_IGNITION_GROUP_DISTANCE_KM = 0.15;
const TRACK_MAP_PLAYBACK_BASE_INTERVAL_MS = 800;
const TRACK_MAP_PLAYBACK_MIN_INTERVAL_MS = 120;
const TRACK_PLAYBACK_SPEED_OPTIONS = [
  { value: 1, label: '1x' },
  { value: 2, label: '2x' },
  { value: 4, label: '4x' },
  { value: 8, label: '8x' }
];
const FUEL_EVENTS_PAGE_SIZE = 50;
const TRIP_REPORT_PAGE_SIZE = 100;
const CURRENT_RUNS_PAGE_SIZE = 50;
const EVENTS_PAGE_SIZE = 100;
const GROUP_WORK_PAGE_SIZE = 50;
const LOG_REPORT_PAGE_SIZE = 100;
const LOG_REPORT_MAIN_ENGINE_RPM_THRESHOLD = 1000;
const LOG_REPORT_SECONDARY_ENGINE_DEFAULT_THRESHOLD = 8;
const DEFAULT_FUEL_REPORT_LOCALE = 'en';
const OMNICOMM_UNIX_EPOCH_OFFSET_SECONDS = 1230768000;
const TRIP_RUN_STATUS_META = {
  3: {
    label: 'Completed',
    color: '#166534',
    background: alpha('#16A34A', 0.12)
  },
  10: {
    label: 'Completed with violations',
    color: '#9A3412',
    background: alpha('#EA580C', 0.14)
  },
  4: {
    label: 'In progress',
    color: '#1D4ED8',
    background: alpha('#2563EB', 0.12)
  }
};

const TRACK_EVENT_META = {
  1: { label: 'Refuel start', shortLabel: 'Refuel start', color: '#16A34A' },
  2: { label: 'Refuel end', shortLabel: 'Refuel end', color: '#15803D' },
  3: { label: 'Drain start', shortLabel: 'Drain start', color: '#DC2626' },
  4: { label: 'Drain end', shortLabel: 'Drain end', color: '#B91C1C' },
  5: { label: 'Ignition on', shortLabel: 'Ignition on', color: '#2563EB' },
  6: { label: 'Ignition off', shortLabel: 'Ignition off', color: '#1D4ED8' },
  14: { label: 'Overspeed start', shortLabel: 'Overspeed start', color: '#EA580C' },
  30: { label: 'Overspeed end', shortLabel: 'Overspeed end', color: '#C2410C' },
  42: { label: 'Stop start', shortLabel: 'Stop start', color: '#7C3AED' },
  43: { label: 'Stop end', shortLabel: 'Stop end', color: '#6D28D9' },
  97: { label: 'Parking start', shortLabel: 'Parking start', color: '#0891B2' },
  98: { label: 'Parking end', shortLabel: 'Parking end', color: '#0E7490' },
  100: { label: 'Parking start (ignition)', shortLabel: 'Parking start', color: '#0F766E' },
  13: { label: 'Parking end (ignition)', shortLabel: 'Parking end', color: '#115E59' },
  22: { label: 'Option equipment on', shortLabel: 'Equipment on', color: '#4F46E5' },
  23: { label: 'Option equipment off', shortLabel: 'Equipment off', color: '#4338CA' },
  122: { label: 'Additional equipment on', shortLabel: 'Equipment on', color: '#4F46E5' },
  123: { label: 'Additional equipment off', shortLabel: 'Equipment off', color: '#4338CA' },
  222: { label: 'Additional equipment on', shortLabel: 'Equipment on', color: '#4F46E5' },
  223: { label: 'Additional equipment off', shortLabel: 'Equipment off', color: '#4338CA' },
  322: { label: 'Additional equipment on', shortLabel: 'Equipment on', color: '#4F46E5' },
  323: { label: 'Additional equipment off', shortLabel: 'Equipment off', color: '#4338CA' }
};
const EVENTS_TYPE_META = {
  1: 'Refueling (beginning)',
  2: 'Refueling (end)',
  3: 'Draining (beginning)',
  4: 'Draining (end)',
  5: 'Ignition On',
  6: 'Ignition Off',
  14: 'Overspeed start',
  22: 'Switching On',
  23: 'Switching Off',
  30: 'Overspeed end',
  35: 'Entered geozone',
  36: 'Exited geozone',
  42: 'Stop start',
  43: 'Stop end',
  72: 'Route start',
  73: 'Control point visit',
  74: 'Route end',
  97: 'Parking start',
  98: 'Parking end',
  122: 'Switching On',
  123: 'Switching Off',
  222: 'Switching On',
  223: 'Switching Off',
  322: 'Switching On',
  323: 'Switching Off'
};
const UNIVERSAL_INPUT_EVENT_PREFIXES = {
  22: 'Switching On',
  23: 'Switching Off',
  122: 'Switching On',
  123: 'Switching Off',
  222: 'Switching On',
  223: 'Switching Off',
  322: 'Switching On',
  323: 'Switching Off'
};
const DEVICE_EVENT_PREFIXES = {
  ...UNIVERSAL_INPUT_EVENT_PREFIXES
};
const GEOFENCE_EVENT_PREFIXES = {
  35: 'Vehicle entered the Geofence',
  36: 'Vehicle left the Geofence'
};

const EVENTS_REPORT_COLUMNS = [
  { key: 'serialNumber', label: 'Sl No' },
  { key: 'vehicle', label: 'Vehicle' },
  { key: 'dateTime', label: 'Date and time' },
  { key: 'event', label: 'Event' },
  { key: 'parameters', label: 'Parameters' },
  { key: 'address', label: 'Address' }
];

const TRACK_IGNITION_EVENT_TYPES = new Set([5, 6]);

const GOOGLE_MAPS_SCRIPT_ID = 'fleet-management-google-maps-script';
const GOOGLE_MAPS_INIT_CALLBACK = '__fleetManagementMapInit';
const MAP_KEY_SESSION_STORAGE_KEY = 'fleet_management_google_maps_key';
const MAP_ID_SESSION_STORAGE_KEY = 'fleet_management_google_map_id';

const isGoogleMapsApiReady = () => Boolean(
  window.google?.maps && typeof window.google.maps.Map === 'function' && typeof window.google.maps.InfoWindow === 'function'
);

const LOCATION_MAP_OPTIONS = {
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: true,
  streetViewControl: false,
  fullscreenControl: true,
  clickableIcons: false,
  gestureHandling: 'cooperative'
};

const LOCATION_MARKER_ICON_DIMENSION = { width: 44, height: 52 };

const getCoordinateValue = (...values) => {
  const matchedValue = values.find((value) => value !== null && value !== undefined && value !== '');
  const parsedValue = Number(matchedValue);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const isValidCoordinatePair = (latitude, longitude) => latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180;

const normalizePackedCoordinateValue = (value, maxAbsValue) => {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return null;
  const absoluteValue = Math.abs(parsedValue);
  if (absoluteValue <= maxAbsValue) return parsedValue;

  let divisor = 10;
  while (divisor <= 1000000000) {
    const normalizedValue = parsedValue / divisor;
    if (Math.abs(normalizedValue) <= maxAbsValue) return normalizedValue;
    divisor *= 10;
  }

  return null;
};

const normalizeCoordinatePair = (latitude, longitude) => {
  const normalizedLatitude = normalizePackedCoordinateValue(latitude, 90);
  const normalizedLongitude = normalizePackedCoordinateValue(longitude, 180);
  if (
    normalizedLatitude !== null
    && normalizedLongitude !== null
    && isValidCoordinatePair(normalizedLatitude, normalizedLongitude)
    && !(normalizedLatitude === 0 && normalizedLongitude === 0)
  ) {
    return {
      latitude: normalizedLatitude,
      longitude: normalizedLongitude
    };
  }
  return null;
};

const getOmnicommPointCoordinates = (point) => {
  if (typeof point === 'string') {
    const match = point.match(/x=([-+]?\d+(?:\.\d+)?),\s*y=([-+]?\d+(?:\.\d+)?)/i);
    if (match) {
      const rawLng = Number(match[1]);
      const rawLat = Number(match[2]);
      return normalizeCoordinatePair(rawLat, rawLng);
    }
  }

  if (point && typeof point === 'object') {
    const pointLatitude = getCoordinateValue(point.y, point.latitude, point.lat);
    const pointLongitude = getCoordinateValue(point.x, point.longitude, point.lng, point.lon);
    return normalizeCoordinatePair(pointLatitude, pointLongitude);
  }

  return null;
};

const getVehicleCoordinates = (vehicle) => {
  const latitude = getCoordinateValue(vehicle?.latitude, vehicle?.lat, vehicle?.currentLatitude, vehicle?.currentLat);
  const longitude = getCoordinateValue(vehicle?.longitude, vehicle?.lng, vehicle?.lon, vehicle?.long, vehicle?.currentLongitude, vehicle?.currentLng);

  const directCoordinates = normalizeCoordinatePair(latitude, longitude);
  if (directCoordinates) return directCoordinates;
  if (typeof vehicle?.coordinates === 'string') {
    const [rawLat, rawLng] = vehicle.coordinates.split(',').map((value) => Number(value?.trim()));
    const parsedCoordinates = normalizeCoordinatePair(rawLat, rawLng);
    if (parsedCoordinates) return parsedCoordinates;
  }
  return null;
};

const getFleetEventCoordinates = (row, options = {}) => {
  const { includeLocationText = false } = options;
  const latitude = getCoordinateValue(row?.latitude, row?.lat, row?.lt);
  const longitude = getCoordinateValue(row?.longitude, row?.lng, row?.lon, row?.long, row?.lg);
  const directCoordinates = normalizeCoordinatePair(latitude, longitude);
  if (directCoordinates) return directCoordinates;

  const pointCoordinates = getOmnicommPointCoordinates(row?.point);
  if (pointCoordinates) return pointCoordinates;

  const coordinateTextFields = includeLocationText
    ? [
      row?.coordinates,
      row?.location,
      row?.startLocation,
      row?.endLocation
    ]
    : [row?.coordinates];

  for (let index = 0; index < coordinateTextFields.length; index += 1) {
    const coordinateText = coordinateTextFields[index];
    if (typeof coordinateText === 'string') {
      const matches = coordinateText.match(/[-+]?\d+(?:\.\d+)?/g);
      if (matches && matches.length >= 2) {
        const rawLat = Number(matches[0]);
        const rawLng = Number(matches[1]);
        const parsedCoordinates = normalizeCoordinatePair(rawLat, rawLng);
        if (parsedCoordinates) return parsedCoordinates;
      }
    }
  }

  return null;
};

const formatFuelVolume = (value) => {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return '--';
  return Number.isInteger(parsedValue) ? `${parsedValue} L` : `${parsedValue.toFixed(1)} L`;
};

const formatSyncDate = (syncAt, fallbackMinutes, format = 'DD MMM YYYY, hh:mm A') => {
  const timezoneName = getStoredUserTimezone();
  const syncMoment = moment(syncAt).tz(timezoneName);
  if (syncMoment.isValid()) return syncMoment.format(format);
  if (!Number.isFinite(fallbackMinutes)) return 'N/A';
  return nowInTimezone(timezoneName).subtract(fallbackMinutes, 'minutes').format(format);
};

const formatCoordinateLabel = (value) => {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return '--';
  return parsedValue.toFixed(5);
};

const getDirectionLabel = (degrees) => {
  const parsedValue = Number(degrees);
  if (!Number.isFinite(parsedValue)) return '--';
  const normalizedValue = ((parsedValue % 360) + 360) % 360;
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return directions[Math.round(normalizedValue / 45) % directions.length];
};

const getLastSyncMinutes = (eventDate) => {
  const syncMoment = moment(eventDate);
  if (!syncMoment.isValid()) return Number.POSITIVE_INFINITY;
  const minutes = moment().diff(syncMoment, 'minutes');
  return minutes >= 0 ? minutes : 0;
};

const getResolvedGoogleMapId = (mapId) => (typeof mapId === 'string' && mapId.trim() ? mapId.trim() : '');

const getNormalizedTimestampValue = (value) => {
  const parsedValue = Number(value);
  if (Number.isFinite(parsedValue)) {
    if (parsedValue > 1e12) return parsedValue;
    if (parsedValue > 1e9) return parsedValue * 1000;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsedMoment = moment(value);
    if (parsedMoment.isValid()) return parsedMoment.valueOf();
  }
  return null;
};

function getNormalizedOmnicommTimestampValue(value) {
  const parsedValue = Number(value);
  if (Number.isFinite(parsedValue)) {
    if (parsedValue > 1e12) return parsedValue;
    if (parsedValue > 1e9) return parsedValue * 1000;
    if (parsedValue >= 0) return (parsedValue + OMNICOMM_UNIX_EPOCH_OFFSET_SECONDS) * 1000;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsedMoment = moment(value);
    if (parsedMoment.isValid()) return parsedMoment.valueOf();
  }
  return null;
}

function getNormalizedTrackTimestampValue(value) {
  return getNormalizedTimestampValue(value) ?? getNormalizedOmnicommTimestampValue(value);
}

const getFirstFiniteNumber = (...values) => {
  for (let index = 0; index < values.length; index += 1) {
    const parsedValue = Number(values[index]);
    if (Number.isFinite(parsedValue)) return parsedValue;
  }
  return null;
};

const normalizeLegacyTrackMetrics = (metrics = []) => {
  const rawFuelDeciliters = getFirstFiniteNumber(metrics?.[0]);
  const speed = getFirstFiniteNumber(metrics?.[1]);
  const distance = getFirstFiniteNumber(metrics?.[2]);
  const direction = getFirstFiniteNumber(metrics?.[5]);

  return {
    rawFuelDeciliters,
    speed,
    distance,
    stopFlag: metrics?.[3] ?? null,
    ignitionFlag: metrics?.[4] ?? null,
    direction
  };
};

const normalizeOmnicommTrackPointFlags = (flags = []) => ({
  rawFuelDeciliters: getFirstFiniteNumber(flags?.[0]),
  stopFlag: flags?.[1] ?? null,
  ignitionFlag: flags?.[2] ?? null
});

const getTrackEventDateValue = (rawTimestamp, normalizedTimestamp) => {
  const parsedRawTimestamp = Number(rawTimestamp);
  if (Number.isFinite(parsedRawTimestamp) && parsedRawTimestamp >= 0) return parsedRawTimestamp;

  const parsedNormalizedTimestamp = Number(normalizedTimestamp);
  if (Number.isFinite(parsedNormalizedTimestamp) && parsedNormalizedTimestamp > 0) {
    return Math.floor(parsedNormalizedTimestamp / 1000);
  }

  return null;
};

const formatTrackTimestamp = (value, fallback = '--') => {
  const normalizedValue = getNormalizedTrackTimestampValue(value);
  if (!normalizedValue) return fallback;
  const timeMoment = moment(normalizedValue);
  return timeMoment.isValid() ? timeMoment.format('DD MMM YYYY, hh:mm A') : fallback;
};

const getTrackEventMeta = (type) => {
  const normalizedType = Number(type);
  return TRACK_EVENT_META[normalizedType] || {
    label: `Event ${normalizedType}`,
    shortLabel: `Event ${normalizedType}`,
    color: '#F97316'
  };
};

const getTrackEventParameterText = (param2) => {
  const parsedValue = Number(param2);
  if (Number.isFinite(parsedValue) && parsedValue !== 0) return `Ref ${parsedValue}`;
  return '';
};

const normalizeTrackEvent = ({
  rawEvent,
  pointIndex,
  eventIndex,
  pointTimestamp,
  pointEventDateValue
}) => {
  if (Array.isArray(rawEvent)) {
    const [type, param1 = null, param2 = null] = rawEvent;
    const normalizedType = Number(type);
    if (!Number.isFinite(normalizedType)) return null;
    const meta = getTrackEventMeta(normalizedType);

    return {
      id: `track-event-${pointIndex + 1}-${eventIndex + 1}`,
      type: normalizedType,
      param1: Number.isFinite(Number(param1)) ? Number(param1) : param1,
      param2: Number.isFinite(Number(param2)) ? Number(param2) : param2,
      label: meta.label,
      shortLabel: meta.shortLabel,
      color: meta.color,
      timestamp: pointTimestamp,
      eventDateValue: pointEventDateValue,
      parameterText: getTrackEventParameterText(param2)
    };
  }

  if (!rawEvent || typeof rawEvent !== 'object') return null;
  const normalizedType = Number(rawEvent.type ?? rawEvent.eventType ?? rawEvent.code);
  if (!Number.isFinite(normalizedType)) return null;
  const meta = getTrackEventMeta(normalizedType);
  const rawTimestamp = rawEvent.timestamp ?? rawEvent.eventDate ?? rawEvent.date ?? pointEventDateValue;
  const normalizedTimestamp = getNormalizedTrackTimestampValue(rawTimestamp) ?? pointTimestamp;
  const eventDateValue = getTrackEventDateValue(rawTimestamp, normalizedTimestamp) ?? pointEventDateValue;

  return {
    id: rawEvent.id || `track-event-${pointIndex + 1}-${eventIndex + 1}`,
    type: normalizedType,
    param1: rawEvent.param1 ?? null,
    param2: rawEvent.param2 ?? rawEvent.value ?? null,
    label: rawEvent.name || rawEvent.label || meta.label,
    shortLabel: rawEvent.shortLabel || meta.shortLabel,
    color: meta.color,
    timestamp: normalizedTimestamp,
    eventDateValue,
    parameterText: getTrackEventParameterText(rawEvent.param2 ?? rawEvent.value)
  };
};

const getFirstObjectMatch = (value, predicate) => {
  if (!value || typeof value !== 'object') return null;
  if (Array.isArray(value)) return value.find((item) => predicate(item)) || null;
  return Object.values(value).find((item) => predicate(item)) || null;
};

const getNullableBoolean = (value) => {
  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();
    if (normalizedValue === '1' || normalizedValue === 'true') return true;
    if (normalizedValue === '0' || normalizedValue === 'false') return false;
  }
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  return null;
};

const getTrackMovementLabel = (isStopped, speed) => {
  if (isStopped === true) return 'Stopped';
  if (isStopped === false) return 'Moving';
  return speed > 0 ? 'Moving' : 'Stopped';
};

const getTrackBooleanLabel = (value, truthyLabel, falsyLabel) => {
  if (value === true) return truthyLabel;
  if (value === false) return falsyLabel;
  return '--';
};

const getTrackFuelVolume = (value, rawDeciliters, preferRawDeciliters = false) => {
  const parsedRawDeciliters = Number(rawDeciliters);
  const rawFuelVolume = Number.isFinite(parsedRawDeciliters) ? parsedRawDeciliters / 10 : null;
  const explicitFuelVolume = Number(value);
  if (preferRawDeciliters && rawFuelVolume !== null) return rawFuelVolume;
  if (Number.isFinite(explicitFuelVolume) && explicitFuelVolume > 0) return explicitFuelVolume;
  if (rawFuelVolume !== null) return rawFuelVolume;
  if (Number.isFinite(explicitFuelVolume)) return explicitFuelVolume;
  return null;
};

const isTrackPointLike = (item) => {
  if (Array.isArray(item)) return item.length >= 2;
  if (!item || typeof item !== 'object') return false;

  return Boolean(
    Number.isFinite(Number(item?.latitude))
    || Number.isFinite(Number(item?.lat))
    || Number.isFinite(Number(item?.lt))
    || Number.isFinite(Number(item?.longitude))
    || Number.isFinite(Number(item?.lng))
    || Number.isFinite(Number(item?.lg))
    || item?.timestamp
    || item?.eventDate
    || item?.gpsDataActualDate
    || item?.ed
    || Array.isArray(item?.p)
  );
};

const getTrackVehicleEntry = (response) => {
  const roots = [
    response?.result?.data,
    response?.result?.results?.data?.[0],
    response?.data,
    response?.result,
    response
  ].filter(Boolean);

  const hasTrackShape = (item) => Boolean(
    item
    && typeof item === 'object'
    && (
      Array.isArray(item?.trackPoints)
      || Array.isArray(item?.track)
      || Array.isArray(item?.points)
      || Array.isArray(item?.routePoints)
      || (Array.isArray(item?.data) && item.data.some((trackPoint) => isTrackPointLike(trackPoint)))
      || item?.summary
      || item?.profile
    )
  );

  return roots.find((root) => hasTrackShape(root))
    || roots.map((root) => getFirstObjectMatch(root?.data, hasTrackShape)).find(Boolean)
    || roots.map((root) => getFirstObjectMatch(root, hasTrackShape)).find(Boolean)
    || null;
};

const getTrackLastKnownEntry = (response) => {
  const roots = [
    response?.result?.data,
    response?.result?.results?.data?.[0],
    response?.data,
    response?.result,
    response
  ].filter(Boolean);

  const hasLastKnownShape = (item) => Boolean(
    item
    && typeof item === 'object'
    && (
      Number.isFinite(Number(item?.latitude))
      || Number.isFinite(Number(item?.lat))
      || Number.isFinite(Number(item?.longitude))
      || Number.isFinite(Number(item?.lng))
      || item?.eventDate
      || item?.gpsDataActualDate
    )
  );

  return roots.find((root) => hasLastKnownShape(root))
    || roots.map((root) => getFirstObjectMatch(root?.data, hasLastKnownShape)).find(Boolean)
    || roots.map((root) => getFirstObjectMatch(root, hasLastKnownShape)).find(Boolean)
    || null;
};

const normalizeTrackPoint = (point, index) => {
  if (Array.isArray(point)) {
    const [longitude, latitude, rawTimestamp, metrics = [], rawEvents = [], gpsValidity = null] = point;
    const normalizedTimestamp = getNormalizedOmnicommTimestampValue(rawTimestamp);
    const legacyMetrics = normalizeLegacyTrackMetrics(metrics);
    const fuelVolume = getTrackFuelVolume(null, legacyMetrics.rawFuelDeciliters, true);
    const eventDateValue = getTrackEventDateValue(rawTimestamp, normalizedTimestamp);
    const events = (Array.isArray(rawEvents) ? rawEvents : [])
      .map((rawEvent, eventIndex) => normalizeTrackEvent({
        rawEvent,
        pointIndex: index,
        eventIndex,
        pointTimestamp: normalizedTimestamp,
        pointEventDateValue: eventDateValue
      }))
      .filter(Boolean);

    return {
      id: `track-point-${index + 1}`,
      latitude: getCoordinateValue(latitude),
      longitude: getCoordinateValue(longitude),
      timestamp: normalizedTimestamp,
      rawTimestamp: rawTimestamp ?? null,
      eventDateValue,
      fuelVolume,
      speed: Number.isFinite(legacyMetrics.speed) ? legacyMetrics.speed : 0,
      distance: Number.isFinite(legacyMetrics.distance) ? legacyMetrics.distance : null,
      ignition: getNullableBoolean(legacyMetrics.ignitionFlag),
      direction: legacyMetrics.direction,
      eventCount: events.length,
      satellitesCount: null,
      isStopped: getNullableBoolean(legacyMetrics.stopFlag),
      gpsValid: getNullableBoolean(gpsValidity),
      rawEvents: Array.isArray(rawEvents) ? rawEvents : [],
      events
    };
  }

  if (!point || typeof point !== 'object') return null;

  const rawPointFlags = Array.isArray(point.p) ? point.p : [];
  const pointFlags = normalizeOmnicommTrackPointFlags(rawPointFlags);
  const rawTimestamp = point.timestamp ?? point.eventDate ?? point.gpsDataActualDate ?? point.date ?? point.time ?? point.ed ?? point.trackTime ?? null;
  const normalizedTimestamp = getNormalizedTrackTimestampValue(rawTimestamp);
  const eventDateValue = getTrackEventDateValue(rawTimestamp, normalizedTimestamp);
  const legacyMetrics = normalizeLegacyTrackMetrics(rawPointFlags);
  const fuelVolume = getTrackFuelVolume(point.fuelVolume ?? point.fuel ?? point.fl, pointFlags.rawFuelDeciliters, Array.isArray(point.p));
  const speed = getFirstFiniteNumber(point.speed, point.s, legacyMetrics.speed);
  const distance = getFirstFiniteNumber(point.distance, point.m, point.trackMileage, legacyMetrics.distance);
  const direction = getFirstFiniteNumber(point.direction, point.d, legacyMetrics.direction);
  const satellitesCount = getFirstFiniteNumber(point.satellitesCount, point.satelliteCount);
  const isStopped = point.stop !== undefined
    ? getNullableBoolean(point.stop)
    : getNullableBoolean(pointFlags.stopFlag);
  const ignition = point.ignition ?? point.currentIgn ?? getNullableBoolean(pointFlags.ignitionFlag);
  let rawEvents = [];
  if (Array.isArray(point.events)) rawEvents = point.events;
  else if (Array.isArray(point.e)) rawEvents = point.e;
  const events = rawEvents
    .map((rawEvent, eventIndex) => normalizeTrackEvent({
      rawEvent,
      pointIndex: index,
      eventIndex,
      pointTimestamp: normalizedTimestamp,
      pointEventDateValue: eventDateValue
    }))
    .filter(Boolean);

  return {
    id: point.id || point.uuid || `track-point-${index + 1}`,
    latitude: getCoordinateValue(point.latitude, point.lat, point.lt),
    longitude: getCoordinateValue(point.longitude, point.lng, point.lon, point.long, point.lg),
    timestamp: normalizedTimestamp,
    rawTimestamp,
    eventDateValue,
    fuelVolume,
    speed: Number.isFinite(speed) ? speed : 0,
    distance: Number.isFinite(distance) ? distance : null,
    ignition,
    direction: Number.isFinite(direction) ? direction : null,
    eventCount: events.length || Number(point.eventCount) || 0,
    satellitesCount: Number.isFinite(satellitesCount) ? satellitesCount : null,
    isStopped,
    gpsValid: point.gpsDataValid !== undefined ? Boolean(point.gpsDataValid) : getNullableBoolean(point.v),
    rawEvents,
    events
  };
};

const normalizeTrackLastKnownPosition = (response) => {
  const entry = getTrackLastKnownEntry(response);
  if (!entry) return null;

  const rawPointFlags = Array.isArray(entry.p) ? entry.p : [];
  const legacyMetrics = normalizeLegacyTrackMetrics(rawPointFlags);
  const latitude = getCoordinateValue(entry.latitude, entry.lat, entry.lt);
  const longitude = getCoordinateValue(entry.longitude, entry.lng, entry.lon, entry.long, entry.lg);
  const rawTimestamp = entry.eventDate ?? entry.gpsDataActualDate ?? entry.timestamp ?? entry.ed ?? null;
  const normalizedTimestamp = getNormalizedTrackTimestampValue(rawTimestamp);
  const speed = getFirstFiniteNumber(entry.speed, entry.s, legacyMetrics.speed);
  const fuelVolume = getTrackFuelVolume(entry.fuelVolume ?? entry.fuel ?? entry.fl, legacyMetrics.rawFuelDeciliters, Array.isArray(entry.p));
  const voltage = Number(entry.voltage);
  const direction = getFirstFiniteNumber(entry.direction, entry.d, legacyMetrics.direction);
  const ignition = entry.ignition ?? getNullableBoolean(legacyMetrics.ignitionFlag);

  return {
    latitude,
    longitude,
    timestamp: normalizedTimestamp,
    rawTimestamp,
    eventDateValue: getTrackEventDateValue(rawTimestamp, normalizedTimestamp),
    speed: Number.isFinite(speed) ? speed : 0,
    fuelVolume,
    voltage: Number.isFinite(voltage) ? voltage : null,
    direction: Number.isFinite(direction) ? direction : null,
    ignition: typeof ignition === 'boolean' ? ignition : null,
    gpsDataValid: entry.gpsDataValid !== undefined ? Boolean(entry.gpsDataValid) : getNullableBoolean(entry.v),
    gsmOn: entry.gsmOn !== undefined ? Boolean(entry.gsmOn) : null,
    eventDate: rawTimestamp
  };
};

const normalizeTrackReport = (response) => {
  const entry = getTrackVehicleEntry(response);
  if (!entry) {
    return {
      hasData: false,
      points: [],
      profile: null,
      summary: null
    };
  }

  const rawPoints = entry.track || entry.trackPoints || entry.points || entry.routePoints || entry.data || [];
  const points = (Array.isArray(rawPoints) ? rawPoints : [])
    .map((point, index) => normalizeTrackPoint(point, index))
    .filter((point) => point && Number.isFinite(point.latitude) && Number.isFinite(point.longitude));
  const events = points
    .flatMap((point, pointIndex) => (Array.isArray(point.events) ? point.events : []).map((event) => ({
      ...event,
      point,
      pointIndex
    })))
    .sort((firstEvent, secondEvent) => (firstEvent.timestamp || 0) - (secondEvent.timestamp || 0));

  const derivedProfile = {
    registrationNumber: entry.registrationNumber ?? null,
    vehicleId: entry.vehicleId ?? entry.vehicleID ?? null,
    maximalSpeed: Number.isFinite(Number(entry.maximalSpeed)) ? Number(entry.maximalSpeed) : null,
    unis: Array.isArray(entry.unis) ? entry.unis : []
  };
  const hasDerivedProfile = Object.values(derivedProfile).some((value) => (
    Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== ''
  ));
  const profile = entry.profile || entry.vehicleProfile || (hasDerivedProfile ? derivedProfile : null);
  const summary = entry.summary || null;

  return {
    hasData: Boolean(points.length || profile || summary || events.length),
    points,
    events,
    profile,
    summary
  };
};

const normalizeFuelLevelSeries = (response) => {
  const reportEntry = response?.result?.results?.data?.[0] || null;
  if (!reportEntry || typeof reportEntry !== 'object') {
    return {
      hasData: false,
      actualSeries: [],
      smoothedSeries: [],
      tankCapacity: null,
      tankNumber: null
    };
  }

  const normalizeSeries = (series) => (Array.isArray(series) ? series : [])
    .map((point) => {
      if (!Array.isArray(point) || point.length < 2) return null;
      const timestamp = Number(point[0]);
      const value = Number(point[1]);
      if (!Number.isFinite(timestamp) || !Number.isFinite(value)) return null;
      return [timestamp, Number(value.toFixed(1))];
    })
    .filter(Boolean);

  const actualSeries = normalizeSeries(reportEntry.rawValues);
  const smoothedSeries = normalizeSeries(reportEntry.approxValues);

  return {
    hasData: Boolean(actualSeries.length || smoothedSeries.length),
    actualSeries,
    smoothedSeries,
    tankCapacity: Number.isFinite(Number(reportEntry.tankCapacity)) ? Number(reportEntry.tankCapacity) : null,
    tankNumber: Number.isFinite(Number(reportEntry.tankNumber)) ? Number(reportEntry.tankNumber) : null
  };
};

const getDistanceBetweenCoordinates = (startPoint, endPoint) => {
  if (!startPoint || !endPoint) return 0;
  const earthRadiusKm = 6371;
  const latitudeDelta = ((endPoint.latitude - startPoint.latitude) * Math.PI) / 180;
  const longitudeDelta = ((endPoint.longitude - startPoint.longitude) * Math.PI) / 180;
  const startLatitude = (startPoint.latitude * Math.PI) / 180;
  const endLatitude = (endPoint.latitude * Math.PI) / 180;

  const haversine = Math.sin(latitudeDelta / 2) ** 2
    + (Math.cos(startLatitude) * Math.cos(endLatitude) * Math.sin(longitudeDelta / 2) ** 2);

  return 2 * earthRadiusKm * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
};

const getTrackDistanceFromPoints = (points) => points.reduce((totalDistance, point, index) => {
  if (index === 0) return totalDistance;
  return totalDistance + getDistanceBetweenCoordinates(points[index - 1], point);
}, 0);

const getTrackMetricDistance = (points) => {
  const distanceValues = points
    .map((point) => Number(point.distance))
    .filter((value) => Number.isFinite(value));

  if (distanceValues.length < 2) {
    return distanceValues.length ? Math.max(distanceValues[0], 0) : getTrackDistanceFromPoints(points);
  }

  const isNonDecreasingSeries = distanceValues.every((value, index) => index === 0 || value >= distanceValues[index - 1]);
  if (isNonDecreasingSeries) {
    return Math.max(0, distanceValues[distanceValues.length - 1] - distanceValues[0]);
  }

  return Math.max(...distanceValues);
};

const getTrackSummary = (trackReport, lastKnownPosition) => {
  const points = trackReport?.points || [];
  const trackEvents = trackReport?.events || [];
  const speeds = points.map((point) => point.speed).filter((value) => Number.isFinite(value));
  const hasStopFlags = points.some((point) => typeof point.isStopped === 'boolean');
  const movingPointsCount = hasStopFlags
    ? points.filter((point) => point.isStopped === false).length
    : points.filter((point) => point.speed > 0).length;
  const startPoint = points[0] || null;
  const endPoint = points[points.length - 1] || null;
  const maxSpeed = speeds.length ? Math.max(...speeds) : 0;
  const totalDistance = getTrackMetricDistance(points);
  const periodMinutes = startPoint?.timestamp && endPoint?.timestamp
    ? Math.max(0, Math.round((endPoint.timestamp - startPoint.timestamp) / 60000))
    : null;
  const hasLastKnownCoordinates = Number.isFinite(lastKnownPosition?.latitude)
    && Number.isFinite(lastKnownPosition?.longitude);

  return {
    totalPoints: points.length,
    totalEvents: trackEvents.length,
    eventPointsCount: points.filter((point) => point.eventCount > 0).length,
    movingPointsCount,
    stoppedPointsCount: hasStopFlags
      ? points.filter((point) => point.isStopped === true).length
      : Math.max(0, points.length - movingPointsCount),
    maxSpeed,
    totalDistance,
    startPoint,
    endPoint,
    currentPoint: hasLastKnownCoordinates ? lastKnownPosition : endPoint,
    periodMinutes
  };
};

const getSampledTrackMarkers = (points, maxMarkers = TRACK_CLICKABLE_MARKERS_LIMIT, excludedPointIds = new Set()) => {
  if (!Array.isArray(points) || points.length <= 2) return [];

  const intermediatePoints = points
    .map((point, pointIndex) => ({ point, pointIndex }))
    .filter(({ point }) => !excludedPointIds.has(point.id))
    .slice(1, -1);

  if (!intermediatePoints.length) return [];

  const step = Math.max(1, Math.ceil(intermediatePoints.length / maxMarkers));
  return intermediatePoints.filter((_, index) => index % step === 0).slice(0, maxMarkers);
};

const getTrackEventMarkers = (points, maxMarkers = TRACK_EVENT_MARKERS_LIMIT) => {
  if (!Array.isArray(points) || points.length <= 2) return [];

  const eventPoints = points
    .map((point, pointIndex) => ({ point, pointIndex }))
    .slice(1, -1)
    .filter(({ point }) => point.eventCount > 0);

  if (!eventPoints.length) return [];

  const step = Math.max(1, Math.ceil(eventPoints.length / maxMarkers));
  return eventPoints.filter((_, index) => index % step === 0).slice(0, maxMarkers);
};

const getNearestTrackPointByTimestamp = (points, targetTimestamp) => {
  if (!Array.isArray(points) || !Number.isFinite(targetTimestamp)) return null;

  let nearestPoint = null;
  let nearestPointIndex = null;
  let nearestTimeDiff = Number.POSITIVE_INFINITY;

  points.forEach((point, pointIndex) => {
    const pointTimestamp = Number(point?.timestamp ?? point?.eventDateValue);
    if (!Number.isFinite(pointTimestamp)) return;

    const timeDiff = Math.abs(pointTimestamp - targetTimestamp);
    if (timeDiff < nearestTimeDiff) {
      nearestTimeDiff = timeDiff;
      nearestPoint = point;
      nearestPointIndex = pointIndex;
    }
  });

  return nearestPoint ? { point: nearestPoint, pointIndex: nearestPointIndex } : null;
};

const getTrackIgnitionEventGroups = (events, distanceThresholdKm = TRACK_IGNITION_GROUP_DISTANCE_KM) => {
  if (!Array.isArray(events) || !events.length) return [];

  const ignitionEvents = events
    .filter((event) => TRACK_IGNITION_EVENT_TYPES.has(Number(event?.type)))
    .filter((event) => Number.isFinite(event?.point?.latitude) && Number.isFinite(event?.point?.longitude));

  if (!ignitionEvents.length) return [];

  const groups = [];
  ignitionEvents.forEach((event) => {
    const eventPoint = {
      latitude: event.point.latitude,
      longitude: event.point.longitude
    };

    const matchedGroup = groups.find((group) => getDistanceBetweenCoordinates(group, eventPoint) <= distanceThresholdKm);

    if (matchedGroup) {
      matchedGroup.events.push(event);
      matchedGroup.lastTimestamp = event.timestamp || matchedGroup.lastTimestamp;
      matchedGroup.ignitionOnCount += event.type === 5 ? 1 : 0;
      matchedGroup.ignitionOffCount += event.type === 6 ? 1 : 0;
      return;
    }

    groups.push({
      id: `track-ignition-group-${groups.length + 1}`,
      latitude: event.point.latitude,
      longitude: event.point.longitude,
      events: [event],
      firstTimestamp: event.timestamp || null,
      lastTimestamp: event.timestamp || null,
      ignitionOnCount: event.type === 5 ? 1 : 0,
      ignitionOffCount: event.type === 6 ? 1 : 0
    });
  });

  return groups
    .map((group) => ({
      ...group,
      eventCount: group.events.length,
      title: `${group.events.length} ignition events`
    }))
    .sort((firstGroup, secondGroup) => (secondGroup.eventCount || 0) - (firstGroup.eventCount || 0));
};

const normalizeLocationMapCardData = (response) => {
  const data = response?.result?.data || response?.data || null;
  if (!data || typeof data !== 'object') return null;

  return {
    address: data.parseAddress || data.address || '--',
    driver: data.driver || '--',
    engineWork: data.engineWork || '--',
    tempFuel: Number.isFinite(Number(data.tempFuel)) ? Number(data.tempFuel) : null,
    voltage: Number.isFinite(Number(data.voltage)) ? Number(data.voltage) : null,
    mileageCAN: Number.isFinite(Number(data.mileageCAN)) ? Number(data.mileageCAN) : null,
    vehicleID: data.vehicleID ?? '--',
    additionalEquipment: Array.isArray(data.additionalEquipment) ? data.additionalEquipment : []
  };
};

const getFuelEventTypeLabel = (type) => {
  const normalizedType = Number(type);
  if (normalizedType === 1) return 'Refilling';
  if (normalizedType === 3) return 'Draining';
  return 'Unknown';
};

const normalizeFuelEventRow = (row, index) => {
  if (!row || typeof row !== 'object') return null;

  const rawVolume = Number(row.volume);
  const [longitude, latitude] = Array.isArray(row.address) ? row.address : [];
  const startDate = row.startdate ?? row.startDate ?? null;
  const endDate = row.enddate ?? row.endDate ?? null;
  const eventDate = row.eventdate ?? row.eventDate ?? endDate ?? startDate;
  const durationMinutes = Number.isFinite(Number(startDate)) && Number.isFinite(Number(endDate))
    ? Math.max(0, Math.round((Number(endDate) - Number(startDate)) / 60000))
    : null;

  return {
    id: row.id ?? `fuel-event-${index + 1}`,
    vehicleName: row.name ?? '--',
    type: Number(row.type),
    typeLabel: getFuelEventTypeLabel(row.type),
    volume: Number.isFinite(rawVolume) ? rawVolume / 10 : null,
    startDate,
    endDate,
    eventDate,
    durationMinutes,
    tankNumber: row.tankNmb ?? '--',
    address: row.parseAddress || '--',
    latitude: getCoordinateValue(latitude),
    longitude: getCoordinateValue(longitude),
    vehicleId: row.vehicleID ?? null,
    sourceReport: 'fuel'
  };
};

const normalizeFuelEventsReport = (response) => {
  const results = response?.result?.results || response?.results || null;
  const rows = Array.isArray(results?.rows)
    ? results.rows.map((row, index) => normalizeFuelEventRow(row, index)).filter(Boolean)
    : [];

  return {
    rows,
    totalPages: Number(results?.total) || 0,
    totalRecords: Number(results?.records) || rows.length,
    currentPage: Number(results?.page) || 1
  };
};

const getTripRunStatusMeta = (status) => {
  const matchedStatus = TRIP_RUN_STATUS_META[Number(status)];
  if (matchedStatus) return matchedStatus;
  return {
    label: Number.isFinite(Number(status)) ? `Status ${status}` : 'Unknown',
    color: '#475569',
    background: alpha('#64748B', 0.12)
  };
};

const formatTripMetricValue = (value, decimals = 1) => {
  if (isUnavailableMetric(value)) return '--';
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return '--';
  return parsedValue.toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

const formatTripRunDuration = (durationInSeconds) => {
  if (isUnavailableMetric(durationInSeconds)) return '--';
  const parsedSeconds = Math.max(0, Math.floor(Number(durationInSeconds)));
  if (!Number.isFinite(parsedSeconds)) return '--';

  const totalMinutes = Math.floor(parsedSeconds / 60);
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);
  if (hours > 0) parts.push(`${hours} h`);
  if (minutes > 0 || !parts.length) parts.push(`${minutes} min`);
  return parts.join(' ');
};

const getTripControlPointNo = (controlPoint, pointIndex) => {
  if (!controlPoint) return '--';
  const controlPointType = Number(controlPoint.type);
  if (controlPointType === 0) return 'Start *';
  if (controlPointType === 2) return 'End *';
  const numericNo = Number(controlPoint.num);
  if (Number.isFinite(numericNo) && numericNo > 0) return `${numericNo}`;
  return `${pointIndex + 1}`;
};

const getTripViolationText = (controlPoint) => {
  if (!controlPoint) return '--';
  return Number(controlPoint.violation) > 0 ? 'Control point is missed' : '-';
};

const getTripRunSummaryNote = (runSummary) => {
  if (!runSummary || typeof runSummary !== 'object') return 'Trip summary';
  const visitedControlPoints = Number(runSummary.visitedCP);
  const totalControlPoints = Number(runSummary.totalCP);
  const runDurationText = formatTripRunDuration(runSummary.runDuration);
  const violations = Array.isArray(runSummary.violations) ? runSummary.violations : [];
  const hasSkippedControlPoints = violations.some((violation) => `${violation}`.toUpperCase() === 'SKIPPED');
  let violationText = '';
  if (hasSkippedControlPoints) violationText = 'Control points are missed';
  else if (violations.length) violationText = `Violations: ${violations.join(', ')}`;

  const parts = [];
  if (Number.isFinite(visitedControlPoints) && Number.isFinite(totalControlPoints)) {
    parts.push(`Control points visited: ${visitedControlPoints} out of ${totalControlPoints}`);
  }
  if (runDurationText !== '--') parts.push(`Actual trip duration ${runDurationText}`);
  if (violationText) parts.push(violationText);

  return parts.length ? parts.join('   ') : 'Trip summary';
};

const normalizeTripControlPoint = (controlPoint, pointIndex) => {
  if (!controlPoint || typeof controlPoint !== 'object') return null;
  return {
    id: controlPoint.id || `trip-control-point-${pointIndex + 1}`,
    number: getTripControlPointNo(controlPoint, pointIndex),
    name: controlPoint.name || '--',
    visitTime: controlPoint.visitTime ?? null,
    violationText: getTripViolationText(controlPoint),
    mileage: controlPoint.totalMileage,
    fuelConsumption: controlPoint.fuelConsumption,
    fuelVolume: controlPoint.fuelVolume,
    refuelingVolume: controlPoint.refuelVolume,
    drainingVolume: controlPoint.dischargeVolume,
    mileagePerLitre: controlPoint.mileagePerLitre
  };
};

const normalizeFleetTripReport = (response) => {
  const results = response?.result?.results || response?.results || null;
  const vehicles = Array.isArray(results?.vehicles) ? results.vehicles : [];

  const normalizedVehicles = vehicles.map((vehicle, vehicleIndex) => {
    const routes = Array.isArray(vehicle?.routes) ? vehicle.routes : [];
    const normalizedRoutes = routes.map((route, routeIndex) => {
      const runs = Array.isArray(route?.runs) ? route.runs : [];
      const normalizedRuns = runs.map((run, runIndex) => {
        const controlPoints = Array.isArray(run?.controlPoints) ? run.controlPoints : [];
        const normalizedControlPoints = controlPoints
          .map((controlPoint, pointIndex) => normalizeTripControlPoint(controlPoint, pointIndex))
          .filter(Boolean);
        const statusMeta = getTripRunStatusMeta(run?.status);

        return {
          id: run?.id || `trip-run-${vehicleIndex + 1}-${routeIndex + 1}-${runIndex + 1}`,
          actualStartTime: run?.actualStartTime ?? null,
          actualFinishTime: run?.actualFinishTime ?? null,
          status: run?.status ?? null,
          statusLabel: statusMeta.label,
          statusColor: statusMeta.color,
          statusBackground: statusMeta.background,
          controlPoints: normalizedControlPoints,
          summary: run?.sumUp || {}
        };
      });

      return {
        id: route?.id || `trip-route-${vehicleIndex + 1}-${routeIndex + 1}`,
        name: route?.name || '--',
        runs: normalizedRuns,
        summary: route?.sumUp || {}
      };
    });

    return {
      id: vehicle?.id || `trip-vehicle-${vehicleIndex + 1}`,
      name: vehicle?.name || '--',
      routes: normalizedRoutes,
      summary: vehicle?.sumUp || {}
    };
  });

  const totalRoutes = normalizedVehicles.reduce((count, vehicle) => count + vehicle.routes.length, 0);
  const totalRuns = normalizedVehicles.reduce(
    (count, vehicle) => count + vehicle.routes.reduce((routeTotal, route) => routeTotal + route.runs.length, 0),
    0
  );
  const totalViolatedRuns = normalizedVehicles.reduce(
    (count, vehicle) => count + vehicle.routes.reduce((routeTotal, route) => routeTotal + (Number(route?.summary?.violatedRuns) || 0), 0),
    0
  );

  return {
    vehicles: normalizedVehicles,
    totalVehicles: normalizedVehicles.length,
    totalRoutes,
    totalRuns,
    totalViolatedRuns
  };
};

const normalizeFleetCurrentRuns = (response) => {
  const root = response?.result?.data || response?.data || {};
  const rows = Array.isArray(root?.rows) ? root.rows : [];

  const normalizedRows = rows.map((row, index) => {
    if (!row || typeof row !== 'object') return null;
    return {
      id: row.runID || `current-run-${index + 1}`,
      vehicleName: row.vehicleName || '--',
      routeName: row.routeName || '--',
      runId: row.runID ?? '--',
      routeId: row.routeID ?? '--',
      vehicleId: row.vehicleID ?? '--',
      actualRunStartTime: row.actRunStartTime ?? null,
      plannedRunStartTime: row.planRunStartTime ?? null,
      plannedRunFinishTime: row.planRunFinTime ?? null,
      vehicleLastDataTime: row.vehicleLastDataTime ?? null,
      rciLastEventTime: row.rciLastEventTime ?? null,
      rciLastEventName: row.rciLastEventName ?? '--',
      controlPointName: row?.rciLastEventData?.ps || '--'
    };
  }).filter(Boolean);

  return {
    total: Number(root?.total) || normalizedRows.length,
    rows: normalizedRows
  };
};

const getEventsTypeLabel = (eventTypeID) => {
  const normalizedType = Number(eventTypeID);
  if (!Number.isFinite(normalizedType)) return '--';
  return EVENTS_TYPE_META[normalizedType] || `Event ${normalizedType}`;
};

const getFirstNonEmptyEventValue = (...values) => {
  const value = values.flatMap((entry) => (Array.isArray(entry) ? entry : [entry]))
    .find((entry) => entry !== null && entry !== undefined && entry !== '');
  return value !== null && value !== undefined && value !== '' ? String(value) : '';
};

const isNumericEventLabel = (label, eventTypeID) => {
  if (label === null || label === undefined || label === '') return false;
  const normalizedType = Number(eventTypeID);
  return Number.isFinite(normalizedType) && String(label).trim() === String(normalizedType);
};

const formatFleetEventLabel = (row, displayEvent, options = {}) => {
  const { hasSecondaryEngine = true } = options;
  const eventTypeID = Number(row?.eventTypeID);
  const deviceName = getFirstNonEmptyEventValue(
    row?.auxiliaryDevice,
    row?.uniNameArr,
    row?.universalInputName
  );
  const deviceEventPrefix = DEVICE_EVENT_PREFIXES[eventTypeID];
  const isSecondaryEngineDevice = deviceName.trim().toUpperCase() === 'SECONDARY ENGINE';
  if (isSecondaryEngineDevice && !hasSecondaryEngine) {
    return getEventsTypeLabel(eventTypeID);
  }
  if (deviceName && deviceEventPrefix) return `${deviceEventPrefix} ${deviceName}`;

  if (
    !hasSecondaryEngine
    && typeof displayEvent === 'string'
    && displayEvent.toUpperCase().includes('SECONDARY ENGINE')
  ) {
    return getEventsTypeLabel(eventTypeID);
  }

  if (displayEvent && !isNumericEventLabel(displayEvent, eventTypeID)) return displayEvent;
  if (row?.event && !isNumericEventLabel(row.event, eventTypeID)) return row.event;

  const eventPrefix = UNIVERSAL_INPUT_EVENT_PREFIXES[eventTypeID];
  if (eventPrefix) {
    return eventPrefix;
  }

  return getEventsTypeLabel(eventTypeID);
};

const formatFleetEventDateTime = (value, fallback = '--') => {
  if (value === null || value === undefined || value === '') return fallback;
  const numericValue = Number(value);
  const timeMoment = Number.isFinite(numericValue)
    ? moment(numericValue > 10000000000 ? numericValue : numericValue * 1000)
    : moment(value);
  return timeMoment.isValid() ? timeMoment.format('DD.MM.YYYY HH:mm:ss') : fallback;
};

const formatFleetEventParameterText = (row) => {
  const eventTypeID = Number(row?.eventTypeID);
  const parameters = row?.parameters;

  if ([1, 2, 3, 4].includes(eventTypeID)) {
    const fuelVolume = Number(parameters) / 10;
    return Number.isFinite(fuelVolume) ? `Fuel volume ${fuelVolume.toFixed(1)} l` : '';
  }

  if (eventTypeID === 5) {
    const durationMinutes = Number(parameters);
    if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) return '';
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    return `Switched Off ${String(hours).padStart(2, '0')} h ${String(minutes).padStart(2, '0')} min`;
  }

  if (eventTypeID === 6) return '';

  if (UNIVERSAL_INPUT_EVENT_PREFIXES[eventTypeID]) {
    return formatFleetEventLabel(row, row?.event);
  }

  if (row?.strParameter) return row.strParameter;

  if (GEOFENCE_EVENT_PREFIXES[eventTypeID]) {
    const geofenceName = getFirstNonEmptyEventValue(
      row?.geozoneName,
      row?.geofenceName,
      row?.geozone,
      row?.geofence,
      row?.zoneName,
      row?.zone
    );
    return geofenceName ? `${GEOFENCE_EVENT_PREFIXES[eventTypeID]} ${geofenceName}` : '';
  }

  return parameters !== null && parameters !== undefined && parameters !== '' ? String(parameters) : '';
};

const normalizeFleetEvents = (response, options = {}) => {
  const results = response?.result?.results || response?.results || {};
  const rows = Array.isArray(results?.rows) ? results.rows : [];
  const columns = Array.isArray(results?.columns) && results.columns.length
    ? results.columns
    : EVENTS_REPORT_COLUMNS;
  let profileHasSecondaryEngine = options.hasSecondaryEngine;
  if (typeof results?.profileSecondaryEngine === 'boolean') {
    profileHasSecondaryEngine = results.profileSecondaryEngine;
  } else if (results?.eventCapabilities && typeof results.eventCapabilities.secondaryEngine === 'boolean') {
    profileHasSecondaryEngine = results.eventCapabilities.secondaryEngine;
  }

  const normalizedRows = rows.map((row, index) => {
    if (!row || typeof row !== 'object') return null;
    const displayRow = row.displayRow && typeof row.displayRow === 'object' ? row.displayRow : {};
    const serialNumber = displayRow.serialNumber ?? row.serialNumber ?? row.id ?? index + 1;
    const vehicleName = displayRow.vehicle || row.vehicle || row.name || '--';
    const eventTypeLabel = formatFleetEventLabel(row, displayRow.event, {
      ...options,
      hasSecondaryEngine: profileHasSecondaryEngine
    });
    const parameterText = displayRow.parameters !== null && displayRow.parameters !== undefined && displayRow.parameters !== ''
      ? displayRow.parameters
      : row.parameterText || formatFleetEventParameterText(row);
    const address = displayRow.address || row.addressText || row.parseAddress || row.address || '--';
    const dateTime = displayRow.dateTime || row.dateTime || formatFleetEventDateTime(row.eventDate);
    const coordinates = getFleetEventCoordinates(row);

    return {
      id: row.id || `event-row-${index + 1}`,
      serialNumber,
      vehicleName,
      eventTypeID: Number.isFinite(Number(row.eventTypeID)) ? Number(row.eventTypeID) : null,
      eventTypeLabel,
      eventDate: row.eventDate ?? null,
      dateTime,
      parameterText,
      address,
      latitude: coordinates?.latitude ?? null,
      longitude: coordinates?.longitude ?? null,
      sourceReport: 'events',
      displayRow: {
        serialNumber,
        vehicle: vehicleName,
        dateTime,
        event: eventTypeLabel,
        parameters: parameterText,
        address
      }
    };
  }).filter(Boolean);

  const totalPages = Number(results?.total);
  const totalRecords = Number(results?.records);
  const pageNumber = Number(results?.page);

  return {
    totalPages: Number.isFinite(totalPages) && totalPages > 0 ? totalPages : null,
    totalRecords: Number.isFinite(totalRecords) ? totalRecords : normalizedRows.length,
    pageNumber: Number.isFinite(pageNumber) && pageNumber > 0 ? pageNumber : 1,
    columns,
    rows: normalizedRows,
    profileSecondaryEngine: profileHasSecondaryEngine
  };
};

const normalizeFleetVehicleProfile = (response) => {
  const profile = response?.result?.results || response?.results || response?.result || response || {};
  const capabilities = profile?.capabilities && typeof profile.capabilities === 'object' ? profile.capabilities : {};
  return {
    ...profile,
    capabilities: {
      ...capabilities,
      secondaryEngine: Boolean(capabilities.secondaryEngine),
      universalInputs: Boolean(capabilities.universalInputs)
    }
  };
};

const formatSecondaryEngineCell = (status) => status || '--';

const parseFleetLogNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const normalizeSecondaryEngineValue = (value) => {
  const parsedValue = parseFleetLogNumber(value);
  if (parsedValue === null) return null;
  return Math.abs(parsedValue) >= 100 ? parsedValue / 1000 : parsedValue;
};

const getFleetLogStatusFromThreshold = (value, threshold) => {
  const parsedValue = parseFleetLogNumber(value);
  if (parsedValue === null) return 'Unknown';
  return parsedValue >= threshold ? 'On' : 'Off';
};

const getFleetLogMainEngineStatus = ({
  rpm,
  engineImpulse,
  fallbackStatus,
  threshold = LOG_REPORT_MAIN_ENGINE_RPM_THRESHOLD
}) => {
  const parsedEngineImpulse = parseFleetLogNumber(engineImpulse);
  if (parsedEngineImpulse !== null) return parsedEngineImpulse !== 0 ? 'On' : 'Off';

  const parsedRpm = parseFleetLogNumber(rpm);
  if (parsedRpm !== null) return parsedRpm >= threshold ? 'On' : 'Off';

  return fallbackStatus || 'Unknown';
};

const normalizeFleetLogReport = (response, options = {}) => {
  const {
    rawMode = false,
    mainEngineThreshold = LOG_REPORT_MAIN_ENGINE_RPM_THRESHOLD,
    secondaryEngineThreshold = LOG_REPORT_SECONDARY_ENGINE_DEFAULT_THRESHOLD
  } = options;
  const results = response?.result?.results || response?.results || {};
  const columns = Array.isArray(results?.columns) ? results.columns : [];
  const columnVisibility = results?.columnVisibility && typeof results.columnVisibility === 'object'
    ? results.columnVisibility
    : {};
  const rows = Array.isArray(results?.rows) ? results.rows : [];

  const normalizedRows = rows.map((row, index) => {
    if (!row || typeof row !== 'object') return null;
    const displayRow = row.displayRow && typeof row.displayRow === 'object' ? row.displayRow : row;
    const slNo = displayRow.slNo ?? row.slNo ?? index + 1;
    const rawSecondaryEngineValue = displayRow.secondaryEngineValue ?? row.secondaryEngineValue ?? '';
    const secondaryEngineValue = normalizeSecondaryEngineValue(rawSecondaryEngineValue);
    const rpm = displayRow.rpm ?? row.rpm ?? row.maxRpm ?? '';
    const engineImpulse = displayRow.engineImpulse ?? row.engineImpulse ?? '';
    const engineStatus = rawMode
      ? getFleetLogMainEngineStatus({
        rpm,
        engineImpulse,
        fallbackStatus: displayRow.engineStatus || row.engineStatus || '',
        threshold: mainEngineThreshold
      })
      : displayRow.engineStatus || row.engineStatus || '--';
    const secondaryEngineStatus = rawMode
      ? getFleetLogStatusFromThreshold(secondaryEngineValue, secondaryEngineThreshold)
      : formatSecondaryEngineCell(displayRow.secondaryEngineStatus || row.secondaryEngineStatus || '--');
    const coordinates = getFleetEventCoordinates({
      ...row,
      ...displayRow
    }, {
      includeLocationText: true
    });

    return {
      id: row.id || `log-report-${slNo}-${index + 1}`,
      slNo,
      vehicle: displayRow.vehicle || row.vehicle || '--',
      dateTime: displayRow.dateTime || row.dateTime || row.startDateTime || '--',
      startDateTime: displayRow.startDateTime || row.startDateTime || '--',
      endDateTime: displayRow.endDateTime || row.endDateTime || '--',
      duration: displayRow.duration || row.duration || '--',
      ignitionStatus: displayRow.ignitionStatus || row.ignitionStatus || '--',
      engineStatus,
      engineImpulse,
      secondaryEngineStatus,
      secondaryEngineValue,
      startSecondaryEngineValue: displayRow.startSecondaryEngineValue ?? row.startSecondaryEngineValue ?? '',
      endSecondaryEngineValue: displayRow.endSecondaryEngineValue ?? row.endSecondaryEngineValue ?? '',
      universalInputs: row.universalInputs || displayRow.universalInputs || null,
      secondaryEngineSignalAvailable: Boolean(row.secondaryEngineSignalAvailable || displayRow.secondaryEngineSignalAvailable),
      rpm,
      startRpm: displayRow.startRpm ?? row.startRpm ?? '',
      endRpm: displayRow.endRpm ?? row.endRpm ?? '',
      maxRpm: displayRow.maxRpm ?? row.maxRpm ?? '',
      location: displayRow.location || row.location || row.startLocation || '--',
      startLocation: displayRow.startLocation || row.startLocation || '--',
      endLocation: displayRow.endLocation || row.endLocation || '--',
      address: displayRow.location || row.location || row.startLocation || '--',
      latitude: coordinates?.latitude ?? null,
      longitude: coordinates?.longitude ?? null,
      eventDate: displayRow.dateTime || row.dateTime || row.startDateTime || null,
      typeLabel: 'Log row',
      parameterText: [
        displayRow.ignitionStatus || row.ignitionStatus ? `Ignition: ${displayRow.ignitionStatus || row.ignitionStatus}` : null,
        engineStatus ? `Main engine: ${engineStatus}` : null,
        secondaryEngineStatus ? `Secondary: ${secondaryEngineStatus}` : null
      ].filter(Boolean).join(' | '),
      sampleCount: displayRow.sampleCount ?? row.sampleCount ?? '',
      displayRow: {
        slNo,
        vehicle: displayRow.vehicle || row.vehicle || '--',
        dateTime: displayRow.dateTime || row.dateTime || row.startDateTime || '--',
        startDateTime: displayRow.startDateTime || row.startDateTime || '--',
        endDateTime: displayRow.endDateTime || row.endDateTime || '--',
        duration: displayRow.duration || row.duration || '--',
        ignitionStatus: displayRow.ignitionStatus || row.ignitionStatus || '--',
        engineStatus,
        engineImpulse,
        secondaryEngineStatus,
        rpm,
        startRpm: displayRow.startRpm ?? row.startRpm ?? '',
        endRpm: displayRow.endRpm ?? row.endRpm ?? '',
        maxRpm: displayRow.maxRpm ?? row.maxRpm ?? '',
        location: displayRow.location || row.location || row.startLocation || '--',
        startLocation: displayRow.startLocation || row.startLocation || '--',
        endLocation: displayRow.endLocation || row.endLocation || '--',
        sampleCount: displayRow.sampleCount ?? row.sampleCount ?? ''
      }
    };
  }).filter(Boolean);

  const totalPages = Number(results?.total);
  const totalRecords = Number(results?.records);
  const pageNumber = Number(results?.page);

  return {
    columns,
    columnVisibility,
    rows: normalizedRows,
    totalPages: Number.isFinite(totalPages) && totalPages > 0 ? totalPages : null,
    totalRecords: Number.isFinite(totalRecords) ? totalRecords : normalizedRows.length,
    rawRecords: Number(results?.rawRecords) || 0,
    loadedRecords: Number(results?.loadedRecords) || normalizedRows.length,
    pageNumber: Number.isFinite(pageNumber) && pageNumber > 0 ? pageNumber : 1,
    export: Boolean(results?.export),
    raw: Boolean(results?.raw),
    hasMore: Boolean(results?.hasMore),
    nextCursor: results?.nextCursor || ''
  };
};

const mergeFleetLogReportData = (currentData, nextData) => {
  if (!currentData) return nextData;
  if (!nextData) return currentData;
  const existingRows = Array.isArray(currentData.rows) ? currentData.rows : [];
  const nextRows = Array.isArray(nextData.rows) ? nextData.rows : [];
  const rows = [...existingRows, ...nextRows];
  return {
    ...currentData,
    ...nextData,
    columns: nextData.columns?.length ? nextData.columns : currentData.columns,
    rows,
    totalRecords: rows.length,
    loadedRecords: nextData.loadedRecords || rows.length,
    rawRecords: Number(nextData.rawRecords) || Number(currentData.rawRecords) || 0
  };
};

const formatGroupWorkMetric = (value, decimals = 1) => {
  if (isUnavailableMetric(value)) return '--';
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return '--';
  return parsedValue.toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

const normalizeFleetGroupWork = (response) => {
  const results = response?.result?.results || response?.results || {};
  const rows = Array.isArray(results?.rows) ? results.rows : [];

  const normalizedRows = rows.map((row, index) => {
    if (!row || typeof row !== 'object') return null;
    const hasVehicleContext = Boolean(row?.name || row?.vehicleID);

    return {
      id: row.id || `group-work-row-${index + 1}`,
      isSummaryRow: !hasVehicleContext,
      vehicleName: row.name || '--',
      date: row.date ?? row.startTime ?? null,
      shiftNumber: Number.isFinite(Number(row.shiftNmb)) ? Number(row.shiftNmb) : '--',
      startTime: row.startTime ?? null,
      endTime: row.endTime ?? null,
      workTime: row.workTime,
      workTimePercent: row.workTimePr ?? '--',
      movementTime: row.movementTime,
      mileage: row.mileage,
      mileagePerLitre: row.mileagePerLitre,
      consumption: row.consumption,
      fuelConsumptionOnMove: row.fuelConsumptionOnMove,
      fuelConsumptionWithoutMovement: row.fuelConsumptionWithoutMovement,
      refuelVolume: row.refuelVolume ?? row.fillVolume,
      drainingVolume: row.drainingVolume,
      startVolume: row.startVolume,
      endVolume: row.endVolume,
      maxSpeed: row.maxSpeed,
      groupName: row.groups || '--'
    };
  }).filter(Boolean);

  const totalPages = Number(results?.total);
  const totalRecords = Number(results?.records);
  const pageNumber = Number(results?.page);

  return {
    totalPages: Number.isFinite(totalPages) && totalPages > 0 ? totalPages : null,
    totalRecords: Number.isFinite(totalRecords) ? totalRecords : normalizedRows.length,
    pageNumber: Number.isFinite(pageNumber) && pageNumber > 0 ? pageNumber : 1,
    rows: normalizedRows
  };
};

const getDefaultDatePresetForReport = (reportValue) => {
  if (reportValue === 'dailyReadingSummary') return 'yesterday';
  if (reportValue === 'logReport') return 'lastHour';
  return 'today';
};

const createDateFilterStateForReport = (reportValue) => {
  const preset = getDefaultDatePresetForReport(reportValue);
  return {
    preset,
    range: getDateTimeRangeForPreset(preset)
  };
};

const isSameDateRange = (firstRange, secondRange) => (
  firstRange?.fromDateTime === secondRange?.fromDateTime
  && firstRange?.toDateTime === secondRange?.toDateTime
);

const getNextDateRange = (previousRange, field, value) => {
  const nextRange = { ...previousRange, [field]: value };
  let nextFrom = moment(nextRange.fromDateTime, DATE_TIME_INPUT_FORMAT, true);
  let nextTo = moment(nextRange.toDateTime, DATE_TIME_INPUT_FORMAT, true);

  if (
    field === 'fromDateTime'
    && nextFrom.isValid()
    && nextTo.isValid()
    && nextFrom.isAfter(nextTo)
  ) {
    nextRange.toDateTime = value;
    nextTo = moment(nextRange.toDateTime, DATE_TIME_INPUT_FORMAT, true);
  }

  if (
    field === 'toDateTime'
    && nextFrom.isValid()
    && nextTo.isValid()
    && nextTo.isBefore(nextFrom)
  ) {
    nextRange.fromDateTime = value;
    nextFrom = moment(nextRange.fromDateTime, DATE_TIME_INPUT_FORMAT, true);
  }

  if (nextFrom.isValid() && nextTo.isValid()) {
    const maxRangeDaysExceeded = nextTo.diff(nextFrom, 'days', true) > MAX_DATE_RANGE_DAYS;
    if (maxRangeDaysExceeded) {
      if (field === 'fromDateTime') {
        nextRange.toDateTime = nextFrom.clone().add(MAX_DATE_RANGE_DAYS, 'days').format(DATE_TIME_INPUT_FORMAT);
      } else {
        nextRange.fromDateTime = nextTo.clone().subtract(MAX_DATE_RANGE_DAYS, 'days').format(DATE_TIME_INPUT_FORMAT);
      }
    }
  }

  return nextRange;
};

const isValidReportValue = (value) => REPORT_TABS.some((tab) => tab.value === value);

const getFleetManagementInitialContext = (search = '') => {
  let storedContext = {};
  try {
    storedContext = JSON.parse(window.sessionStorage.getItem(FLEET_MANAGEMENT_CONTEXT_STORAGE_KEY) || '{}') || {};
  } catch (error) {
    storedContext = {};
  }

  const params = new URLSearchParams(search || '');
  const queryContext = {
    vehicle: params.get(FLEET_CONTEXT_QUERY_KEYS.vehicle) || '',
    report: params.get(FLEET_CONTEXT_QUERY_KEYS.report) || '',
    preset: params.get(FLEET_CONTEXT_QUERY_KEYS.preset) || '',
    fromDateTime: params.get(FLEET_CONTEXT_QUERY_KEYS.from) || '',
    toDateTime: params.get(FLEET_CONTEXT_QUERY_KEYS.to) || ''
  };

  let report = DEFAULT_REPORT_TAB;
  if (isValidReportValue(storedContext.report)) report = storedContext.report;
  if (isValidReportValue(queryContext.report)) report = queryContext.report;
  const preset = queryContext.preset || storedContext.preset || getDefaultDatePresetForReport(report);
  const storedRange = {
    fromDateTime: queryContext.fromDateTime || storedContext.fromDateTime || '',
    toDateTime: queryContext.toDateTime || storedContext.toDateTime || ''
  };
  const hasStoredRange = storedRange.fromDateTime && storedRange.toDateTime;

  return {
    vehicle: normalizeRegistrationNumber(queryContext.vehicle || storedContext.vehicle),
    report,
    preset,
    range: hasStoredRange ? storedRange : getDateTimeRangeForPreset(preset)
  };
};

const formatDailyReadingDate = (value, fallback = '--') => {
  if (!value) return fallback;
  const parsedDate = moment(value, 'YYYY-MM-DD', true);
  if (!parsedDate.isValid()) return fallback;
  return parsedDate.format('DD MMM YYYY');
};

const normalizeDailyReadingSummary = (response) => {
  const results = response?.result || response || {};
  const dailyReadings = Array.isArray(results?.daily_readings) ? results.daily_readings : [];

  return {
    vehicleId: results?.vehicle_id ?? null,
    vehicleNumber: results?.vehicle_number || '--',
    vehicleName: results?.vehicle_name || '--',
    vehicleType: results?.vehicle_type || '--',
    readingUnit: results?.reading_unit || '--',
    startDate: results?.start_date || null,
    endDate: results?.end_date || null,
    rangeSummary: {
      summaryStartAnchorDate: results?.range_summary?.summary_start_anchor_date || null,
      summaryEndAnchorDate: results?.range_summary?.summary_end_anchor_date || null,
      startReading: results?.range_summary?.start_reading ?? null,
      endReading: results?.range_summary?.end_reading ?? null,
      totalKmTravelled: results?.range_summary?.total_km_travelled ?? null,
      totalHourWorked: results?.range_summary?.total_hour_worked ?? null
    },
    dailyReadings: dailyReadings.map((row, index) => ({
      id: `daily-reading-${index + 1}-${row?.date || 'unknown'}`,
      date: row?.date || null,
      startReading: row?.start_reading ?? null,
      endReading: row?.end_reading ?? null,
      delta: row?.delta ?? null,
      unit: row?.unit || results?.reading_unit || '--',
      status: row?.status || '--',
      deltaSource: row?.delta_source || '--',
      totalKmTravelled: row?.total_km_travelled ?? null,
      totalHourWorked: row?.total_hour_worked ?? null
    }))
  };
};

// eslint-disable-next-line no-unused-vars
const normalizeFleetAnalytics = (response) => {
  const payload = response?.result || response || {};
  const charts = payload?.charts && typeof payload.charts === 'object' ? payload.charts : {};
  const table = payload?.table && typeof payload.table === 'object' ? payload.table : {};

  return {
    defaultTab: payload?.defaultTab || DEFAULT_REPORT_TAB,
    rangeOptions: Array.isArray(payload?.rangeOptions) && payload.rangeOptions.length
      ? payload.rangeOptions
      : ANALYTICS_PERIOD_OPTIONS.map((days) => ({ value: days, label: `Last ${days} days` })),
    vehicle: payload?.vehicle || {},
    dateRange: payload?.dateRange || {},
    summary: {
      cards: Array.isArray(payload?.summary?.cards) ? payload.summary.cards : []
    },
    charts: {
      dailyUtilization: Array.isArray(charts.dailyUtilization) ? charts.dailyUtilization : [],
      cumulativeUtilization: Array.isArray(charts.cumulativeUtilization) ? charts.cumulativeUtilization : [],
      ignitionBreakdown: Array.isArray(charts.ignitionBreakdown) ? charts.ignitionBreakdown : [],
      engineBreakdown: Array.isArray(charts.engineBreakdown) ? charts.engineBreakdown : [],
      hourlyHeatmap: Array.isArray(charts.hourlyHeatmap) ? charts.hourlyHeatmap : [],
      locationPoints: Array.isArray(charts.locationPoints) ? charts.locationPoints : [],
      topLocations: Array.isArray(charts.topLocations) ? charts.topLocations : [],
      dataQuality: Array.isArray(charts.dataQuality) ? charts.dataQuality : []
    },
    table: {
      columns: Array.isArray(table.columns) ? table.columns : [],
      rows: Array.isArray(table.rows) ? table.rows : []
    },
    emptyState: payload?.emptyState || null
  };
};

const getDailyReadingSummaryMetricConfig = (summaryData) => {
  const isHourBased = summaryData?.readingUnit === 'hr';
  return {
    label: isHourBased ? 'Total hour worked' : 'Total km travelled',
    valueKey: isHourBased ? 'totalHourWorked' : 'totalKmTravelled'
  };
};

const getLatestTimestampFromRow = (row, keys = []) => {
  const timestamp = keys
    .map((key) => getNormalizedTrackTimestampValue(row?.[key]))
    .find(Boolean);
  return timestamp || 0;
};

const sortLatestRows = (rows = [], keys = []) => [...rows].sort(
  (firstRow, secondRow) => getLatestTimestampFromRow(secondRow, keys) - getLatestTimestampFromRow(firstRow, keys)
);

const sumTripMetric = (tripReport, metricKey) => (tripReport?.vehicles || []).reduce((vehicleTotal, vehicle) => (
  vehicleTotal + (Number(vehicle?.summary?.[metricKey]) || 0)
), 0);

const getDailyReadingSummaryTotal = (summaryData) => {
  if (!summaryData) return null;
  const metricConfig = getDailyReadingSummaryMetricConfig(summaryData);
  return summaryData?.rangeSummary?.[metricConfig.valueKey] ?? summaryData?.dailyReadings?.reduce(
    (total, row) => total + (Number(row?.[metricConfig.valueKey]) || 0),
    0
  );
};

const exportFleetWorkbook = ({
  sheetName,
  fileNamePrefix,
  vehicleLabel,
  rangeLabel,
  headers,
  rows
}) => {
  const exportRows = [
    [vehicleLabel, ...Array(Math.max(headers.length - 1, 0)).fill('')],
    [rangeLabel, ...Array(Math.max(headers.length - 1, 0)).fill('')],
    Array(headers.length).fill(''),
    headers,
    ...rows
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(exportRows);
  worksheet['!cols'] = headers.map((header) => ({
    wch: Math.max(String(header || '').length + 4, 16)
  }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  const safeVehicleLabel = (vehicleLabel || 'vehicle').replace(/\s+/g, '_');
  const fileName = `${fileNamePrefix}_${safeVehicleLabel}_${moment().format(DATE_FORMAT.reportDateTime)}.xlsx`;
  XLSX.writeFile(workbook, fileName);
};

const getFleetReportCacheKey = ({
  registrationNumber,
  from,
  to,
  suffix = ''
}) => [
  registrationNumber || '',
  Number.isFinite(Number(from)) ? Number(from) : '',
  Number.isFinite(Number(to)) ? Number(to) : '',
  suffix
].join('|');

const getAnalyticsMetricValue = (row, key, fallback = 0) => {
  const parsedValue = Number(row?.[key]);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const buildAnalyticsDailyUtilizationOptions = (rows, readingUnit = '') => {
  const labels = rows.map((row) => row.label || formatDailyReadingDate(row.date));
  const unitLabel = readingUnit || rows.find((row) => row.unit)?.unit || '';
  return {
    animation: false,
    color: ['#2563EB', '#10B981', '#F97316'],
    grid: {
      top: 52, left: 48, right: 20, bottom: 54
    },
    legend: {
      top: 10,
      icon: 'roundRect',
      textStyle: { color: '#475569', fontSize: 12, fontWeight: 700 }
    },
    tooltip: { trigger: 'axis' },
    xAxis: {
      type: 'category',
      data: labels,
      axisLabel: { color: '#64748B' },
      axisLine: { lineStyle: { color: '#CBD5E1' } }
    },
    yAxis: [
      {
        type: 'value',
        name: unitLabel ? `Reading (${unitLabel})` : 'Reading',
        nameTextStyle: { color: '#475569', fontWeight: 700 },
        axisLabel: { color: '#64748B' },
        splitLine: { lineStyle: { color: 'rgba(148, 163, 184, 0.18)' } }
      },
      {
        type: 'value',
        name: 'Hours',
        nameTextStyle: { color: '#475569', fontWeight: 700 },
        axisLabel: { color: '#64748B' },
        splitLine: { show: false }
      }
    ],
    series: [
      {
        name: unitLabel ? `Daily ${unitLabel}` : 'Daily reading',
        type: 'bar',
        barMaxWidth: 28,
        data: rows.map((row) => getAnalyticsMetricValue(row, 'delta'))
      },
      {
        name: 'Ignition hours',
        type: 'line',
        yAxisIndex: 1,
        smooth: 0.25,
        symbolSize: 7,
        data: rows.map((row) => getAnalyticsMetricValue(row, 'ignitionOnHours'))
      },
      {
        name: 'Engine hours',
        type: 'line',
        yAxisIndex: 1,
        smooth: 0.25,
        symbolSize: 7,
        data: rows.map((row) => getAnalyticsMetricValue(row, 'engineOnHours'))
      }
    ]
  };
};

const buildAnalyticsCumulativeOptions = (rows, readingUnit = '') => ({
  animation: false,
  color: ['#0F172A'],
  grid: {
    top: 26, left: 52, right: 20, bottom: 44
  },
  tooltip: { trigger: 'axis' },
  xAxis: {
    type: 'category',
    data: rows.map((row) => row.label || formatDailyReadingDate(row.date)),
    axisLabel: { color: '#64748B' },
    axisLine: { lineStyle: { color: '#CBD5E1' } }
  },
  yAxis: {
    type: 'value',
    name: readingUnit ? `Total (${readingUnit})` : 'Total',
    nameTextStyle: { color: '#475569', fontWeight: 700 },
    axisLabel: { color: '#64748B' },
    splitLine: { lineStyle: { color: 'rgba(148, 163, 184, 0.18)' } }
  },
  series: [{
    name: 'Cumulative',
    type: 'line',
    smooth: 0.25,
    showSymbol: true,
    symbolSize: 7,
    lineStyle: { width: 3 },
    areaStyle: { color: 'rgba(15, 23, 42, 0.08)' },
    data: rows.map((row) => getAnalyticsMetricValue(row, 'cumulativeDelta'))
  }]
});

const buildAnalyticsBreakdownOptions = (rows) => ({
  animation: false,
  color: rows.map((row) => row.color).filter(Boolean).length
    ? rows.map((row) => row.color)
    : ['#10B981', '#CBD5E1', '#F97316', '#2563EB'],
  tooltip: {
    trigger: 'item',
    formatter: '{b}: {c}h ({d}%)'
  },
  legend: {
    bottom: 0,
    icon: 'circle',
    textStyle: { color: '#475569', fontSize: 12, fontWeight: 700 }
  },
  series: [{
    type: 'pie',
    radius: ['48%', '72%'],
    center: ['50%', '43%'],
    avoidLabelOverlap: true,
    label: { formatter: '{d}%', color: '#334155', fontWeight: 700 },
    data: rows.map((row) => ({
      name: row.name || row.label || '--',
      value: getAnalyticsMetricValue(row, 'value')
    }))
  }]
});

const buildAnalyticsHourlyOptions = (rows) => ({
  animation: false,
  color: ['#0891B2', '#F97316'],
  grid: {
    top: 44, left: 46, right: 20, bottom: 42
  },
  legend: {
    top: 6,
    icon: 'roundRect',
    textStyle: { color: '#475569', fontSize: 12, fontWeight: 700 }
  },
  tooltip: { trigger: 'axis' },
  xAxis: {
    type: 'category',
    data: rows.map((row) => row.label || `${row.hour}:00`),
    axisLabel: { color: '#64748B' },
    axisLine: { lineStyle: { color: '#CBD5E1' } }
  },
  yAxis: {
    type: 'value',
    name: 'Hours',
    nameTextStyle: { color: '#475569', fontWeight: 700 },
    axisLabel: { color: '#64748B' },
    splitLine: { lineStyle: { color: 'rgba(148, 163, 184, 0.18)' } }
  },
  series: [
    {
      name: 'Ignition',
      type: 'bar',
      stack: 'usage',
      barMaxWidth: 22,
      data: rows.map((row) => getAnalyticsMetricValue(row, 'ignitionOnHours'))
    },
    {
      name: 'Engine',
      type: 'bar',
      stack: 'usage',
      barMaxWidth: 22,
      data: rows.map((row) => getAnalyticsMetricValue(row, 'engineOnHours'))
    }
  ]
});

const shouldShowFirstLoadSkeleton = ({
  isLoading,
  error,
  data
}) => !isLoading && !error && (data === null || data === undefined);

// Render a high-contrast truck badge that stays visible on all map backgrounds.
const buildVehicleMarkerIconSvg = () => (
  `<svg width="44" height="52" viewBox="0 0 44 52" xmlns="http://www.w3.org/2000/svg">
    <circle cx="22" cy="20" r="16" fill="#0F172A"/>
    <circle cx="22" cy="20" r="13" fill="#FFFFFF"/>
    <path d="M22 49 15.5 38h13L22 49z" fill="#0F172A"/>
    <path d="M29.8 24.3c0 1-.8 1.8-1.8 1.8s-1.8-.8-1.8-1.8h-4.5c0 1-.8 1.8-1.8 1.8s-1.8-.8-1.8-1.8H17c-.5 0-.9-.4-.9-.9v-6.2c0-.5.4-.9.9-.9H25v-1.6c0-.5.4-.9.9-.9h3.3c.3 0 .5.1.7.4l2.3 3c.1.2.2.4.2.6v5.6c0 .5-.4.9-.9.9h-1.7zm-9.8 0c0 .4.3.7.7.7s.7-.3.7-.7-.3-.7-.7-.7-.7.3-.7.7zm8 0c0 .4.3.7.7.7s.7-.3.7-.7-.3-.7-.7-.7-.7.3-.7.7zm2.3-6.1-1.6-2.1h-2.2v2.1h3.8z" fill="#0284C7"/>
    <rect x="17.8" y="17.7" width="6.2" height="2.8" rx="0.6" fill="#BAE6FD"/>
  </svg>`
);

const escapeHtml = (value) => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const getMapMarkerInfoContent = (marker) => {
  const statusLabel = escapeHtml(STATUS_META[marker.status]?.shortLabel || 'Live');
  const safePlate = escapeHtml(marker.plate || 'Vehicle');
  return `
    <div style="font-family:Arial,sans-serif;font-size:12px;line-height:1.4;padding:4px 0;">
      <div style="font-weight:700;color:#0F172A;">${safePlate}</div>
      <div style="color:#475569;">${statusLabel}</div>
      <div style="color:#64748B;">Speed: ${escapeHtml(`${marker.speed} km/h`)}</div>
      <div style="color:#64748B;">Fuel: ${escapeHtml(formatFuelVolume(marker.fuelLevel))}</div>
    </div>
  `;
};

const getFleetEventMarkerInfoContent = (row) => {
  const safeType = escapeHtml(row?.typeLabel || '--');
  const safeVehicle = escapeHtml(row?.vehicleName || '--');
  const safeTime = escapeHtml(formatTrackTimestamp(row?.eventDate, '--'));
  const hasVolume = row?.volume !== null && row?.volume !== undefined && Number.isFinite(Number(row.volume));
  const safeVolume = escapeHtml(formatFuelVolume(row?.volume));
  const safeParameter = escapeHtml(row?.parameterText || '--');
  const safeAddress = escapeHtml(row?.address || '--');

  return `
    <div style="font-family:Arial,sans-serif;font-size:12px;line-height:1.4;padding:4px 0;min-width:220px;max-width:280px;">
      <div style="font-weight:700;color:#0F172A;">${safeVehicle}</div>
      <div style="color:#475569;">${safeType}</div>
      <div style="margin-top:6px;color:#334155;"><span style="color:#64748B;">Time:</span> ${safeTime}</div>
      ${hasVolume
    ? `<div style="color:#334155;"><span style="color:#64748B;">Volume:</span> ${safeVolume}</div>`
    : `<div style="color:#334155;"><span style="color:#64748B;">Parameters:</span> ${safeParameter}</div>`}
      <div style="margin-top:6px;color:#334155;"><span style="color:#64748B;">Address:</span> ${safeAddress}</div>
    </div>
  `;
};

const getTrackMarkerInfoContent = ({
  title,
  plate,
  speed,
  fuelVolume,
  satellitesCount,
  timestamp,
  direction,
  latitude,
  longitude,
  ignition,
  isStopped,
  gpsValid,
  events = [],
  details,
  isLoading = false,
  error = ''
}) => `
  <div style="font-family:Arial,sans-serif;font-size:11px;line-height:1.35;padding:2px 0;min-width:0;max-width:240px;color:#475569;">
    <div style="font-weight:700;font-size:12px;color:#0F172A;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(title)}</div>
    <div style="margin-top:2px;color:#334155;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(plate)}</div>
    <div style="margin-top:8px;display:grid;grid-template-columns:auto 1fr;gap:4px 10px;align-items:start;">
      <div style="color:#94A3B8;">Time</div>
      <div style="color:#0F172A;">${escapeHtml(formatTrackTimestamp(timestamp))}</div>
      <div style="color:#94A3B8;">Coords</div>
      <div style="color:#0F172A;">${escapeHtml(`${formatCoordinateLabel(latitude)}, ${formatCoordinateLabel(longitude)}`)}</div>
      <div style="color:#94A3B8;">Speed</div>
      <div style="color:#0F172A;">${escapeHtml(`${speed || 0} km/h`)}</div>
      <div style="color:#94A3B8;">Smooth fuel</div>
      <div style="color:#0F172A;">${escapeHtml(formatFuelVolume(fuelVolume))}</div>
      <div style="color:#94A3B8;">Move</div>
      <div style="color:#0F172A;">${escapeHtml(getTrackMovementLabel(isStopped, speed))}</div>
      <div style="color:#94A3B8;">Ignition</div>
      <div style="color:#0F172A;">${escapeHtml(getTrackBooleanLabel(ignition, 'On', 'Off'))}</div>
      <div style="color:#94A3B8;">GPS</div>
      <div style="color:#0F172A;">${escapeHtml(getTrackBooleanLabel(gpsValid, 'Valid', 'Invalid'))}</div>
      <div style="color:#94A3B8;">Heading</div>
      <div style="color:#0F172A;">${escapeHtml(getDirectionLabel(direction))}</div>
      <div style="color:#94A3B8;">Sats</div>
      <div style="color:#0F172A;">${escapeHtml(satellitesCount ?? '--')}</div>
    </div>
    ${events.length ? `
      <div style="margin-top:8px;padding-top:8px;border-top:1px solid #E2E8F0;">
        <div style="margin-bottom:6px;color:#94A3B8;">Events</div>
        <div style="display:flex;flex-direction:column;gap:6px;">
          ${events.slice(0, 5).map((event) => `
            <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:8px;">
              <div style="min-width:0;">
                <div style="display:inline-flex;align-items:center;gap:6px;color:#0F172A;font-weight:700;">
                  <span style="width:8px;height:8px;border-radius:999px;background:${escapeHtml(event.color || '#F97316')};display:inline-block;"></span>
                  <span>${escapeHtml(event.shortLabel || event.label || 'Event')}</span>
                </div>
                ${event.parameterText ? `<div style="color:#64748B;">${escapeHtml(event.parameterText)}</div>` : ''}
              </div>
              <div style="color:#64748B;white-space:nowrap;">${escapeHtml(formatTrackTimestamp(event.timestamp || event.eventDateValue, '--'))}</div>
            </div>
          `).join('')}
        </div>
      </div>
    ` : ''}
    ${isLoading ? '<div style="margin-top:8px;padding-top:8px;border-top:1px solid #E2E8F0;color:#2563EB;font-weight:700;">Loading details...</div>' : ''}
    ${error ? `<div style="margin-top:8px;padding-top:8px;border-top:1px solid #E2E8F0;color:#B91C1C;font-weight:700;">${escapeHtml(error)}</div>` : ''}
    ${!isLoading && !error && details ? `
      <div style="margin-top:8px;padding-top:8px;border-top:1px solid #E2E8F0;display:grid;grid-template-columns:auto 1fr;gap:4px 10px;align-items:start;">
        <div style="color:#94A3B8;">Address</div>
        <div style="color:#0F172A;">${escapeHtml(details.address || '--')}</div>
        <div style="color:#94A3B8;">Engine</div>
        <div style="color:#0F172A;">${escapeHtml(details.engineWork || '--')}</div>
        <div style="color:#94A3B8;">Fuel temp</div>
        <div style="color:#0F172A;">${escapeHtml(details.tempFuel !== null && details.tempFuel !== undefined ? `${details.tempFuel} deg C` : '--')}</div>
        <div style="color:#94A3B8;">Voltage</div>
        <div style="color:#0F172A;">${escapeHtml(details.voltage !== null && details.voltage !== undefined ? `${details.voltage} V` : '--')}</div>
        <div style="color:#94A3B8;">Mileage CAN</div>
        <div style="color:#0F172A;">${escapeHtml(details.mileageCAN !== null && details.mileageCAN !== undefined ? `${details.mileageCAN}` : '--')}</div>
        <div style="color:#94A3B8;">Vehicle ID</div>
        <div style="color:#0F172A;">${escapeHtml(details.vehicleID ?? '--')}</div>
        <div style="color:#94A3B8;">Equipment</div>
        <div style="color:#0F172A;">${escapeHtml(Array.isArray(details.additionalEquipment) && details.additionalEquipment.length ? details.additionalEquipment.map((equipment) => `${equipment?.name || 'Equipment'}: ${equipment?.value ?? '--'}`).join(', ') : '--')}</div>
      </div>
    ` : ''}
  </div>
`;

const getMapZoomByCoordinateSpread = (spread) => {
  if (spread <= 0.002) return 16;
  if (spread <= 0.008) return 15;
  if (spread <= 0.02) return 14;
  if (spread <= 0.05) return 13;
  if (spread <= 0.12) return 12;
  if (spread <= 0.3) return 11;
  if (spread <= 0.8) return 10;
  return 9;
};

const fitLocationMapToMarkers = (mapInstance, markers) => {
  if (!markers.length) return;
  if (markers.length === 1) {
    const onlyMarker = markers[0];
    mapInstance.setCenter({ lat: onlyMarker.latitude, lng: onlyMarker.longitude });
    mapInstance.setZoom(16);
    return;
  }
  const bounds = new window.google.maps.LatLngBounds();
  markers.forEach((marker) => bounds.extend({ lat: marker.latitude, lng: marker.longitude }));
  if (bounds.isEmpty()) return;
  mapInstance.fitBounds(bounds, 64);
  window.google.maps.event.addListenerOnce(mapInstance, 'idle', () => {
    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();
    const spread = Math.max(Math.abs(northEast.lat() - southWest.lat()), Math.abs(northEast.lng() - southWest.lng()));
    const targetZoom = getMapZoomByCoordinateSpread(spread);
    const currentZoom = mapInstance.getZoom();
    if (Number.isFinite(currentZoom) && currentZoom > targetZoom) {
      mapInstance.setZoom(targetZoom);
    }
  });
};

const fitMapToCoordinatePoints = (mapInstance, points) => {
  if (!points.length) return;
  if (points.length === 1) {
    const onlyPoint = points[0];
    mapInstance.setCenter({ lat: onlyPoint.latitude, lng: onlyPoint.longitude });
    mapInstance.setZoom(15);
    return;
  }

  const bounds = new window.google.maps.LatLngBounds();
  points.forEach((point) => bounds.extend({ lat: point.latitude, lng: point.longitude }));
  if (bounds.isEmpty()) return;

  mapInstance.fitBounds(bounds, 72);
  window.google.maps.event.addListenerOnce(mapInstance, 'idle', () => {
    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();
    const spread = Math.max(Math.abs(northEast.lat() - southWest.lat()), Math.abs(northEast.lng() - southWest.lng()));
    const targetZoom = getMapZoomByCoordinateSpread(spread);
    const currentZoom = mapInstance.getZoom();
    if (Number.isFinite(currentZoom) && currentZoom > targetZoom) {
      mapInstance.setZoom(targetZoom);
    }
  });
};

const getVehicleState = ({
  lastSyncMinutes, currentIgn, speed, omnicommState, engineWork, omnicommActivityStatus
}) => {
  const normalizedActivityState = normalizeCurrentStateStatus(omnicommActivityStatus);
  if (normalizedActivityState) return normalizedActivityState;
  const normalizedOmnicommState = normalizeCurrentStateStatus(omnicommState)
    || normalizeCurrentStateStatus(engineWork);
  if (normalizedOmnicommState) return normalizedOmnicommState;
  if (lastSyncMinutes > OFFLINE_SYNC_THRESHOLD_MINUTES) return 'offline';
  if (currentIgn || speed > 0) return 'active';
  return 'idle';
};

const getVehicleDisplayName = (row, index) => row.vehicleName
  || row.name
  || row.regNumb
  || `Vehicle ${index + 1}`;

const getVehiclePlaceName = (row) => {
  const placeName = [
    row.locationName,
    row.placeName,
    row.address,
    row.location,
    row.currentAddress,
    row.formattedAddress,
    row.siteName,
    row.site,
    row.areaName,
    row.area,
    row.city,
    row.district,
    row.zoneName,
    row.geofenceName
  ].find((value) => typeof value === 'string' && value.trim() && value.trim() !== 'N/A' && value.trim() !== '--');

  return placeName?.trim() || 'Location unavailable';
};

const getVehicleSiteId = (row) => {
  const siteObject = row?.site && typeof row.site === 'object' ? row.site : null;
  return row?.siteId ?? row?.site_id ?? siteObject?.id ?? null;
};

const getVehicleSiteName = (row) => {
  const siteObject = row?.site && typeof row.site === 'object' ? row.site : null;
  const siteName = [
    row?.siteName,
    row?.site_name,
    siteObject?.name,
    typeof row?.site === 'string' ? row.site : null
  ].find((value) => typeof value === 'string' && value.trim() && value.trim() !== 'N/A' && value.trim() !== '--');

  return siteName?.trim() || 'Unassigned Site';
};

const getVehicleSiteGroupId = (vehicle) => {
  if (vehicle?.siteId !== null && vehicle?.siteId !== undefined && vehicle?.siteId !== '') {
    return `site:${vehicle.siteId}`;
  }
  return `site-name:${String(vehicle?.siteName || 'Unassigned Site').trim().toLowerCase()}`;
};

const getVehicleDistance = (row) => {
  const distanceValue = [
    row.todayDistance,
    row.distance,
    row.dayDistance,
    row.totalDistance
  ].find((value) => Number.isFinite(Number(value)));

  return distanceValue ? Math.max(0, Math.round(Number(distanceValue))) : 0;
};

const getCurrentStateRowKey = (row, index) => {
  if (row?.uuid) return `uuid:${String(row.uuid).trim()}`;
  if (row?.vehicleID !== undefined && row?.vehicleID !== null) return `id:${row.vehicleID}`;
  if (row?.vehicleId !== undefined && row?.vehicleId !== null) return `id:${row.vehicleId}`;
  if (row?.terminal_id !== undefined && row?.terminal_id !== null) return `id:${row.terminal_id}`;
  if (row?.terminalId !== undefined && row?.terminalId !== null) return `id:${row.terminalId}`;
  if (row?.regNumb) return `reg:${String(row.regNumb).trim().toLowerCase()}`;
  if (row?.name) return `name:${String(row.name).trim().toLowerCase()}`;
  return `index:${index}`;
};

const getCurrentStateRowTimestamp = (row) => {
  const activityDate = Number(row?.omnicommActivityDateID);
  if (Number.isFinite(activityDate)) return activityDate;

  if (!row?.eventDate) return null;
  const eventMoment = moment(row?.eventDate);
  if (eventMoment.isValid()) return eventMoment.valueOf();
  return null;
};

const dedupeCurrentStateRows = (rows = []) => {
  const dedupedRows = [];
  const rowIndexByKey = new Map();

  rows.forEach((row, index) => {
    const key = getCurrentStateRowKey(row, index);
    const existingIndex = rowIndexByKey.get(key);
    if (existingIndex === undefined) {
      rowIndexByKey.set(key, dedupedRows.length);
      dedupedRows.push(row);
      return;
    }

    const existingRow = dedupedRows[existingIndex];
    const rowTimestamp = getCurrentStateRowTimestamp(row);
    const existingTimestamp = getCurrentStateRowTimestamp(existingRow);
    if (existingTimestamp === null || (rowTimestamp !== null && rowTimestamp > existingTimestamp)) {
      dedupedRows[existingIndex] = row;
    }
  });

  return dedupedRows;
};

const buildCurrentStateVehicles = (rows = []) => {
  if (!Array.isArray(rows) || !rows.length) return [];

  return dedupeCurrentStateRows(rows).map((row, index) => {
    const speed = Number(row.speed) || 0;
    const fuelValue = Number(row.currentFuelVolumeTank1);
    const hasFuel = Number.isFinite(fuelValue);
    const lastSyncMinutes = getLastSyncMinutes(row.eventDate);
    const currentIgn = getNullableBoolean(row.currentIgn) === true;
    const status = getVehicleState({
      lastSyncMinutes,
      currentIgn,
      speed,
      omnicommState: row.state || row.status || row.stateLabel,
      engineWork: row.engineWork,
      omnicommActivityStatus: row.omnicommActivityStatus || row.omnicommActivityStatusLabel
    });
    const coordinates = getVehicleCoordinates(row);
    const latitude = coordinates?.latitude ?? null;
    const longitude = coordinates?.longitude ?? null;
    const hasCoordinates = Boolean(coordinates);

    return {
      id: row.uuid || `vehicle-${index + 1}`,
      plate: row.regNumb || `Vehicle ${index + 1}`,
      name: getVehicleDisplayName(row, index),
      placeName: getVehiclePlaceName(row),
      siteId: getVehicleSiteId(row),
      siteName: getVehicleSiteName(row),
      status,
      speed,
      fuelLevel: hasFuel ? fuelValue : 0,
      todayDistance: getVehicleDistance(row),
      lastSyncAt: row.eventDate || null,
      latitude: hasCoordinates ? latitude : null,
      longitude: hasCoordinates ? longitude : null,
      coordinates: hasCoordinates ? `${latitude.toFixed(5)}, ${longitude.toFixed(5)}` : 'N/A',
      idleMinutes: status === 'idle' ? lastSyncMinutes : 0
    };
  });
};

const VehicleListItem = ({
  vehicle, selected, onClick
}) => {
  const statusMeta = STATUS_META[vehicle.status];
  const syncLabel = formatRelativeMinutes(vehicle.lastSyncMinutes);
  const hoverDetails = (
    <Box sx={{ p: 0.75 }}>
      <Typography variant="subtitle2" sx={{ color: '#FFFFFF', fontWeight: 800 }}>{vehicle.plate}</Typography>
      <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.78)' }}>
        Status:
        {' '}
        {statusMeta.shortLabel}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.78)' }}>
        Last sync:
        {' '}
        {formatSyncDate(vehicle.lastSyncAt, vehicle.lastSyncMinutes, 'DD MMM, hh:mm A')}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', mt: 0.45, color: '#E2E8F0' }}>
        Speed:
        {' '}
        {vehicle.speed}
        {' '}
        km/h
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: '#E2E8F0' }}>
        Fuel:
        {' '}
        {formatFuelVolume(vehicle.fuelLevel)}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: '#E2E8F0' }}>
        Distance:
        {' '}
        {vehicle.todayDistance}
        {' '}
        km
      </Typography>
    </Box>
  );

  return (
    <Tooltip title={hoverDetails} placement="right" enterDelay={250} arrow>
      <Box>
        <ButtonBase
          onClick={onClick}
          sx={{
            width: '100%', borderRadius: 2.5, display: 'block', textAlign: 'left'
          }}
        >
          <Card
            sx={{
              p: 1,
              borderRadius: 2.5,
              border: `1px solid ${selected ? alpha(statusMeta.color, 0.28) : 'rgba(148, 163, 184, 0.2)'}`,
              boxShadow: 'none',
              bgcolor: selected ? alpha(statusMeta.color, 0.06) : '#FFFFFF',
              transition: 'background-color 150ms ease, border-color 150ms ease',
              '&:hover': { bgcolor: alpha(statusMeta.color, 0.04), borderColor: alpha(statusMeta.color, 0.24) }
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  flexShrink: 0,
                  bgcolor: statusMeta.color,
                  boxShadow: selected ? `0 0 0 4px ${alpha(statusMeta.color, 0.18)}` : 'none'
                }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 800, lineHeight: 1.25 }}>
                  {vehicle.plate}
                </Typography>
                <Typography noWrap variant="caption" sx={{ display: 'block', mt: 0.2, color: '#64748B' }}>
                  {vehicle.name}
                </Typography>
              </Box>
              {syncLabel !== 'No sync' && (
                <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700, flexShrink: 0 }}>
                  {syncLabel}
                </Typography>
              )}
            </Stack>
          </Card>
        </ButtonBase>
      </Box>
    </Tooltip>
  );
};

VehicleListItem.propTypes = {
  vehicle: PropTypes.object.isRequired,
  selected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired
};

const DetailMetricCard = ({
  label, value, caption, icon: Icon, color
}) => (
  <Card
    sx={{
      p: 1.75, height: '100%', borderRadius: 3, border: `1px solid ${alpha(color, 0.14)}`, boxShadow: 'none', bgcolor: alpha(color, 0.05)
    }}
  >
    <Stack direction="row" justifyContent="space-between" spacing={1.25}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>{label}</Typography>
        <Typography variant="h6" sx={{ mt: 0.75, fontWeight: 800, color: '#0F172A' }}>{value}</Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#475569' }}>{caption}</Typography>
      </Box>
      <Avatar
        variant="rounded"
        sx={{
          width: 40, height: 40, bgcolor: alpha(color, 0.15), color
        }}
      >
        <Icon fontSize="small" />
      </Avatar>
    </Stack>
  </Card>
);

DetailMetricCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  caption: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.string.isRequired
};

const FleetManagement = () => {
  const theme = useTheme();
  const isDesktopFleetShell = useMediaQuery(theme.breakpoints.up('lg'));
  const history = useHistory();
  const location = useLocation();
  const initialFleetContextRef = useRef(getFleetManagementInitialContext(location.search));
  const initialFleetContext = initialFleetContextRef.current;

  const [vehicles, setVehicles] = useState([]);
  const [vehicleRegistry, setVehicleRegistry] = useState([]);
  const [vehicleProfileByRegistration, setVehicleProfileByRegistration] = useState({});
  const [vehicleProfileLoadingByRegistration, setVehicleProfileLoadingByRegistration] = useState({});
  const [vehicleDetailsLinkError, setVehicleDetailsLinkError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [collapsedSiteGroupIds, setCollapsedSiteGroupIds] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [selectedVehicleIds, setSelectedVehicleIds] = useState([]);
  const [activeReport, setActiveReport] = useState(initialFleetContext.report);
  const [openReportTabs, setOpenReportTabs] = useState([initialFleetContext.report]);
  const [reportDateFilters, setReportDateFilters] = useState(() => REPORT_TABS.reduce((filters, tab) => {
    if (!tab.requiresDateFilter) return filters;
    if (tab.value === initialFleetContext.report) {
      return {
        ...filters,
        [tab.value]: {
          preset: initialFleetContext.preset,
          range: initialFleetContext.range
        }
      };
    }
    return {
      ...filters,
      [tab.value]: createDateFilterStateForReport(tab.value)
    };
  }, {}));
  const [dateRangePreset, setDateRangePreset] = useState(initialFleetContext.preset);
  const [dateRange, setDateRange] = useState(initialFleetContext.range);
  const [isVehicleLoading, setVehicleLoading] = useState(false);
  const [isFleetLoading, setFleetLoading] = useState(true);
  const [isRefreshingFleet, setRefreshingFleet] = useState(false);
  const [isVehicleRailCollapsed, setVehicleRailCollapsed] = useState(false);
  const [lastFleetRefreshAt, setLastFleetRefreshAt] = useState(null);
  const [refreshStatusTick, setRefreshStatusTick] = useState(Date.now());
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsRangePreset, setAnalyticsRangePreset] = useState('today');
  const [analyticsPeriodDays, setAnalyticsPeriodDays] = useState(7);
  const [isAnalyticsLoading, setAnalyticsLoading] = useState(false);
  const [isAnalyticsRefreshing, setAnalyticsRefreshing] = useState(false);
  const [analyticsError, setAnalyticsError] = useState('');
  const [analyticsRefreshToken, setAnalyticsRefreshToken] = useState(0);
  const [reportRefreshToken, setReportRefreshToken] = useState(0);
  const [reportLastLoadedAt, setReportLastLoadedAt] = useState({});
  const [statisticsData, setStatisticsData] = useState(null);
  const [isStatisticsLoading, setStatisticsLoading] = useState(false);
  const [statisticsError, setStatisticsError] = useState('');
  const [fuelEventsData, setFuelEventsData] = useState(null);
  const [isFuelEventsLoading, setFuelEventsLoading] = useState(false);
  const [fuelEventsError, setFuelEventsError] = useState('');
  const [tripReportData, setTripReportData] = useState(null);
  const [isTripReportLoading, setTripReportLoading] = useState(false);
  const [tripReportError, setTripReportError] = useState('');
  const [currentRunsData, setCurrentRunsData] = useState(null);
  const [isCurrentRunsLoading, setCurrentRunsLoading] = useState(false);
  const [currentRunsError, setCurrentRunsError] = useState('');
  const [currentRunsPage, setCurrentRunsPage] = useState(1);
  const [eventsData, setEventsData] = useState(null);
  const [isEventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState('');
  const [eventsPage, setEventsPage] = useState(1);
  const [groupWorkData, setGroupWorkData] = useState(null);
  const [isGroupWorkLoading, setGroupWorkLoading] = useState(false);
  const [groupWorkError, setGroupWorkError] = useState('');
  const [logReportData, setLogReportData] = useState(null);
  const [isLogReportLoading, setLogReportLoading] = useState(false);
  const [isLogReportLoadingMore, setLogReportLoadingMore] = useState(false);
  const [isLogReportExporting, setLogReportExporting] = useState(false);
  const [logReportError, setLogReportError] = useState('');
  const [, setLogReportPage] = useState(1);
  const [logReportStatusFilters, setLogReportStatusFilters] = useState({
    ignitionStatus: 'all',
    engineStatus: 'all',
    secondaryEngineStatus: 'all'
  });
  const [dailyReadingSummaryData, setDailyReadingSummaryData] = useState(null);
  const [isDailyReadingSummaryLoading, setDailyReadingSummaryLoading] = useState(false);
  const [dailyReadingSummaryError, setDailyReadingSummaryError] = useState('');
  const [dailyReadingSummaryRefreshToken, setDailyReadingSummaryRefreshToken] = useState(0);
  const [groupWorkPage, setGroupWorkPage] = useState(1);
  const [selectedFuelEventMapRow, setSelectedFuelEventMapRow] = useState(null);
  const [fuelEventMapError, setFuelEventMapError] = useState('');
  const [trackData, setTrackData] = useState(null);
  const [isTrackLoading, setTrackLoading] = useState(false);
  const [trackError, setTrackError] = useState('');
  const [trackFuelData, setTrackFuelData] = useState(null);
  const [isTrackFuelLoading, setTrackFuelLoading] = useState(false);
  const [trackFuelError, setTrackFuelError] = useState('');
  const [selectedTrackPoint, setSelectedTrackPoint] = useState(null);
  const [selectedTrackEventGroup, setSelectedTrackEventGroup] = useState(null);
  const [trackPlaybackIndex, setTrackPlaybackIndex] = useState(0);
  const [isTrackPlaybackActive, setTrackPlaybackActive] = useState(false);
  const [trackPlaybackSpeed, setTrackPlaybackSpeed] = useState(4);
  const [selectedTrackPointDetails, setSelectedTrackPointDetails] = useState(null);
  const [selectedTrackPointDetailsLoading, setSelectedTrackPointDetailsLoading] = useState(false);
  const [selectedTrackPointDetailsError, setSelectedTrackPointDetailsError] = useState('');
  const [googleMapsKey, setGoogleMapsKey] = useState('');
  const [googleMapsMapId, setGoogleMapsMapId] = useState('');
  const [isMapKeyLoading, setMapKeyLoading] = useState(false);
  const [isMapScriptLoading, setMapScriptLoading] = useState(false);
  const [, setMapReady] = useState(false);
  const [mapError, setMapError] = useState('');
  const [mapReloadToken, setMapReloadToken] = useState(0);
  const [eventLocationDialogReadyToken, setEventLocationDialogReadyToken] = useState(0);
  const locationMapContainerRef = useRef(null);
  const locationMapInstanceRef = useRef(null);
  const locationMapMarkersRef = useRef([]);
  const locationMapInfoWindowRef = useRef(null);
  const fuelEventMapContainerRef = useRef(null);
  const eventsLocationMapContainerRef = useRef(null);
  const fuelEventMapInstanceRef = useRef(null);
  const fuelEventMapMarkerRef = useRef(null);
  const fuelEventMapInfoWindowRef = useRef(null);
  const trackMapContainerRef = useRef(null);
  const trackMapInstanceRef = useRef(null);
  const trackMapMarkersRef = useRef([]);
  const trackMapPolylineRef = useRef(null);
  const trackMapInfoWindowRef = useRef(null);
  const trackMapCurrentMarkerRef = useRef(null);
  const trackPlaybackTimerRef = useRef(null);
  const mapScriptWatchdogRef = useRef(null);
  const mapConfigRequestRef = useRef(null);
  const analyticsReportCacheRef = useRef(new Map());
  const statisticsReportCacheRef = useRef(new Map());
  const fuelReportCacheRef = useRef(new Map());
  const tripReportCacheRef = useRef(new Map());
  const currentRunsCacheRef = useRef(new Map());
  const eventsCacheRef = useRef(new Map());
  const groupWorkCacheRef = useRef(new Map());
  const logReportCacheRef = useRef(new Map());
  const dailyReadingSummaryCacheRef = useRef(new Map());
  const trackReportCacheRef = useRef(new Map());
  const trackFuelReportCacheRef = useRef(new Map());
  const analyticsDataRef = useRef(null);
  const hasConsumedVehicleDetailsStateRef = useRef(false);

  const fleetCounts = useMemo(() => ({
    total: vehicles.length,
    active: vehicles.filter((vehicle) => vehicle.status === 'active').length,
    idle: vehicles.filter((vehicle) => vehicle.status === 'idle').length,
    offline: vehicles.filter((vehicle) => vehicle.status === 'offline').length
  }), [vehicles]);

  const filteredVehicles = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    return vehicles.filter((vehicle) => {
      const matchesStatus = statusFilter === 'all' ? true : vehicle.status === statusFilter;
      const matchesQuery = query ? [vehicle.plate, vehicle.name, vehicle.siteName].join(' ').toLowerCase().includes(query) : true;
      return matchesStatus && matchesQuery;
    });
  }, [vehicles, searchText, statusFilter]);

  const vehicleSiteGroups = useMemo(() => {
    const siteGroupsById = new Map();

    filteredVehicles.forEach((vehicle) => {
      const siteGroupId = getVehicleSiteGroupId(vehicle);
      if (!siteGroupsById.has(siteGroupId)) {
        siteGroupsById.set(siteGroupId, {
          id: siteGroupId,
          siteName: vehicle.siteName || 'Unassigned Site',
          counts: {
            active: 0,
            idle: 0,
            offline: 0
          },
          vehicles: []
        });
      }

      const siteGroup = siteGroupsById.get(siteGroupId);
      siteGroup.vehicles.push(vehicle);
      if (siteGroup.counts[vehicle.status] !== undefined) {
        siteGroup.counts[vehicle.status] += 1;
      }
    });

    return Array.from(siteGroupsById.values());
  }, [filteredVehicles]);

  const shouldUseHoverVehicleRail = isDesktopFleetShell && Boolean(selectedVehicleId);

  const fleetShellColumns = useMemo(() => {
    if (!isDesktopFleetShell) return 'minmax(0, 1fr)';
    if (shouldUseHoverVehicleRail) return 'minmax(0, 1fr)';
    if (isVehicleRailCollapsed) return '84px minmax(0, 1fr)';
    return 'minmax(248px, 276px) minmax(0, 1fr)';
  }, [isDesktopFleetShell, isVehicleRailCollapsed, shouldUseHoverVehicleRail]);

  const selectedVehicle = useMemo(
    () => vehicles.find((vehicle) => vehicle.id === selectedVehicleId) || null,
    [vehicles, selectedVehicleId]
  );
  const vehicleIdByRegistration = useMemo(() => {
    const registrationMap = new Map();
    vehicleRegistry.forEach((vehicle) => {
      const normalizedRegistration = normalizeRegistrationNumber(vehicle?.registration_num);
      if (!normalizedRegistration || !vehicle?.id) return;
      if (!registrationMap.has(normalizedRegistration)) {
        registrationMap.set(normalizedRegistration, vehicle.id);
      }
    });
    return registrationMap;
  }, [vehicleRegistry]);
  const registrationByBackendVehicleId = useMemo(() => {
    const backendIdMap = new Map();
    vehicleRegistry.forEach((vehicle) => {
      if (vehicle?.id === null || vehicle?.id === undefined) return;
      const normalizedRegistration = normalizeRegistrationNumber(vehicle?.registration_num);
      if (!normalizedRegistration) return;
      backendIdMap.set(String(vehicle.id), normalizedRegistration);
    });
    return backendIdMap;
  }, [vehicleRegistry]);
  const selectedBackendVehicleId = useMemo(() => {
    const normalizedRegistration = normalizeRegistrationNumber(selectedVehicle?.plate);
    if (!normalizedRegistration) return null;
    return vehicleIdByRegistration.get(normalizedRegistration) || null;
  }, [selectedVehicle?.plate, vehicleIdByRegistration]);
  const selectedBackendVehicle = useMemo(
    () => vehicleRegistry.find((vehicle) => String(vehicle?.id) === String(selectedBackendVehicleId)) || null,
    [selectedBackendVehicleId, vehicleRegistry]
  );
  const selectedVehicleProfileKey = normalizeRegistrationNumber(selectedVehicle?.plate);
  const selectedVehicleProfile = selectedVehicleProfileKey
    ? vehicleProfileByRegistration[selectedVehicleProfileKey] || null
    : null;
  const effectiveFleetTimezone = useMemo(
    () => getEffectiveFleetTimezone(selectedVehicle, selectedBackendVehicle),
    [selectedBackendVehicle, selectedVehicle]
  );
  const selectedSecondaryEngineThreshold = useMemo(() => {
    const parsedThreshold = parseFleetLogNumber(selectedBackendVehicle?.omnicomm_secondary_engine_threshold);
    return parsedThreshold === null ? LOG_REPORT_SECONDARY_ENGINE_DEFAULT_THRESHOLD : parsedThreshold;
  }, [selectedBackendVehicle?.omnicomm_secondary_engine_threshold]);
  const selectedHasSecondaryEngine = typeof selectedVehicleProfile?.capabilities?.secondaryEngine === 'boolean'
    ? selectedVehicleProfile.capabilities.secondaryEngine
    : selectedBackendVehicle?.show_omnicomm_log_secondary_engine !== false;

  const isLocationSelectionMode = activeReport === 'location';
  const isAnalyticsReportActive = activeReport === 'analytics';
  const isFuelReportActive = activeReport === 'fuel';
  const isEventsReportActive = activeReport === 'events';
  const isLogReportActive = activeReport === 'logReport';
  const isTrackReportActive = activeReport === 'track';
  const isTrackFuelReportActive = activeReport === 'trackFuel';
  const isTrackVisualReportActive = isTrackReportActive || isTrackFuelReportActive || isAnalyticsReportActive;
  const activeReportConfig = useMemo(
    () => REPORT_TABS.find((tab) => tab.value === activeReport) || null,
    [activeReport]
  );
  const isEventLocationMapActive = (isFuelReportActive || isEventsReportActive || isLogReportActive)
    && Boolean(selectedFuelEventMapRow)
    && selectedFuelEventMapRow?.sourceReport === activeReport;
  const shouldEnableGoogleMaps = isLocationSelectionMode || isTrackVisualReportActive || isEventLocationMapActive;

  const unixDateRange = useMemo(() => ({
    from: toUnixSeconds(dateRange.fromDateTime, effectiveFleetTimezone),
    to: toUnixSeconds(dateRange.toDateTime, effectiveFleetTimezone)
  }), [dateRange.fromDateTime, dateRange.toDateTime, effectiveFleetTimezone]);

  const fuelDateRange = useMemo(() => ({
    from: Number.isFinite(unixDateRange.from) ? unixDateRange.from * 1000 : null,
    to: Number.isFinite(unixDateRange.to) ? unixDateRange.to * 1000 : null
  }), [unixDateRange.from, unixDateRange.to]);

  const isUnixDateRangeValid = useMemo(
    () => Number.isFinite(unixDateRange.from) && Number.isFinite(unixDateRange.to) && unixDateRange.from <= unixDateRange.to,
    [unixDateRange.from, unixDateRange.to]
  );
  const fromDateRangeMoment = useMemo(
    () => moment(dateRange.fromDateTime, DATE_TIME_INPUT_FORMAT, true),
    [dateRange.fromDateTime]
  );
  const toDateRangeMoment = useMemo(
    () => moment(dateRange.toDateTime, DATE_TIME_INPUT_FORMAT, true),
    [dateRange.toDateTime]
  );
  const dailyReadingDateRange = useMemo(() => ({
    startDate: dateRange.fromDateTime ? dateRange.fromDateTime.slice(0, 10) : '',
    endDate: dateRange.toDateTime ? dateRange.toDateTime.slice(0, 10) : ''
  }), [dateRange.fromDateTime, dateRange.toDateTime]);
  const isDateRangeWithinLimit = useMemo(() => {
    if (!fromDateRangeMoment.isValid() || !toDateRangeMoment.isValid()) return false;
    return toDateRangeMoment.diff(fromDateRangeMoment, 'days', true) <= MAX_DATE_RANGE_DAYS;
  }, [fromDateRangeMoment, toDateRangeMoment]);
  const isFleetDateRangeValid = isUnixDateRangeValid && isDateRangeWithinLimit;
  const dateRangeValidationMessage = isDateRangeWithinLimit
    ? 'Please choose a valid From and To date-time range.'
    : `Please choose a date range of ${MAX_DATE_RANGE_DAYS} days or less.`;
  const shouldUseRawLogReport = true;
  const minAllowedFromDateTime = toDateRangeMoment.isValid()
    ? toDateRangeMoment.clone().subtract(MAX_DATE_RANGE_DAYS, 'days').format(DATE_TIME_INPUT_FORMAT)
    : undefined;
  const maxAllowedToDateTime = fromDateRangeMoment.isValid()
    ? fromDateRangeMoment.clone().add(MAX_DATE_RANGE_DAYS, 'days').format(DATE_TIME_INPUT_FORMAT)
    : undefined;

  const locationSelectedVehicles = useMemo(() => {
    const selectedMap = new Map();
    selectedVehicleIds.forEach((id) => {
      const matchedVehicle = vehicles.find((vehicle) => vehicle.id === id);
      if (matchedVehicle) {
        selectedMap.set(matchedVehicle.id, matchedVehicle);
      }
    });
    return Array.from(selectedMap.values());
  }, [selectedVehicleIds, vehicles]);

  const locationMapMarkers = useMemo(
    () => locationSelectedVehicles.map((vehicle) => {
      const coordinates = getVehicleCoordinates(vehicle);
      if (!coordinates) return null;
      return {
        id: vehicle.id,
        plate: vehicle.plate,
        placeName: vehicle.placeName,
        status: vehicle.status,
        speed: vehicle.speed,
        fuelLevel: vehicle.fuelLevel,
        latitude: coordinates.latitude,
        longitude: coordinates.longitude
      };
    }).filter(Boolean),
    [locationSelectedVehicles]
  );

  const activeTrackData = useMemo(() => {
    if (isAnalyticsReportActive) return analyticsData?.track || null;
    return isTrackFuelReportActive ? trackFuelData : trackData;
  }, [analyticsData?.track, isAnalyticsReportActive, isTrackFuelReportActive, trackData, trackFuelData]);
  let activeTrackLoading = isTrackLoading;
  if (isAnalyticsReportActive) activeTrackLoading = isAnalyticsLoading;
  else if (isTrackFuelReportActive) activeTrackLoading = isTrackFuelLoading;
  let activeTrackError = trackError;
  if (isAnalyticsReportActive) activeTrackError = analyticsData?.errors?.track || '';
  else if (isTrackFuelReportActive) activeTrackError = trackFuelError;

  const handleStatisticsVehicleClick = useCallback(() => {
    const lookupCandidates = [
      selectedVehicle?.plate,
      statisticsData?.vehicleData?.name
    ];
    const matchedVehicleId = lookupCandidates
      .map((value) => normalizeRegistrationNumber(value))
      .find((normalizedValue) => normalizedValue && vehicleIdByRegistration.has(normalizedValue));

    if (!matchedVehicleId) {
      setVehicleDetailsLinkError('Vehicle details not found.');
      return;
    }

    setVehicleDetailsLinkError('');
    const vehicleId = vehicleIdByRegistration.get(matchedVehicleId);
    history.push(ROUTE.vehicleDetails.replace(':vehicleId', vehicleId));
  }, [history, selectedVehicle?.plate, statisticsData?.vehicleData?.name, vehicleIdByRegistration]);
  const primeReportLoading = useCallback((reportValue) => {
    if (reportValue === 'analytics') setAnalyticsLoading(true);
    if (reportValue === 'statistics') setStatisticsLoading(true);
    if (reportValue === 'fuel') setFuelEventsLoading(true);
    if (reportValue === 'tripReport') setTripReportLoading(true);
    if (reportValue === 'currentRuns') setCurrentRunsLoading(true);
    if (reportValue === 'events') setEventsLoading(true);
    if (reportValue === 'groupWork') setGroupWorkLoading(true);
    if (reportValue === 'logReport') setLogReportLoading(true);
    if (reportValue === 'dailyReadingSummary') setDailyReadingSummaryLoading(true);
    if (reportValue === 'track') setTrackLoading(true);
    if (reportValue === 'trackFuel') setTrackFuelLoading(true);
  }, []);

  const markReportLoaded = useCallback((reportValue) => {
    setReportLastLoadedAt((previousLoadedAt) => ({
      ...previousLoadedAt,
      [reportValue]: Date.now()
    }));
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadVehicleRegistry = async () => {
      try {
        const response = await APIService.getAllVehicles();
        const vehiclesList = Array.isArray(response?.result) ? response.result : [];
        if (isMounted) setVehicleRegistry(vehiclesList);
      } catch (error) {
        if (isMounted) setVehicleRegistry([]);
      }
    };
    loadVehicleRegistry();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedVehicle?.plate || !selectedVehicleProfileKey) return () => { };
    if (vehicleProfileByRegistration[selectedVehicleProfileKey]) return () => { };
    if (vehicleProfileLoadingByRegistration[selectedVehicleProfileKey]) return () => { };

    let isMounted = true;
    setVehicleProfileLoadingByRegistration((profiles) => ({
      ...profiles,
      [selectedVehicleProfileKey]: true
    }));

    const loadVehicleProfile = async () => {
      try {
        const response = await APIService.getFleetVehicleProfile({
          registration_num: selectedVehicle.plate
        });
        if (!isMounted) return;
        const normalizedProfile = normalizeFleetVehicleProfile(response);
        setVehicleProfileByRegistration((profiles) => ({
          ...profiles,
          [selectedVehicleProfileKey]: normalizedProfile
        }));
      } catch (error) {
        console.log('fleet vehicle profile error', error?.message || error);
        if (isMounted) {
          setVehicleProfileByRegistration((profiles) => ({
            ...profiles,
            [selectedVehicleProfileKey]: {
              registrationNum: selectedVehicle.plate,
              loadError: true,
              capabilities: {
                secondaryEngine: selectedBackendVehicle?.show_omnicomm_log_secondary_engine !== false,
                universalInputs: false
              }
            }
          }));
        }
      } finally {
        if (isMounted) {
          setVehicleProfileLoadingByRegistration((profiles) => ({
            ...profiles,
            [selectedVehicleProfileKey]: false
          }));
        }
      }
    };

    loadVehicleProfile();
    return () => {
      isMounted = false;
    };
  }, [selectedBackendVehicle?.show_omnicomm_log_secondary_engine, selectedVehicle?.plate, selectedVehicleProfileKey, vehicleProfileByRegistration, vehicleProfileLoadingByRegistration]);

  useEffect(() => {
    setVehicleDetailsLinkError('');
  }, [selectedVehicleId]);

  useEffect(() => {
    analyticsDataRef.current = analyticsData;
  }, [analyticsData]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const queryVehicleId = query.get('vehicle');
    const queryReport = query.get('report');
    let vehicleState = null;
    if (location.state?.fromVehicleDetails) {
      vehicleState = location.state;
    } else if (queryVehicleId) {
      vehicleState = {
        fromVehicleDetails: true,
        targetVehicleId: queryVehicleId,
        targetReport: queryReport
      };
    }
    if (hasConsumedVehicleDetailsStateRef.current) return;
    if (!vehicleState?.fromVehicleDetails) return;

    let normalizedTargetRegistration = normalizeRegistrationNumber(vehicleState?.targetRegistration);
    if (!normalizedTargetRegistration && vehicleState?.targetVehicleId !== null && vehicleState?.targetVehicleId !== undefined) {
      if (!vehicleRegistry.length) return;
      normalizedTargetRegistration = registrationByBackendVehicleId.get(String(vehicleState.targetVehicleId)) || '';
    }

    const requestedReport = REPORT_TABS.some((tab) => tab.value === vehicleState?.targetReport)
      ? vehicleState.targetReport
      : DEFAULT_REPORT_TAB;
    if (requestedReport) {
      if (!openReportTabs.includes(requestedReport)) {
        setOpenReportTabs((previousTabs) => (
          previousTabs.includes(requestedReport) ? previousTabs : [...previousTabs, requestedReport]
        ));
      }
      if (activeReport !== requestedReport) {
        const requestedReportConfig = REPORT_TABS.find((tab) => tab.value === requestedReport);
        const requestedFilter = reportDateFilters[requestedReport] || createDateFilterStateForReport(requestedReport);
        primeReportLoading(requestedReport);
        if (requestedReportConfig?.requiresDateFilter) {
          setDateRangePreset(requestedFilter.preset);
          setDateRange(requestedFilter.range);
        }
        setActiveReport(requestedReport);
      }
    }

    if (normalizedTargetRegistration && !vehicles.length) return;

    if (normalizedTargetRegistration) {
      const matchedVehicle = vehicles.find((vehicle) => normalizeRegistrationNumber(vehicle?.plate) === normalizedTargetRegistration);
      if (matchedVehicle?.id) {
        setSelectedVehicleId(matchedVehicle.id);
        setSelectedVehicleIds((previousIds) => (
          previousIds.length === 1 && previousIds[0] === matchedVehicle.id ? previousIds : [matchedVehicle.id]
        ));
      }
    }

    hasConsumedVehicleDetailsStateRef.current = true;
    if (location.state?.fromVehicleDetails) {
      history.replace({
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        state: undefined
      });
    }
  }, [
    activeReport,
    history,
    location.hash,
    location.pathname,
    location.search,
    location.state,
    openReportTabs,
    reportDateFilters,
    primeReportLoading,
    vehicleRegistry.length,
    registrationByBackendVehicleId,
    vehicles
  ]);

  const activeReportLoadingStatus = useMemo(() => {
    if (activeReport === 'analytics') {
      return {
        isLoading: isAnalyticsLoading || isAnalyticsRefreshing || shouldShowFirstLoadSkeleton({
          isLoading: isAnalyticsLoading,
          error: analyticsError,
          data: analyticsData
        }),
        label: 'Dashboard'
      };
    }
    if (activeReport === 'statistics') {
      return {
        isLoading: isStatisticsLoading || shouldShowFirstLoadSkeleton({
          isLoading: isStatisticsLoading,
          error: statisticsError,
          data: statisticsData
        }),
        label: 'Statistics'
      };
    }
    if (activeReport === 'fuel') {
      return {
        isLoading: isFuelEventsLoading || shouldShowFirstLoadSkeleton({
          isLoading: isFuelEventsLoading,
          error: fuelEventsError,
          data: fuelEventsData
        }),
        label: 'Fuel Events'
      };
    }
    if (activeReport === 'tripReport') {
      return {
        isLoading: isTripReportLoading || shouldShowFirstLoadSkeleton({
          isLoading: isTripReportLoading,
          error: tripReportError,
          data: tripReportData
        }),
        label: 'Trip Report'
      };
    }
    if (activeReport === 'currentRuns') {
      return {
        isLoading: isCurrentRunsLoading || shouldShowFirstLoadSkeleton({
          isLoading: isCurrentRunsLoading,
          error: currentRunsError,
          data: currentRunsData
        }),
        label: 'Current Runs'
      };
    }
    if (activeReport === 'events') {
      return {
        isLoading: isEventsLoading || shouldShowFirstLoadSkeleton({
          isLoading: isEventsLoading,
          error: eventsError,
          data: eventsData
        }),
        label: 'Events'
      };
    }
    if (activeReport === 'groupWork') {
      return {
        isLoading: isGroupWorkLoading || shouldShowFirstLoadSkeleton({
          isLoading: isGroupWorkLoading,
          error: groupWorkError,
          data: groupWorkData
        }),
        label: 'Group Work'
      };
    }
    if (activeReport === 'logReport') {
      return {
        isLoading: isLogReportLoading || shouldShowFirstLoadSkeleton({
          isLoading: isLogReportLoading,
          error: logReportError,
          data: logReportData
        }),
        label: 'Log Report'
      };
    }
    if (activeReport === 'dailyReadingSummary') {
      return {
        isLoading: isDailyReadingSummaryLoading || shouldShowFirstLoadSkeleton({
          isLoading: isDailyReadingSummaryLoading,
          error: dailyReadingSummaryError,
          data: dailyReadingSummaryData
        }),
        label: 'Daily Reading Summary'
      };
    }
    if (activeReport === 'location') {
      return {
        isLoading: isMapKeyLoading || isMapScriptLoading,
        label: 'Location'
      };
    }
    if (activeReport === 'trackFuel') {
      return {
        isLoading: isTrackFuelLoading || shouldShowFirstLoadSkeleton({
          isLoading: isTrackFuelLoading,
          error: trackFuelError,
          data: trackFuelData
        }),
        label: 'Track + Fuel'
      };
    }
    return {
      isLoading: isTrackLoading || shouldShowFirstLoadSkeleton({
        isLoading: isTrackLoading,
        error: trackError,
        data: trackData
      }),
      label: 'Track'
    };
  }, [
    activeReport,
    analyticsData,
    analyticsError,
    currentRunsData,
    currentRunsError,
    dailyReadingSummaryData,
    dailyReadingSummaryError,
    eventsData,
    eventsError,
    fuelEventsData,
    fuelEventsError,
    groupWorkData,
    groupWorkError,
    isAnalyticsLoading,
    isAnalyticsRefreshing,
    isLogReportLoading,
    isCurrentRunsLoading,
    isDailyReadingSummaryLoading,
    isEventsLoading,
    isFuelEventsLoading,
    isGroupWorkLoading,
    isMapKeyLoading,
    isMapScriptLoading,
    isStatisticsLoading,
    isTrackFuelLoading,
    isTrackLoading,
    isTripReportLoading,
    logReportData,
    logReportError,
    statisticsData,
    statisticsError,
    trackData,
    trackError,
    trackFuelData,
    trackFuelError,
    tripReportData,
    tripReportError
  ]);

  const trackSummary = useMemo(
    () => getTrackSummary(activeTrackData, activeTrackData?.lastKnownPosition || null),
    [activeTrackData]
  );

  const trackIgnitionGroups = useMemo(
    () => getTrackIgnitionEventGroups(activeTrackData?.events || []),
    [activeTrackData]
  );

  const trackEventMarkers = useMemo(
    () => getTrackEventMarkers(activeTrackData?.points || []),
    [activeTrackData]
  );

  const sampledTrackMarkers = useMemo(
    () => getSampledTrackMarkers(
      activeTrackData?.points || [],
      isTrackVisualReportActive ? 24 : TRACK_CLICKABLE_MARKERS_LIMIT,
      new Set(trackEventMarkers.map(({ point }) => point.id))
    ),
    [activeTrackData, isTrackVisualReportActive, trackEventMarkers]
  );
  const clampedTrackPlaybackIndex = useMemo(() => {
    const totalTrackPoints = activeTrackData?.points?.length || 0;
    if (!totalTrackPoints) return 0;
    return Math.min(Math.max(trackPlaybackIndex, 0), totalTrackPoints - 1);
  }, [activeTrackData, trackPlaybackIndex]);
  const trackPlaybackPoint = useMemo(
    () => activeTrackData?.points?.[clampedTrackPlaybackIndex] || null,
    [activeTrackData, clampedTrackPlaybackIndex]
  );
  const trackPlaybackIntervalMs = useMemo(() => Math.max(
    TRACK_MAP_PLAYBACK_MIN_INTERVAL_MS,
    Math.round(TRACK_MAP_PLAYBACK_BASE_INTERVAL_MS / (trackPlaybackSpeed || 1))
  ), [trackPlaybackSpeed]);
  const trackFuelChartOptions = useMemo(() => {
    const fuelLevelSeries = trackFuelData?.fuelLevelSeries || null;
    const fallbackSmoothedSeries = (trackFuelData?.points || [])
      .filter((point) => Number.isFinite(point?.timestamp) && Number.isFinite(point?.fuelVolume))
      .map((point) => [point.timestamp, Number(point.fuelVolume.toFixed(1))]);
    const hasFuelLevelSmoothedSeries = Array.isArray(fuelLevelSeries?.smoothedSeries) && fuelLevelSeries.smoothedSeries.length;
    const smoothedFuelSeries = hasFuelLevelSmoothedSeries
      ? fuelLevelSeries.smoothedSeries
      : fallbackSmoothedSeries;
    const actualFuelSeries = Array.isArray(fuelLevelSeries?.actualSeries)
      ? fuelLevelSeries.actualSeries
      : [];

    if (!smoothedFuelSeries.length && !actualFuelSeries.length) return null;

    const combinedFuelValues = [...smoothedFuelSeries, ...actualFuelSeries]
      .map((point) => Number(point?.[1]))
      .filter((value) => Number.isFinite(value));
    const tankCapacity = Number(fuelLevelSeries?.tankCapacity);
    let yAxisMaxCandidate = null;
    if (Number.isFinite(tankCapacity)) yAxisMaxCandidate = tankCapacity;
    else if (combinedFuelValues.length) yAxisMaxCandidate = Math.max(...combinedFuelValues);
    const yAxisMax = Number.isFinite(yAxisMaxCandidate)
      ? Math.max(10, Math.ceil(yAxisMaxCandidate / 10) * 10)
      : null;

    return {
      animation: false,
      grid: {
        top: 58,
        left: 56,
        right: 34,
        bottom: 84
      },
      legend: {
        top: 14,
        icon: 'roundRect',
        itemWidth: 18,
        itemHeight: 10,
        textStyle: { color: '#475569', fontSize: 12, fontWeight: 700 }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line',
          lineStyle: { color: 'rgba(148, 163, 184, 0.5)', width: 1 }
        },
        backgroundColor: 'rgba(15, 23, 42, 0.94)',
        borderWidth: 0,
        textStyle: { color: '#F8FAFC' },
        formatter: (items) => {
          const rows = Array.isArray(items) ? items : [items];
          const [firstItem] = rows;
          const timestamp = firstItem?.value?.[0];
          const lines = rows.map((item) => {
            const suffix = ' L';
            return `${item.marker}${item.seriesName}: ${Number(item.value?.[1] || 0).toLocaleString('en-IN', {
              maximumFractionDigits: 1,
              minimumFractionDigits: 1
            })}${suffix}`;
          });
          return [
            moment(timestamp).format('DD MMM YYYY, hh:mm A'),
            ...lines
          ].join('<br/>');
        }
      },
      xAxis: {
        type: 'time',
        axisLabel: {
          color: '#64748B',
          formatter: (value) => moment(value).format('DD MMM\nHH:mm')
        },
        axisLine: { lineStyle: { color: '#CBD5E1' } },
        splitLine: { show: false }
      },
      yAxis: {
        type: 'value',
        name: 'Fuel (L)',
        nameTextStyle: { color: '#475569', fontWeight: 700 },
        axisLabel: {
          color: '#64748B',
          formatter: (value) => `${value}`
        },
        max: yAxisMax || undefined,
        axisLine: { show: false },
        splitLine: { lineStyle: { color: 'rgba(148, 163, 184, 0.18)' } }
      },
      dataZoom: [
        {
          type: 'inside',
          throttle: 60
        },
        {
          type: 'slider',
          height: 26,
          bottom: 18,
          borderColor: 'rgba(148, 163, 184, 0.18)',
          fillerColor: 'rgba(37, 99, 235, 0.12)',
          handleStyle: { color: '#2563EB' }
        }
      ],
      series: [
        {
          name: 'Smooth fuel',
          type: 'line',
          showSymbol: false,
          triggerLineEvent: true,
          smooth: 0.2,
          sampling: 'lttb',
          lineStyle: { width: 3.2, color: '#0F172A' },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(15, 23, 42, 0.16)' },
                { offset: 1, color: 'rgba(15, 23, 42, 0.02)' }
              ]
            }
          },
          emphasis: { focus: 'series' },
          data: smoothedFuelSeries
        },
        {
          name: 'Actual fuel',
          type: 'line',
          showSymbol: false,
          triggerLineEvent: true,
          smooth: false,
          sampling: 'lttb',
          lineStyle: { width: 1.8, color: '#F97316', opacity: 0.92 },
          itemStyle: { color: '#F97316' },
          emphasis: { focus: 'series' },
          data: actualFuelSeries
        }
      ]
    };
  }, [trackFuelData]);

  const handleTrackFuelChartClick = useCallback((chartParams) => {
    let clickedValue = null;
    if (Array.isArray(chartParams?.value)) clickedValue = chartParams.value;
    else if (Array.isArray(chartParams?.data)) clickedValue = chartParams.data;

    const clickedTimestamp = Number(clickedValue?.[0] ?? chartParams?.axisValue);
    const matchedTrackPoint = getNearestTrackPointByTimestamp(activeTrackData?.points || [], clickedTimestamp);
    if (!matchedTrackPoint) return;

    const { point, pointIndex } = matchedTrackPoint;
    const pointPosition = { lat: point.latitude, lng: point.longitude };
    const pointTitle = 'Fuel point location';

    setTrackPlaybackActive(false);
    setTrackPlaybackIndex(pointIndex ?? 0);
    setSelectedTrackEventGroup(null);
    setSelectedTrackPoint({
      point,
      pointIndex,
      title: pointTitle,
      markerType: 'fuel'
    });

    const mapInstance = trackMapInstanceRef.current;
    if (mapInstance && Number.isFinite(point.latitude) && Number.isFinite(point.longitude)) {
      mapInstance.panTo(pointPosition);
      mapInstance.setZoom(Math.max(mapInstance.getZoom() || 14, 16));
    }

    const currentMarker = trackMapCurrentMarkerRef.current;
    if (currentMarker && typeof currentMarker.setPosition === 'function') {
      currentMarker.setPosition(pointPosition);
      currentMarker.setTitle(pointTitle);
    }

    if (mapInstance && currentMarker) {
      if (!trackMapInfoWindowRef.current) trackMapInfoWindowRef.current = new window.google.maps.InfoWindow();
      trackMapInfoWindowRef.current.setContent(getTrackMarkerInfoContent({
        title: pointTitle,
        plate: selectedVehicle?.plate || 'Vehicle',
        speed: point.speed,
        fuelVolume: point.fuelVolume,
        satellitesCount: point.satellitesCount,
        timestamp: point.timestamp || point.eventDate || point.rawTimestamp,
        direction: point.direction,
        latitude: point.latitude,
        longitude: point.longitude,
        ignition: point.ignition,
        isStopped: point.isStopped,
        gpsValid: point.gpsValid ?? point.gpsDataValid ?? null,
        events: point.events || [],
        isLoading: true
      }));
      trackMapInfoWindowRef.current.open({ anchor: currentMarker, map: mapInstance, shouldFocus: false });
    }
  }, [activeTrackData?.points, selectedVehicle?.plate]);

  const trackFuelChartEvents = useMemo(() => ({
    click: handleTrackFuelChartClick
  }), [handleTrackFuelChartClick]);

  const resolvedGoogleMapsMapId = useMemo(() => getResolvedGoogleMapId(googleMapsMapId), [googleMapsMapId]);
  const refreshStatusLabel = useMemo(
    () => formatRefreshStatusLabel(lastFleetRefreshAt, refreshStatusTick),
    [lastFleetRefreshAt, refreshStatusTick]
  );

  const loadGoogleMapsConfig = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh) {
      if (googleMapsKey) {
        setMapError('');
        setMapScriptLoading(!isGoogleMapsApiReady());
        return { apiKey: googleMapsKey, mapId: googleMapsMapId };
      }
    }

    if (!forceRefresh && mapConfigRequestRef.current) {
      return mapConfigRequestRef.current;
    }

    setMapKeyLoading(true);
    setMapScriptLoading(false);
    setMapReady(false);
    setMapError('');

    const requestPromise = (async () => {
      try {
        let localMapsKey = process.env.REACT_APP_FLEET_GOOGLE_MAPS_API_KEY;
        let localMapsMapId = process.env.REACT_APP_FLEET_GOOGLE_MAPS_MAP_ID || '';
        try {
          localMapsKey = localMapsKey || window.sessionStorage.getItem(MAP_KEY_SESSION_STORAGE_KEY) || window.localStorage.getItem(MAP_KEY_SESSION_STORAGE_KEY);
          localMapsMapId = localMapsMapId || window.sessionStorage.getItem(MAP_ID_SESSION_STORAGE_KEY) || window.localStorage.getItem(MAP_ID_SESSION_STORAGE_KEY) || '';
        } catch (error) {
          console.log('google maps local key read error', error?.message || error);
        }
        if (localMapsKey && String(localMapsKey).trim()) {
          const apiKey = String(localMapsKey).trim();
          const apiMapId = String(localMapsMapId).trim();
          try {
            window.sessionStorage.setItem(MAP_KEY_SESSION_STORAGE_KEY, apiKey);
            if (apiMapId) window.sessionStorage.setItem(MAP_ID_SESSION_STORAGE_KEY, apiMapId);
            else window.sessionStorage.removeItem(MAP_ID_SESSION_STORAGE_KEY);
          } catch (error) {
            console.log('google maps local key cache write error', error?.message || error);
          }
          setGoogleMapsKey(apiKey);
          setGoogleMapsMapId(apiMapId);
          setMapScriptLoading(true);
          return { apiKey, mapId: apiMapId };
        }

        const response = await APIService.getFleetGoogleMapsApiKey();
        const apiKeyCandidates = [
          response?.result?.google_maps_api_key,
          response?.result?.googleMapApiKey,
          response?.result?.api_key,
          response?.result?.map_api_key,
          response?.result?.data?.google_maps_api_key,
          response?.result?.data?.googleMapApiKey,
          response?.result?.data?.api_key,
          response?.google_maps_api_key,
          response?.api_key
        ];
        const apiKey = apiKeyCandidates.find((value) => typeof value === 'string' && value.trim())?.trim();
        const mapIdCandidates = [
          response?.result?.google_maps_map_id,
          response?.result?.google_map_id,
          response?.result?.map_id,
          response?.result?.googleMapId,
          response?.result?.mapId,
          response?.result?.data?.google_maps_map_id,
          response?.result?.data?.google_map_id,
          response?.result?.data?.map_id,
          response?.result?.data?.googleMapId,
          response?.result?.data?.mapId,
          response?.google_maps_map_id,
          response?.google_map_id,
          response?.map_id,
          response?.mapId
        ];
        const apiMapId = mapIdCandidates.find((value) => typeof value === 'string' && value.trim())?.trim() || '';

        if (!apiKey) throw new Error('Google Maps API key missing in response');

        try {
          window.sessionStorage.setItem(MAP_KEY_SESSION_STORAGE_KEY, apiKey);
          if (apiMapId) window.sessionStorage.setItem(MAP_ID_SESSION_STORAGE_KEY, apiMapId);
          else window.sessionStorage.removeItem(MAP_ID_SESSION_STORAGE_KEY);
        } catch (error) {
          console.log('google maps key cache write error', error?.message || error);
        }

        setGoogleMapsKey(apiKey);
        setGoogleMapsMapId(apiMapId);
        setMapScriptLoading(true);
        return { apiKey, mapId: apiMapId };
      } catch (error) {
        console.log('google maps key fetch error', error?.message || error);
        setMapError('Unable to load map. Please retry.');
        setGoogleMapsKey('');
        setGoogleMapsMapId('');
        setMapScriptLoading(false);
        return null;
      } finally {
        setMapKeyLoading(false);
        mapConfigRequestRef.current = null;
      }
    })();

    mapConfigRequestRef.current = requestPromise;
    return requestPromise;
  }, [googleMapsKey, googleMapsMapId]);

  const handleVehicleSelect = (vehicleId) => {
    if (!isLocationSelectionMode) {
      primeReportLoading(activeReport);
      setSelectedVehicleId(vehicleId);
      setSelectedVehicleIds((previousIds) => {
        if (previousIds.length === 1 && previousIds[0] === vehicleId) return previousIds;
        return [vehicleId];
      });
      loadGoogleMapsConfig();
      return;
    }

    if (selectedVehicleIds.includes(vehicleId)) {
      const nextIds = selectedVehicleIds.filter((id) => id !== vehicleId);
      setSelectedVehicleIds(nextIds);
      if (selectedVehicleId === vehicleId) {
        setSelectedVehicleId(nextIds[0] || '');
      }
      return;
    }

    setSelectedVehicleId(vehicleId);
    setSelectedVehicleIds([...selectedVehicleIds, vehicleId]);
    loadGoogleMapsConfig();
  };

  const handleSiteGroupToggle = useCallback((siteGroupId) => {
    setCollapsedSiteGroupIds((previousIds) => (
      previousIds.includes(siteGroupId)
        ? previousIds.filter((currentSiteGroupId) => currentSiteGroupId !== siteGroupId)
        : [...previousIds, siteGroupId]
    ));
  }, []);

  const handleReportChange = useCallback((_, value) => {
    const storedReportFilter = reportDateFilters[value];
    const shouldRefreshLogDefault = value === 'logReport' && (!storedReportFilter || storedReportFilter.preset === 'lastHour');
    const nextReportFilter = shouldRefreshLogDefault
      ? {
        preset: 'lastHour',
        range: getDateTimeRangeForPreset('lastHour', effectiveFleetTimezone)
      }
      : storedReportFilter || createDateFilterStateForReport(value);
    primeReportLoading(value);
    if (nextReportFilter && REPORT_TABS.some((tab) => tab.value === value && tab.requiresDateFilter)) {
      setDateRangePreset(nextReportFilter.preset);
      setDateRange(nextReportFilter.range);
      if (shouldRefreshLogDefault) {
        setReportDateFilters((previousFilters) => ({
          ...previousFilters,
          [value]: nextReportFilter
        }));
      }
    }
    setActiveReport(value);
    if ((value === 'track' || value === 'trackFuel' || value === 'location') && selectedVehicleId) {
      loadGoogleMapsConfig();
    }
  }, [effectiveFleetTimezone, loadGoogleMapsConfig, primeReportLoading, reportDateFilters, selectedVehicleId]);

  const handleOpenReportTab = useCallback((reportValue) => {
    setOpenReportTabs((previousOpenTabs) => (
      previousOpenTabs.includes(reportValue) ? previousOpenTabs : [...previousOpenTabs, reportValue]
    ));
    handleReportChange(null, reportValue);
  }, [handleReportChange]);

  const handleCloseReportTab = useCallback((event, reportValue) => {
    event.stopPropagation();
    setOpenReportTabs((previousOpenTabs) => {
      if (previousOpenTabs.length <= 1) return previousOpenTabs;
      if (!previousOpenTabs.includes(reportValue)) return previousOpenTabs;

      const closedTabIndex = previousOpenTabs.indexOf(reportValue);
      const nextOpenTabs = previousOpenTabs.filter((value) => value !== reportValue);

      if (activeReport === reportValue) {
        const fallbackReport = nextOpenTabs[Math.min(closedTabIndex, nextOpenTabs.length - 1)] || nextOpenTabs[0];
        handleReportChange(null, fallbackReport);
      }

      return nextOpenTabs;
    });
  }, [activeReport, handleReportChange]);

  const handleDatePresetChange = (presetValue) => {
    primeReportLoading(activeReport);
    const nextRange = getDateTimeRangeForPreset(presetValue, effectiveFleetTimezone);
    setDateRangePreset(presetValue);
    setDateRange(nextRange);
    if (activeReport === 'dailyReadingSummary') {
      setDailyReadingSummaryRefreshToken((previousToken) => previousToken + 1);
    }
    setReportDateFilters((previousFilters) => ({
      ...previousFilters,
      [activeReport]: {
        preset: presetValue,
        range: nextRange
      }
    }));
  };

  const handleDateRangeChange = (field, value) => {
    primeReportLoading(activeReport);
    setDateRangePreset('custom');
    if (activeReport === 'dailyReadingSummary') {
      setDailyReadingSummaryRefreshToken((previousToken) => previousToken + 1);
    }
    setDateRange((previousRange) => {
      const nextRange = getNextDateRange(previousRange, field, value);
      setReportDateFilters((previousFilters) => ({
        ...previousFilters,
        [activeReport]: {
          preset: 'custom',
          range: nextRange
        }
      }));
      return nextRange;
    });
  };

  const handleRetryLocationMap = () => {
    if (mapScriptWatchdogRef.current) {
      clearTimeout(mapScriptWatchdogRef.current);
      mapScriptWatchdogRef.current = null;
    }
    setMapError('');
    setMapReady(false);
    setGoogleMapsKey('');
    setGoogleMapsMapId('');
    locationMapInstanceRef.current = null;
    const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
    existingScript?.remove();
    setMapReloadToken((previousToken) => previousToken + 1);
  };

  const clearLocationMapMarkers = () => {
    if (locationMapInfoWindowRef.current) {
      locationMapInfoWindowRef.current.close();
      locationMapInfoWindowRef.current = null;
    }
    locationMapMarkersRef.current.forEach((marker) => {
      if (typeof marker?.setMap === 'function') {
        marker.setMap(null);
      } else if (marker && 'map' in marker) {
        const currentMarker = marker;
        currentMarker.map = null;
      }
    });
    locationMapMarkersRef.current = [];
  };

  const clearTrackMapArtifacts = () => {
    if (trackMapInfoWindowRef.current) {
      trackMapInfoWindowRef.current.close();
      trackMapInfoWindowRef.current = null;
    }

    if (trackMapPolylineRef.current) {
      trackMapPolylineRef.current.setMap(null);
      trackMapPolylineRef.current = null;
    }

    trackMapMarkersRef.current.forEach((marker) => {
      if (typeof marker?.setMap === 'function') marker.setMap(null);
    });
    trackMapMarkersRef.current = [];
    trackMapCurrentMarkerRef.current = null;
  };

  const clearFuelEventMapArtifacts = () => {
    if (fuelEventMapInfoWindowRef.current) {
      fuelEventMapInfoWindowRef.current.close();
      fuelEventMapInfoWindowRef.current = null;
    }

    if (fuelEventMapMarkerRef.current && typeof fuelEventMapMarkerRef.current.setMap === 'function') {
      fuelEventMapMarkerRef.current.setMap(null);
    }
    fuelEventMapMarkerRef.current = null;
  };

  const getLocationMarkerIcon = () => {
    if (!isGoogleMapsApiReady()) return undefined;
    const markerSvg = buildVehicleMarkerIconSvg();
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(markerSvg)}`,
      scaledSize: new window.google.maps.Size(
        LOCATION_MARKER_ICON_DIMENSION.width,
        LOCATION_MARKER_ICON_DIMENSION.height
      ),
      anchor: new window.google.maps.Point(
        Math.floor(LOCATION_MARKER_ICON_DIMENSION.width / 2),
        LOCATION_MARKER_ICON_DIMENSION.height - 1
      )
    };
  };

  const getTrackCircleMarkerIcon = (
    fillColor,
    scale = 7,
    strokeColor = '#FFFFFF',
    strokeWeight = 2,
    fillOpacity = 1
  ) => {
    if (!isGoogleMapsApiReady()) return undefined;
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor,
      fillOpacity,
      strokeColor,
      strokeWeight,
      scale
    };
  };

  const getTrackCurrentMarkerIcon = () => getLocationMarkerIcon();

  const createLocationMarker = (mapInstance, markerData) => {
    const markerPosition = { lat: markerData.latitude, lng: markerData.longitude };
    return new window.google.maps.Marker({
      map: mapInstance,
      position: markerPosition,
      title: markerData.plate,
      icon: getLocationMarkerIcon(),
      optimized: true
    });
  };

  const attachMarkerClickHandler = (markerInstance, onClick) => {
    if (typeof markerInstance?.addListener === 'function') {
      markerInstance.addListener('click', onClick);
      return;
    }
    if (typeof markerInstance?.addEventListener === 'function') {
      markerInstance.addEventListener('click', onClick);
    }
  };

  useEffect(() => {
    if (!shouldEnableGoogleMaps) {
      clearLocationMapMarkers();
      clearTrackMapArtifacts();
      clearFuelEventMapArtifacts();
      setMapKeyLoading(false);
      setMapScriptLoading(false);
      setMapReady(false);
      setMapError('');
      setFuelEventMapError('');
      locationMapInstanceRef.current = null;
      fuelEventMapInstanceRef.current = null;
      trackMapInstanceRef.current = null;
      return;
    }

    loadGoogleMapsConfig(Boolean(mapReloadToken));
  }, [shouldEnableGoogleMaps, mapReloadToken, loadGoogleMapsConfig]);

  useEffect(() => {
    if (!shouldEnableGoogleMaps || !googleMapsKey) return undefined;
    let isMounted = true;
    let readinessInterval = null;
    const previousAuthFailureHandler = window.gm_authFailure;

    const handleScriptReady = () => {
      if (!isMounted || !isGoogleMapsApiReady()) return;
      const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
      existingScript?.setAttribute('data-loaded', 'true');
      if (readinessInterval) {
        clearInterval(readinessInterval);
        readinessInterval = null;
      }
      if (mapScriptWatchdogRef.current) {
        clearTimeout(mapScriptWatchdogRef.current);
        mapScriptWatchdogRef.current = null;
      }
      setMapScriptLoading(false);
      setMapError('');
    };

    const handleScriptError = (message = 'Unable to load map. Please retry.') => {
      if (!isMounted) return;
      if (readinessInterval) {
        clearInterval(readinessInterval);
        readinessInterval = null;
      }
      if (mapScriptWatchdogRef.current) {
        clearTimeout(mapScriptWatchdogRef.current);
        mapScriptWatchdogRef.current = null;
      }
      setMapScriptLoading(false);
      setMapReady(false);
      setMapError(message);
    };

    if (isGoogleMapsApiReady()) {
      handleScriptReady();
      return () => { isMounted = false; };
    }

    setMapScriptLoading(true);
    const callbackHandler = () => handleScriptReady();
    const scriptLoadHandler = () => handleScriptReady();
    window[GOOGLE_MAPS_INIT_CALLBACK] = callbackHandler;
    window.gm_authFailure = () => {
      handleScriptError('Google Maps API key is not authorized for this domain.');
      if (typeof previousAuthFailureHandler === 'function') previousAuthFailureHandler();
    };

    const expectedScriptSrc = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(googleMapsKey)}&v=weekly&libraries=marker&loading=async&callback=${GOOGLE_MAPS_INIT_CALLBACK}`;
    let scriptElement = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
    if (scriptElement) {
      const existingSrc = scriptElement.getAttribute('src') || '';
      if (existingSrc !== expectedScriptSrc && !isGoogleMapsApiReady()) {
        scriptElement.remove();
        scriptElement = null;
      }
    }

    if (!scriptElement) {
      scriptElement = document.createElement('script');
      scriptElement.id = GOOGLE_MAPS_SCRIPT_ID;
      scriptElement.src = expectedScriptSrc;
      scriptElement.async = true;
      scriptElement.defer = true;
      document.head.appendChild(scriptElement);
    } else if (scriptElement.getAttribute('data-loaded') === 'true' && isGoogleMapsApiReady()) {
      handleScriptReady();
      return () => {
        isMounted = false;
        if (window[GOOGLE_MAPS_INIT_CALLBACK] === callbackHandler) delete window[GOOGLE_MAPS_INIT_CALLBACK];
      };
    }

    readinessInterval = setInterval(() => {
      if (!isMounted) return;
      if (isGoogleMapsApiReady()) handleScriptReady();
    }, 150);

    mapScriptWatchdogRef.current = setTimeout(() => {
      if (!isMounted) return;
      if (!isGoogleMapsApiReady()) handleScriptError();
      else handleScriptReady();
    }, 15000);

    scriptElement.addEventListener('load', scriptLoadHandler);
    scriptElement.addEventListener('error', handleScriptError);

    return () => {
      isMounted = false;
      if (readinessInterval) {
        clearInterval(readinessInterval);
        readinessInterval = null;
      }
      if (mapScriptWatchdogRef.current) {
        clearTimeout(mapScriptWatchdogRef.current);
        mapScriptWatchdogRef.current = null;
      }
      scriptElement?.removeEventListener('load', scriptLoadHandler);
      scriptElement?.removeEventListener('error', handleScriptError);
      if (window[GOOGLE_MAPS_INIT_CALLBACK] === callbackHandler) delete window[GOOGLE_MAPS_INIT_CALLBACK];
      window.gm_authFailure = previousAuthFailureHandler;
    };
  }, [googleMapsKey, shouldEnableGoogleMaps]);

  useEffect(() => {
    if (!isLocationSelectionMode || isMapScriptLoading || !isGoogleMapsApiReady()) {
      return undefined;
    }

    const mapContainer = locationMapContainerRef.current;
    if (!mapContainer) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      try {
        if (!mapContainer.isConnected || locationMapContainerRef.current !== mapContainer) {
          return;
        }

        let mapInstance = locationMapInstanceRef.current;
        const shouldCreateMap = !mapInstance
          || mapInstance.getDiv() !== mapContainer
          || mapInstance.fleetManagementMapId !== resolvedGoogleMapsMapId;

        // FIX: Cost Optimization - Only execute new google map instances when actually required
        if (shouldCreateMap) {
          const initialCenter = locationMapMarkers[0]
            ? { lat: locationMapMarkers[0].latitude, lng: locationMapMarkers[0].longitude }
            : DEFAULT_LOCATION_MAP_CENTER;
          const mapOptions = { ...LOCATION_MAP_OPTIONS, center: initialCenter, zoom: 12 };
          if (resolvedGoogleMapsMapId) mapOptions.mapId = resolvedGoogleMapsMapId;

          mapInstance = new window.google.maps.Map(mapContainer, mapOptions);
          mapInstance.fleetManagementMapId = resolvedGoogleMapsMapId;
          locationMapInstanceRef.current = mapInstance;
        }

        clearLocationMapMarkers();

        // FIX: Cost Optimization - Do NOT drop Map instance when no markers. Just return out.
        if (!locationMapMarkers.length) {
          setMapReady(true);
          return;
        }

        locationMapMarkersRef.current = locationMapMarkers.map((marker) => {
          const markerInstance = createLocationMarker(mapInstance, marker);
          attachMarkerClickHandler(markerInstance, () => {
            mapInstance.setCenter({ lat: marker.latitude, lng: marker.longitude });
            mapInstance.setZoom(Math.max(mapInstance.getZoom() || 12, 16));
            if (!locationMapInfoWindowRef.current) locationMapInfoWindowRef.current = new window.google.maps.InfoWindow();
            locationMapInfoWindowRef.current.setContent(getMapMarkerInfoContent(marker));
            locationMapInfoWindowRef.current.open({ anchor: markerInstance, map: mapInstance, shouldFocus: false });
          });
          return markerInstance;
        });

        fitLocationMapToMarkers(mapInstance, locationMapMarkers);

        if (locationMapMarkers.length === 1) {
          const onlyMarker = locationMapMarkers[0];
          const markerInstance = locationMapMarkersRef.current[0];
          if (!locationMapInfoWindowRef.current) locationMapInfoWindowRef.current = new window.google.maps.InfoWindow();
          locationMapInfoWindowRef.current.setContent(getMapMarkerInfoContent(onlyMarker));
          locationMapInfoWindowRef.current.open({ anchor: markerInstance, map: mapInstance, shouldFocus: false });
        } else if (locationMapInfoWindowRef.current) {
          locationMapInfoWindowRef.current.close();
        }

        setMapError('');
        setMapReady(true);
      } catch (error) {
        console.log('location map render error', error?.message || error);
        setMapReady(false);
        setMapError('Unable to render map. Please retry.');
      }
    });

    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, [isLocationSelectionMode, isMapScriptLoading, locationMapMarkers, resolvedGoogleMapsMapId]);

  useEffect(() => {
    if (!isTrackVisualReportActive || isMapScriptLoading || !isGoogleMapsApiReady()) {
      return undefined;
    }

    const mapContainer = trackMapContainerRef.current;
    if (!mapContainer) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      try {
        if (!mapContainer.isConnected || trackMapContainerRef.current !== mapContainer) {
          return;
        }

        const routePoints = activeTrackData?.points || [];
        const { currentPoint } = trackSummary;
        const [firstRoutePoint] = routePoints;
        const analyticsLocationPoint = isAnalyticsReportActive
          ? [
            currentPoint,
            activeTrackData?.lastKnownPosition,
            routePoints[routePoints.length - 1],
            firstRoutePoint
          ].find((point) => Number.isFinite(point?.latitude) && Number.isFinite(point?.longitude))
          : null;
        const displayPoint = isAnalyticsReportActive
          ? analyticsLocationPoint
          : (currentPoint || firstRoutePoint);
        const initialCenterPoint = displayPoint || firstRoutePoint;

        let mapInstance = trackMapInstanceRef.current;
        const shouldCreateMap = !mapInstance
          || mapInstance.getDiv() !== mapContainer
          || mapInstance.fleetManagementMapId !== resolvedGoogleMapsMapId;

        if (shouldCreateMap) {
          const mapOptions = {
            ...LOCATION_MAP_OPTIONS,
            center: initialCenterPoint
              ? { lat: initialCenterPoint.latitude, lng: initialCenterPoint.longitude }
              : DEFAULT_LOCATION_MAP_CENTER,
            zoom: 12
          };
          if (resolvedGoogleMapsMapId) mapOptions.mapId = resolvedGoogleMapsMapId;

          mapInstance = new window.google.maps.Map(mapContainer, mapOptions);
          mapInstance.fleetManagementMapId = resolvedGoogleMapsMapId;
          trackMapInstanceRef.current = mapInstance;
          window.google.maps.event.trigger(mapInstance, 'resize');
        }

        clearTrackMapArtifacts();
        window.google.maps.event.trigger(mapInstance, 'resize');

        const fitPoints = [];

        if (routePoints.length > 1) {
          const polylineOptions = {
            path: routePoints.map((point) => ({ lat: point.latitude, lng: point.longitude })),
            geodesic: true,
            strokeColor: '#2563EB',
            strokeOpacity: 0.92,
            strokeWeight: 4
          };

          if (window.google.maps.SymbolPath?.FORWARD_CLOSED_ARROW && routePoints.length > 2) {
            polylineOptions.icons = [{
              icon: {
                path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                fillColor: '#1D4ED8',
                fillOpacity: 1,
                strokeColor: '#DBEAFE',
                strokeWeight: 1,
                scale: 2.8
              },
              offset: '0',
              repeat: '48px'
            }];
          }

          trackMapPolylineRef.current = new window.google.maps.Polyline(polylineOptions);
          trackMapPolylineRef.current.setMap(mapInstance);
          fitPoints.push(...routePoints);
        }

        const createTrackMarker = ({
          point,
          title,
          icon,
          label,
          zIndex = 1,
          pointIndex = null,
          markerType = 'point'
        }) => {
          if (!point || !Number.isFinite(point.latitude) || !Number.isFinite(point.longitude)) return null;
          const markerInstance = new window.google.maps.Marker({
            map: mapInstance,
            position: { lat: point.latitude, lng: point.longitude },
            title,
            icon,
            label,
            zIndex,
            optimized: true
          });
          attachMarkerClickHandler(markerInstance, () => {
            setSelectedTrackPoint({
              point,
              pointIndex,
              title,
              markerType
            });
            if (!isTrackVisualReportActive) {
              if (!trackMapInfoWindowRef.current) trackMapInfoWindowRef.current = new window.google.maps.InfoWindow();
              trackMapInfoWindowRef.current.setContent(getTrackMarkerInfoContent({
                title,
                plate: selectedVehicle?.plate || 'Vehicle',
                speed: point.speed,
                fuelVolume: point.fuelVolume,
                satellitesCount: point.satellitesCount,
                timestamp: point.timestamp || point.eventDate || point.rawTimestamp,
                direction: point.direction,
                latitude: point.latitude,
                longitude: point.longitude,
                ignition: point.ignition,
                isStopped: point.isStopped,
                gpsValid: point.gpsValid,
                events: point.events || [],
                isLoading: !isTrackFuelReportActive
              }));
              trackMapInfoWindowRef.current.open({ anchor: markerInstance, map: mapInstance, shouldFocus: false });
            }
          });
          trackMapMarkersRef.current.push(markerInstance);
          fitPoints.push(point);
          return markerInstance;
        };

        const startPoint = routePoints[0];
        const endPoint = routePoints[routePoints.length - 1];
        const shouldShowSeparateEndMarker = Boolean(
          endPoint
          && (
            !currentPoint
            || currentPoint.latitude !== endPoint.latitude
            || currentPoint.longitude !== endPoint.longitude
          )
        );

        if (!isAnalyticsReportActive) {
          createTrackMarker({
            point: startPoint,
            title: 'Trip start',
            icon: getTrackCircleMarkerIcon('#16A34A'),
            pointIndex: 0,
            markerType: 'start'
          });

          sampledTrackMarkers.forEach(({ point, pointIndex }) => {
            createTrackMarker({
              point,
              pointIndex,
              markerType: 'route',
              title: `Track point ${pointIndex + 1}`,
              icon: getTrackCircleMarkerIcon('#0F172A', 6, '#E2E8F0', 2, 0.9),
              zIndex: 2
            });
          });

          trackIgnitionGroups.forEach((group) => {
            const markerInstance = new window.google.maps.Marker({
              map: mapInstance,
              position: { lat: group.latitude, lng: group.longitude },
              title: group.title,
              icon: getTrackCircleMarkerIcon('#F97316', 13, '#FFFFFF', 2.5, 0.96),
              label: {
                text: `${group.eventCount}`,
                color: '#FFFFFF',
                fontWeight: '700',
                fontSize: '11px'
              },
              zIndex: 7,
              optimized: true
            });
            attachMarkerClickHandler(markerInstance, () => {
              setSelectedTrackEventGroup(group);
            });
            trackMapMarkersRef.current.push(markerInstance);
            fitPoints.push(group);
          });

          if (shouldShowSeparateEndMarker) {
            createTrackMarker({
              point: endPoint,
              title: 'Trip end',
              icon: getTrackCircleMarkerIcon('#DC2626'),
              pointIndex: routePoints.length - 1,
              markerType: 'end'
            });
          }
        }

        let currentMarkerPointIndex = null;
        if (!isAnalyticsReportActive && routePoints.length) currentMarkerPointIndex = routePoints.length - 1;

        if (
          isAnalyticsReportActive
          && displayPoint
          && Number.isFinite(displayPoint.latitude)
          && Number.isFinite(displayPoint.longitude)
        ) {
          const currentHighlightMarker = new window.google.maps.Marker({
            map: mapInstance,
            position: { lat: displayPoint.latitude, lng: displayPoint.longitude },
            title: 'Current location highlight',
            icon: getTrackCircleMarkerIcon('#F97316', 18, '#FFFFFF', 4, 0.36),
            zIndex: 9,
            optimized: true
          });
          trackMapMarkersRef.current.push(currentHighlightMarker);
        }

        const currentMarker = createTrackMarker({
          point: displayPoint || currentPoint,
          title: isAnalyticsReportActive ? 'Current location' : 'Playback position',
          icon: getTrackCurrentMarkerIcon(),
          zIndex: 10,
          pointIndex: currentMarkerPointIndex,
          markerType: 'current'
        });
        trackMapCurrentMarkerRef.current = currentMarker;

        if (fitPoints.length) {
          fitMapToCoordinatePoints(mapInstance, fitPoints);
        } else {
          mapInstance.setCenter(DEFAULT_LOCATION_MAP_CENTER);
          mapInstance.setZoom(11);
        }

        if (currentMarker) {
          if (!trackMapInfoWindowRef.current) trackMapInfoWindowRef.current = new window.google.maps.InfoWindow();
          trackMapInfoWindowRef.current.setContent(getTrackMarkerInfoContent({
            title: isAnalyticsReportActive ? 'Current location' : 'Playback position',
            plate: selectedVehicle?.plate || 'Vehicle',
            speed: displayPoint?.speed,
            fuelVolume: displayPoint?.fuelVolume,
            satellitesCount: displayPoint?.satellitesCount,
            timestamp: displayPoint?.timestamp || displayPoint?.eventDate || displayPoint?.rawTimestamp,
            direction: displayPoint?.direction,
            latitude: displayPoint?.latitude,
            longitude: displayPoint?.longitude,
            ignition: displayPoint?.ignition,
            isStopped: displayPoint?.isStopped,
            gpsValid: displayPoint?.gpsValid ?? displayPoint?.gpsDataValid ?? null,
            events: displayPoint?.events || [],
            isLoading: !isTrackFuelReportActive
          }));
          if (isAnalyticsReportActive) {
            trackMapInfoWindowRef.current.open({ anchor: currentMarker, map: mapInstance, shouldFocus: false });
          }
        }

        setMapError('');
        setMapReady(true);
      } catch (error) {
        console.log('track map render error', error?.message || error);
        setMapReady(false);
        setMapError('Unable to render map. Please retry.');
      }
    });

    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, [activeTrackData, isAnalyticsReportActive, isMapScriptLoading, isTrackFuelReportActive, isTrackVisualReportActive, resolvedGoogleMapsMapId, sampledTrackMarkers, trackIgnitionGroups, trackSummary.currentPoint, selectedVehicle?.plate]);

  useEffect(() => {
    if (!isEventLocationMapActive || !selectedFuelEventMapRow || isMapScriptLoading || !isGoogleMapsApiReady()) {
      return undefined;
    }

    const shouldUseDialogMapContainer = ['events', 'logReport'].includes(selectedFuelEventMapRow?.sourceReport);
    const mapContainer = shouldUseDialogMapContainer
      ? eventsLocationMapContainerRef.current
      : fuelEventMapContainerRef.current;
    if (!mapContainer) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      try {
        const activeMapContainer = shouldUseDialogMapContainer
          ? eventsLocationMapContainerRef.current
          : fuelEventMapContainerRef.current;

        if (!mapContainer.isConnected || activeMapContainer !== mapContainer) {
          return;
        }

        const markerLatitude = Number(selectedFuelEventMapRow.latitude);
        const markerLongitude = Number(selectedFuelEventMapRow.longitude);
        if (!Number.isFinite(markerLatitude) || !Number.isFinite(markerLongitude)) {
          clearFuelEventMapArtifacts();
          setFuelEventMapError('Coordinates are unavailable for this event.');
          return;
        }

        let mapInstance = fuelEventMapInstanceRef.current;
        const shouldCreateMap = !mapInstance
          || mapInstance.getDiv() !== mapContainer
          || mapInstance.fleetManagementMapId !== resolvedGoogleMapsMapId;

        if (shouldCreateMap) {
          const mapOptions = {
            ...LOCATION_MAP_OPTIONS,
            center: { lat: markerLatitude, lng: markerLongitude },
            zoom: 15
          };
          if (resolvedGoogleMapsMapId) mapOptions.mapId = resolvedGoogleMapsMapId;

          mapInstance = new window.google.maps.Map(mapContainer, mapOptions);
          mapInstance.fleetManagementMapId = resolvedGoogleMapsMapId;
          fuelEventMapInstanceRef.current = mapInstance;
        }

        clearFuelEventMapArtifacts();

        let markerColor = '#2563EB';
        if (selectedFuelEventMapRow.type === 1) markerColor = '#16A34A';
        else if (selectedFuelEventMapRow.type === 3) markerColor = '#DC2626';
        else if (selectedFuelEventMapRow.eventTypeID === 35 || selectedFuelEventMapRow.eventTypeID === 36) markerColor = '#0891B2';

        const markerInstance = new window.google.maps.Marker({
          map: mapInstance,
          position: { lat: markerLatitude, lng: markerLongitude },
          title: selectedFuelEventMapRow.typeLabel || selectedFuelEventMapRow.eventTypeLabel || 'Event location',
          icon: getTrackCircleMarkerIcon(markerColor, 10, '#FFFFFF', 2.5, 0.95),
          optimized: true
        });
        fuelEventMapMarkerRef.current = markerInstance;

        if (!fuelEventMapInfoWindowRef.current) fuelEventMapInfoWindowRef.current = new window.google.maps.InfoWindow();
        fuelEventMapInfoWindowRef.current.setContent(getFleetEventMarkerInfoContent(selectedFuelEventMapRow));
        fuelEventMapInfoWindowRef.current.open({ anchor: markerInstance, map: mapInstance, shouldFocus: false });

        window.google.maps.event.trigger(mapInstance, 'resize');
        mapInstance.setCenter({ lat: markerLatitude, lng: markerLongitude });
        mapInstance.setZoom(Math.max(mapInstance.getZoom() || 15, 15));

        setFuelEventMapError('');
        setMapError('');
        setMapReady(true);
      } catch (error) {
        console.log('fuel event map render error', error?.message || error);
        setMapReady(false);
        setFuelEventMapError('Unable to render event map. Please retry.');
      }
    });

    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, [eventLocationDialogReadyToken, isEventLocationMapActive, isMapScriptLoading, resolvedGoogleMapsMapId, selectedFuelEventMapRow]);

  useEffect(() => {
    if (!isTrackVisualReportActive || !activeTrackData) {
      setSelectedTrackPoint(null);
      return;
    }

    if (isTrackVisualReportActive) {
      setSelectedTrackPoint(null);
      return;
    }

    const fallbackPoint = trackSummary.currentPoint || trackSummary.endPoint || trackSummary.startPoint;
    if (!fallbackPoint) {
      setSelectedTrackPoint(null);
      return;
    }

    setSelectedTrackPoint({
      point: fallbackPoint,
      pointIndex: activeTrackData?.points?.length ? activeTrackData.points.length - 1 : null,
      title: trackSummary.currentPoint ? 'Last known position' : 'Track point',
      markerType: trackSummary.currentPoint ? 'current' : 'route'
    });
  }, [activeTrackData, isTrackVisualReportActive, trackSummary.currentPoint, trackSummary.endPoint, trackSummary.startPoint]);

  useEffect(() => {
    if (isFuelReportActive || isEventsReportActive || isLogReportActive) return;
    setSelectedFuelEventMapRow(null);
    setFuelEventMapError('');
    clearFuelEventMapArtifacts();
  }, [isEventsReportActive, isFuelReportActive, isLogReportActive]);

  useEffect(() => {
    if (!selectedFuelEventMapRow?.sourceReport || selectedFuelEventMapRow.sourceReport === activeReport) return;
    setSelectedFuelEventMapRow(null);
    setFuelEventMapError('');
    clearFuelEventMapArtifacts();
  }, [activeReport, selectedFuelEventMapRow]);

  useEffect(() => {
    if (!isTrackVisualReportActive) {
      setSelectedTrackEventGroup(null);
      setTrackPlaybackIndex(0);
      setTrackPlaybackActive(false);
      return;
    }

    const totalTrackPoints = activeTrackData?.points?.length || 0;
    if (!totalTrackPoints) {
      setSelectedTrackEventGroup(null);
      setTrackPlaybackIndex(0);
      setTrackPlaybackActive(false);
      return;
    }

    setTrackPlaybackIndex(0);
    setTrackPlaybackActive(false);
    setSelectedTrackEventGroup((previousGroup) => {
      if (previousGroup) {
        const matchedGroup = trackIgnitionGroups.find((group) => group.id === previousGroup.id);
        if (matchedGroup) return matchedGroup;
      }
      return null;
    });
  }, [activeTrackData, isTrackVisualReportActive, trackIgnitionGroups]);

  useEffect(() => {
    if (!isTrackVisualReportActive || !activeTrackData?.points?.length || !isTrackPlaybackActive) {
      if (trackPlaybackTimerRef.current) {
        window.clearInterval(trackPlaybackTimerRef.current);
        trackPlaybackTimerRef.current = null;
      }
      return undefined;
    }

    trackPlaybackTimerRef.current = window.setInterval(() => {
      setTrackPlaybackIndex((previousIndex) => {
        const lastPointIndex = Math.max((activeTrackData?.points?.length || 1) - 1, 0);
        if (previousIndex >= lastPointIndex) {
          setTrackPlaybackActive(false);
          return lastPointIndex;
        }
        return previousIndex + 1;
      });
    }, trackPlaybackIntervalMs);

    return () => {
      if (trackPlaybackTimerRef.current) {
        window.clearInterval(trackPlaybackTimerRef.current);
        trackPlaybackTimerRef.current = null;
      }
    };
  }, [activeTrackData, isTrackPlaybackActive, isTrackVisualReportActive, trackPlaybackIntervalMs]);

  useEffect(() => {
    if (!isTrackVisualReportActive || !trackPlaybackPoint || !isGoogleMapsApiReady()) return;

    const currentMarker = trackMapCurrentMarkerRef.current;
    if (currentMarker && typeof currentMarker.setPosition === 'function') {
      currentMarker.setPosition({
        lat: trackPlaybackPoint.latitude,
        lng: trackPlaybackPoint.longitude
      });
      currentMarker.setTitle(isTrackPlaybackActive ? 'Playback position' : 'Route position');
    }

    if (isTrackPlaybackActive && trackMapInstanceRef.current) {
      trackMapInstanceRef.current.panTo({
        lat: trackPlaybackPoint.latitude,
        lng: trackPlaybackPoint.longitude
      });
    }
  }, [isTrackPlaybackActive, isTrackVisualReportActive, trackPlaybackPoint]);

  useEffect(() => {
    const detailsPoint = selectedTrackPoint?.point || null;

    if (!isTrackVisualReportActive || !selectedVehicle?.plate || !detailsPoint) {
      setSelectedTrackPointDetails(null);
      setSelectedTrackPointDetailsError('');
      setSelectedTrackPointDetailsLoading(false);
      return undefined;
    }

    const eventDate = detailsPoint.timestamp
      ?? getNormalizedTimestampValue(detailsPoint.rawTimestamp);

    if (!Number.isFinite(eventDate)) {
      setSelectedTrackPointDetails(null);
      setSelectedTrackPointDetailsError('Point details are unavailable for this marker.');
      setSelectedTrackPointDetailsLoading(false);
      return undefined;
    }

    let isMounted = true;
    const payload = {
      registration_num: selectedVehicle.plate,
      eventDate
    };

    const fetchTrackPointDetails = async () => {
      setSelectedTrackPointDetailsLoading(true);
      setSelectedTrackPointDetailsError('');
      try {
        const response = await APIService.getFleetLocationMapCard(payload);
        if (!isMounted) return;

        const normalizedDetails = normalizeLocationMapCardData(response);
        if (normalizedDetails) {
          setSelectedTrackPointDetails(normalizedDetails);
        } else {
          setSelectedTrackPointDetails(null);
          setSelectedTrackPointDetailsError('No point details were returned for this timestamp.');
        }
      } catch (error) {
        if (!isMounted) return;
        console.log('fleet track point details error', error?.message || error);
        setSelectedTrackPointDetails(null);
        setSelectedTrackPointDetailsError('Unable to load point details right now. Please try another marker.');
      } finally {
        if (isMounted) setSelectedTrackPointDetailsLoading(false);
      }
    };

    fetchTrackPointDetails();
    return () => { isMounted = false; };
  }, [isTrackVisualReportActive, selectedTrackPoint, selectedVehicle?.plate]);

  const fetchCurrentState = useCallback(async (showInitialLoading = false) => {
    if (showInitialLoading) setFleetLoading(true);
    else setRefreshingFleet(true);

    try {
      const response = await APIService.getFleetCurrentState();
      const currentStateRows = response?.result?.data;
      const parsedVehicles = buildCurrentStateVehicles(currentStateRows);
      setVehicles(parsedVehicles);
      setLastFleetRefreshAt(Date.now());
    } catch (error) {
      console.log('fleet current state error', error?.message || error);
    } finally {
      if (showInitialLoading) setFleetLoading(false);
      else setRefreshingFleet(false);
    }
  }, []);

  const handleRefreshActiveReport = useCallback(() => {
    fetchCurrentState(false);
    if (!selectedVehicle?.plate && activeReport !== 'location') return;

    primeReportLoading(activeReport);
    if (activeReport === 'analytics') {
      setAnalyticsRefreshToken((previousToken) => previousToken + 1);
      return;
    }
    if (activeReport === 'dailyReadingSummary') {
      setDailyReadingSummaryRefreshToken((previousToken) => previousToken + 1);
      return;
    }
    setReportRefreshToken((previousToken) => previousToken + 1);
  }, [activeReport, fetchCurrentState, primeReportLoading, selectedVehicle?.plate]);

  useEffect(() => {
    fetchCurrentState(true);
  }, [fetchCurrentState]);

  useEffect(() => {
    const refreshStatusTimer = setInterval(() => setRefreshStatusTick(Date.now()), 15000);
    return () => clearInterval(refreshStatusTimer);
  }, []);

  useEffect(() => {
    if (!initialFleetContext.vehicle || selectedVehicleId || !vehicles.length) return;
    const matchedVehicle = vehicles.find((vehicle) => normalizeRegistrationNumber(vehicle?.plate) === initialFleetContext.vehicle);
    if (!matchedVehicle?.id) return;
    setSelectedVehicleId(matchedVehicle.id);
    setSelectedVehicleIds([matchedVehicle.id]);
  }, [initialFleetContext.vehicle, selectedVehicleId, vehicles]);

  useEffect(() => {
    const context = {
      vehicle: normalizeRegistrationNumber(selectedVehicle?.plate) || initialFleetContext.vehicle,
      report: activeReport,
      preset: dateRangePreset,
      fromDateTime: dateRange.fromDateTime,
      toDateTime: dateRange.toDateTime
    };

    try {
      window.sessionStorage.setItem(FLEET_MANAGEMENT_CONTEXT_STORAGE_KEY, JSON.stringify(context));
    } catch (error) {
      // Session persistence is best effort only.
    }

    const params = new URLSearchParams(location.search || '');
    const setOrDeleteParam = (key, value) => {
      if (value) params.set(key, value);
      else params.delete(key);
    };
    setOrDeleteParam(FLEET_CONTEXT_QUERY_KEYS.vehicle, context.vehicle);
    setOrDeleteParam(FLEET_CONTEXT_QUERY_KEYS.report, context.report);
    setOrDeleteParam(FLEET_CONTEXT_QUERY_KEYS.preset, context.preset);
    setOrDeleteParam(FLEET_CONTEXT_QUERY_KEYS.from, context.fromDateTime);
    setOrDeleteParam(FLEET_CONTEXT_QUERY_KEYS.to, context.toDateTime);

    const nextSearch = params.toString() ? `?${params.toString()}` : '';
    if (nextSearch !== location.search) {
      history.replace({
        pathname: location.pathname,
        search: nextSearch,
        hash: location.hash,
        state: location.state
      });
    }
  }, [
    activeReport,
    dateRange.fromDateTime,
    dateRange.toDateTime,
    dateRangePreset,
    history,
    location.hash,
    location.pathname,
    location.search,
    location.state,
    selectedVehicle?.plate
  ]);

  useEffect(() => {
    setSelectedVehicleIds((previousIds) => previousIds.filter((id) => vehicles.some((vehicle) => vehicle.id === id)));
  }, [vehicles]);

  useEffect(() => {
    if (isLocationSelectionMode) return;
    if (!selectedVehicleId) {
      setSelectedVehicleIds((previousIds) => (previousIds.length ? [] : previousIds));
      return;
    }
    setSelectedVehicleIds((previousIds) => (
      previousIds.length === 1 && previousIds[0] === selectedVehicleId ? previousIds : [selectedVehicleId]
    ));
  }, [isLocationSelectionMode, selectedVehicleId]);

  useEffect(() => {
    if (!selectedVehicle) return;
    const selectedSiteGroupId = getVehicleSiteGroupId(selectedVehicle);
    setCollapsedSiteGroupIds((previousIds) => (
      previousIds.includes(selectedSiteGroupId)
        ? previousIds.filter((siteGroupId) => siteGroupId !== selectedSiteGroupId)
        : previousIds
    ));
  }, [selectedVehicle]);

  useEffect(() => {
    if (selectedVehicleId && !vehicles.some((vehicle) => vehicle.id === selectedVehicleId)) setSelectedVehicleId('');
  }, [vehicles, selectedVehicleId]);

  useEffect(() => {
    setCurrentRunsPage(1);
    setEventsPage(1);
    setGroupWorkPage(1);
    setLogReportPage(1);
  }, [selectedVehicle?.plate]);

  useEffect(() => {
    if (activeReport !== 'events') return;
    setEventsPage(1);
  }, [activeReport, unixDateRange.from, unixDateRange.to]);

  useEffect(() => {
    if (activeReport !== 'groupWork') return;
    setGroupWorkPage(1);
  }, [activeReport, unixDateRange.from, unixDateRange.to]);

  useEffect(() => {
    if (activeReport !== 'logReport') return;
    setLogReportPage(1);
  }, [activeReport, unixDateRange.from, unixDateRange.to]);

  useEffect(() => {
    if (!selectedVehicleId) return;
    loadGoogleMapsConfig();
  }, [selectedVehicleId, loadGoogleMapsConfig]);

  useEffect(() => {
    if (isLocationSelectionMode) {
      setVehicleLoading(false);
      return undefined;
    }
    if (!selectedVehicleId) {
      setVehicleLoading(false);
      return undefined;
    }
    setVehicleLoading(true);
    const timer = setTimeout(() => { setVehicleLoading(false); }, 650);
    return () => clearTimeout(timer);
  }, [isLocationSelectionMode, selectedVehicleId]);

  useEffect(() => {
    if (!isLocationSelectionMode) {
      clearLocationMapMarkers();
      locationMapInstanceRef.current = null;
    }
  }, [isLocationSelectionMode]);

  useEffect(() => {
    if (!isTrackVisualReportActive) {
      clearTrackMapArtifacts();
      trackMapInstanceRef.current = null;
    }
  }, [isTrackVisualReportActive]);

  useEffect(() => {
    setOpenReportTabs((previousOpenTabs) => (
      previousOpenTabs.includes(activeReport) ? previousOpenTabs : [...previousOpenTabs, activeReport]
    ));
  }, [activeReport]);

  useEffect(() => {
    if (!activeReportConfig?.requiresDateFilter) return;

    const activeReportFilter = reportDateFilters[activeReport] || createDateFilterStateForReport(activeReport);

    setDateRangePreset((previousPreset) => (
      previousPreset === activeReportFilter.preset ? previousPreset : activeReportFilter.preset
    ));
    setDateRange((previousRange) => (
      isSameDateRange(previousRange, activeReportFilter.range) ? previousRange : activeReportFilter.range
    ));
  }, [activeReport, activeReportConfig?.requiresDateFilter, reportDateFilters]);

  useEffect(() => {
    if (activeReport !== 'analytics') return () => { };
    if (!selectedVehicle?.plate) {
      setAnalyticsData(null);
      setAnalyticsError('');
      setAnalyticsLoading(false);
      setAnalyticsRefreshing(false);
      return () => { };
    }

    let isMounted = true;
    const analyticsRange = getAnalyticsDashboardDateRange(analyticsRangePreset, effectiveFleetTimezone);
    const analyticsUnixRange = {
      from: toUnixSeconds(analyticsRange.fromDateTime, effectiveFleetTimezone),
      to: toUnixSeconds(analyticsRange.toDateTime, effectiveFleetTimezone)
    };
    const analyticsFuelRange = {
      from: Number.isFinite(analyticsUnixRange.from) ? analyticsUnixRange.from * 1000 : null,
      to: Number.isFinite(analyticsUnixRange.to) ? analyticsUnixRange.to * 1000 : null
    };
    const cacheKey = getFleetReportCacheKey({
      registrationNumber: selectedVehicle.plate,
      from: analyticsUnixRange.from,
      to: analyticsUnixRange.to,
      suffix: `analyticsDashboard|range:${analyticsRangePreset}|tz:${effectiveFleetTimezone}`
    });
    const cachedResult = analyticsReportCacheRef.current.get(cacheKey);
    if (cachedResult && analyticsRefreshToken === 0) {
      setAnalyticsData(cachedResult.data);
      setAnalyticsError(cachedResult.error || '');
      setAnalyticsLoading(false);
      setAnalyticsRefreshing(false);
      return () => { };
    }
    if (!Number.isFinite(analyticsUnixRange.from) || !Number.isFinite(analyticsUnixRange.to)) {
      setAnalyticsData(null);
      setAnalyticsError('Choose a valid dashboard range.');
      setAnalyticsLoading(false);
      setAnalyticsRefreshing(false);
      return () => { };
    }

    const fetchAnalytics = async () => {
      const isBackgroundRefresh = Boolean(analyticsDataRef.current) && analyticsRefreshToken > 0;
      setAnalyticsLoading(!isBackgroundRefresh);
      setAnalyticsRefreshing(isBackgroundRefresh);
      setAnalyticsError('');
      try {
        loadGoogleMapsConfig();
        const commonUnixPayload = {
          registration_num: selectedVehicle.plate,
          timeBegin: analyticsUnixRange.from,
          timeEnd: analyticsUnixRange.to,
          timezone: effectiveFleetTimezone,
          tz: effectiveFleetTimezone
        };
        const commonFuelPayload = {
          registration_num: selectedVehicle.plate,
          timeBegin: analyticsFuelRange.from,
          timeEnd: analyticsFuelRange.to,
          timezone: effectiveFleetTimezone,
          tz: effectiveFleetTimezone,
          locale: DEFAULT_FUEL_REPORT_LOCALE
        };
        const [statisticsResult, trackResult, eventsResult, fuelResult, tripResult, dailyReadingSummaryResult] = await Promise.allSettled([
          APIService.getFleetStatistics(commonUnixPayload),
          APIService.getFleetTrack(commonUnixPayload),
          APIService.getFleetEvents({
            ...commonUnixPayload,
            locale: DEFAULT_FUEL_REPORT_LOCALE,
            rows: 12,
            page: 1,
            sidx: 'eventDate',
            sord: 'desc',
            showLastPage: false,
            ...(selectedVehicleProfile ? { profileSecondaryEngine: selectedHasSecondaryEngine } : {})
          }),
          APIService.getFleetFuelDraining({
            ...commonFuelPayload,
            rows: FUEL_EVENTS_PAGE_SIZE,
            page: 1,
            sidx: 'startDate',
            sord: 'desc'
          }),
          APIService.getFleetTripReport({
            ...commonFuelPayload,
            rows: TRIP_REPORT_PAGE_SIZE,
            page: 1,
            sidx: 'routeName',
            sord: 'asc'
          }),
          selectedBackendVehicleId
            ? APIService.getFleetDailyReadingSummary({
              vehicle_id: selectedBackendVehicleId,
              start_time__gte: analyticsRange.fromDateTime.slice(0, 10),
              start_time__lte: analyticsRange.toDateTime.slice(0, 10),
              timezone: effectiveFleetTimezone,
              tz: effectiveFleetTimezone
            })
            : Promise.resolve(null)
        ]);
        if (!isMounted) return;

        const errors = {};
        const statistics = statisticsResult.status === 'fulfilled' ? normalizeFleetStatistics(statisticsResult.value) : null;
        if (statisticsResult.status !== 'fulfilled') errors.statistics = 'Statistics failed to update.';

        const normalizedTrack = trackResult.status === 'fulfilled' ? normalizeTrackReport(trackResult.value) : null;
        const normalizedLastKnown = trackResult.status === 'fulfilled'
          ? normalizeTrackLastKnownPosition(trackResult.value) || normalizedTrack?.points?.[normalizedTrack.points.length - 1] || null
          : null;
        if (trackResult.status !== 'fulfilled') errors.track = 'Track failed to update.';

        const events = eventsResult.status === 'fulfilled'
          ? normalizeFleetEvents(eventsResult.value, {
            hasSecondaryEngine: selectedHasSecondaryEngine
          })
          : null;
        if (eventsResult.status !== 'fulfilled') errors.events = 'Events failed to update.';

        const fuelEvents = fuelResult.status === 'fulfilled' ? normalizeFuelEventsReport(fuelResult.value) : null;
        if (fuelResult.status !== 'fulfilled') errors.fuel = 'Fuel events failed to update.';

        const tripReport = tripResult.status === 'fulfilled' ? normalizeFleetTripReport(tripResult.value) : null;
        if (tripResult.status !== 'fulfilled') errors.tripReport = 'Trip report failed to update.';

        const dailyReadingSummary = dailyReadingSummaryResult.status === 'fulfilled' && dailyReadingSummaryResult.value
          ? normalizeDailyReadingSummary(dailyReadingSummaryResult.value)
          : null;
        if (dailyReadingSummaryResult.status !== 'fulfilled') errors.dailyReadingSummary = 'Summary failed to update.';

        const normalizedAnalytics = {
          mode: 'dashboard',
          vehicle: {
            registrationNumber: selectedVehicle.plate,
            name: selectedVehicle.name
          },
          range: {
            preset: analyticsRangePreset,
            label: getAnalyticsDashboardRangeLabel(analyticsRangePreset),
            fromDateTime: analyticsRange.fromDateTime,
            toDateTime: analyticsRange.toDateTime
          },
          statistics: statistics?.hasData ? {
            vehicleData: statistics.vehicleData,
            totals: statistics.totals,
            periodMileage: statistics.periodMileage,
            vehicleType: statistics.vehicleType,
            derivedStartReading: statistics.derivedStartReading,
            derivedEndReading: statistics.derivedEndReading,
            derivedReadingUnit: statistics.derivedReadingUnit
          } : null,
          track: normalizedTrack && (normalizedTrack.hasData || normalizedLastKnown) ? {
            ...normalizedTrack,
            lastKnownPosition: normalizedLastKnown
          } : null,
          events,
          fuelEvents,
          tripReport,
          dailyReadingSummary,
          errors,
          refreshedAt: Date.now()
        };
        analyticsReportCacheRef.current.set(cacheKey, { data: normalizedAnalytics, error: '' });
        setAnalyticsData(normalizedAnalytics);
        markReportLoaded('analytics');
        setAnalyticsError('');
      } catch (error) {
        if (isMounted) {
          console.log('fleet analytics error', error?.message || error);
          if (!analyticsDataRef.current) setAnalyticsData(null);
          setAnalyticsError('Unable to load dashboard right now. Please try again.');
        }
      } finally {
        if (isMounted) {
          setAnalyticsLoading(false);
          setAnalyticsRefreshing(false);
        }
      }
    };

    fetchAnalytics();
    return () => { isMounted = false; };
  }, [activeReport, analyticsRangePreset, analyticsRefreshToken, effectiveFleetTimezone, loadGoogleMapsConfig, markReportLoaded, selectedBackendVehicleId, selectedHasSecondaryEngine, selectedVehicle?.name, selectedVehicle?.plate]);

  useEffect(() => {
    if (activeReport !== 'analytics' || !selectedVehicle?.plate) return () => { };
    const refreshTimer = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        setAnalyticsRefreshToken((previousToken) => previousToken + 1);
      }
    }, ANALYTICS_AUTO_REFRESH_INTERVAL_MS);

    return () => {
      window.clearInterval(refreshTimer);
    };
  }, [activeReport, analyticsRangePreset, selectedVehicle?.plate]);

  useEffect(() => {
    if (activeReport !== 'statistics') return () => { };
    if (!selectedVehicle?.plate) {
      setStatisticsData(null);
      setStatisticsError('');
      setStatisticsLoading(false);
      return () => { };
    }
    if (!isFleetDateRangeValid) {
      setStatisticsData(null);
      setStatisticsError('Choose a valid date range to load statistics.');
      setStatisticsLoading(false);
      return () => { };
    }

    let isMounted = true;
    const cacheKey = getFleetReportCacheKey({
      registrationNumber: selectedVehicle.plate,
      from: unixDateRange.from,
      to: unixDateRange.to,
      suffix: `statistics|tz:${effectiveFleetTimezone}|refresh:${reportRefreshToken}`
    });
    const cachedResult = statisticsReportCacheRef.current.get(cacheKey);
    if (cachedResult) {
      setStatisticsData(cachedResult.data);
      setStatisticsError(cachedResult.error || '');
      markReportLoaded('statistics');
      setStatisticsLoading(false);
      return () => { };
    }
    const payload = {
      registration_num: selectedVehicle.plate,
      timeBegin: unixDateRange.from,
      timeEnd: unixDateRange.to,
      timezone: effectiveFleetTimezone,
      tz: effectiveFleetTimezone
    };

    const fetchStatistics = async () => {
      setStatisticsLoading(true);
      setStatisticsError('');
      try {
        const response = await APIService.getFleetStatistics(payload);
        const normalizedStatistics = normalizeFleetStatistics(response);
        if (isMounted) {
          if (normalizedStatistics.hasData) {
            const reportData = {
              vehicleData: normalizedStatistics.vehicleData,
              totals: normalizedStatistics.totals,
              periodMileage: normalizedStatistics.periodMileage,
              vehicleType: normalizedStatistics.vehicleType,
              derivedStartReading: normalizedStatistics.derivedStartReading,
              derivedEndReading: normalizedStatistics.derivedEndReading,
              derivedReadingUnit: normalizedStatistics.derivedReadingUnit
            };
            const cacheValue = { data: reportData, error: '' };
            statisticsReportCacheRef.current.set(cacheKey, cacheValue);
            setStatisticsData(reportData);
            markReportLoaded('statistics');
            setStatisticsError('');
          } else {
            const noDataMessage = 'No statistics found for the selected range.';
            const cacheValue = { data: null, error: noDataMessage };
            statisticsReportCacheRef.current.set(cacheKey, cacheValue);
            setStatisticsData(null);
            markReportLoaded('statistics');
            setStatisticsError(noDataMessage);
          }
        }
      } catch (error) {
        if (isMounted) {
          console.log('fleet statistics error', error?.message || error);
          setStatisticsData(null);
          setStatisticsError('Unable to load statistics right now. Please try again.');
        }
      } finally {
        if (isMounted) setStatisticsLoading(false);
      }
    };

    fetchStatistics();
    return () => { isMounted = false; };
  }, [activeReport, effectiveFleetTimezone, isFleetDateRangeValid, markReportLoaded, reportRefreshToken, selectedVehicle?.plate, unixDateRange.from, unixDateRange.to]);

  useEffect(() => {
    if (activeReport !== 'fuel') return () => { };
    if (!selectedVehicle?.plate) {
      setFuelEventsData(null);
      setFuelEventsError('');
      setFuelEventsLoading(false);
      return () => { };
    }
    if (!isFleetDateRangeValid) {
      setFuelEventsData(null);
      setFuelEventsError('Choose a valid date range to load fuel events.');
      setFuelEventsLoading(false);
      return () => { };
    }

    let isMounted = true;
    const cacheKey = getFleetReportCacheKey({
      registrationNumber: selectedVehicle.plate,
      from: fuelDateRange.from,
      to: fuelDateRange.to,
      suffix: `fuel|tz:${effectiveFleetTimezone}|refresh:${reportRefreshToken}`
    });
    const cachedResult = fuelReportCacheRef.current.get(cacheKey);
    if (cachedResult) {
      setFuelEventsData(cachedResult.data);
      setFuelEventsError(cachedResult.error || '');
      markReportLoaded('fuel');
      setFuelEventsLoading(false);
      return () => { };
    }
    const payload = {
      registration_num: selectedVehicle.plate,
      timeBegin: fuelDateRange.from,
      timeEnd: fuelDateRange.to,
      timezone: effectiveFleetTimezone,
      tz: effectiveFleetTimezone,
      locale: DEFAULT_FUEL_REPORT_LOCALE,
      rows: FUEL_EVENTS_PAGE_SIZE,
      page: 1,
      sidx: 'startDate',
      sord: 'asc'
    };

    const fetchFuelEvents = async () => {
      setFuelEventsLoading(true);
      setFuelEventsError('');
      try {
        const response = await APIService.getFleetFuelDraining(payload);
        if (!isMounted) return;

        const normalizedFuelEvents = normalizeFuelEventsReport(response);
        if (normalizedFuelEvents.rows.length) {
          fuelReportCacheRef.current.set(cacheKey, { data: normalizedFuelEvents, error: '' });
          setFuelEventsData(normalizedFuelEvents);
          markReportLoaded('fuel');
          setFuelEventsError('');
        } else {
          const noDataMessage = 'No filling or draining events were returned for the selected range.';
          fuelReportCacheRef.current.set(cacheKey, { data: null, error: noDataMessage });
          setFuelEventsData(null);
          markReportLoaded('fuel');
          setFuelEventsError(noDataMessage);
        }
      } catch (error) {
        if (isMounted) {
          console.log('fleet fuel events error', error?.message || error);
          setFuelEventsData(null);
          setFuelEventsError('Unable to load fuel events right now. Please try again.');
        }
      } finally {
        if (isMounted) setFuelEventsLoading(false);
      }
    };

    fetchFuelEvents();
    return () => { isMounted = false; };
  }, [activeReport, effectiveFleetTimezone, fuelDateRange.from, fuelDateRange.to, isFleetDateRangeValid, markReportLoaded, reportRefreshToken, selectedVehicle?.plate]);

  useEffect(() => {
    if (activeReport !== 'tripReport') return () => { };
    if (!selectedVehicle?.plate) {
      setTripReportData(null);
      setTripReportError('');
      setTripReportLoading(false);
      return () => { };
    }
    if (!isFleetDateRangeValid) {
      setTripReportData(null);
      setTripReportError('Choose a valid date range to load trip report data.');
      setTripReportLoading(false);
      return () => { };
    }

    let isMounted = true;
    const cacheKey = getFleetReportCacheKey({
      registrationNumber: selectedVehicle.plate,
      from: fuelDateRange.from,
      to: fuelDateRange.to,
      suffix: `tripReport|tz:${effectiveFleetTimezone}|refresh:${reportRefreshToken}`
    });
    const cachedResult = tripReportCacheRef.current.get(cacheKey);
    if (cachedResult) {
      setTripReportData(cachedResult.data);
      setTripReportError(cachedResult.error || '');
      markReportLoaded('tripReport');
      setTripReportLoading(false);
      return () => { };
    }
    const payload = {
      registration_num: selectedVehicle.plate,
      timeBegin: fuelDateRange.from,
      timeEnd: fuelDateRange.to,
      timezone: effectiveFleetTimezone,
      tz: effectiveFleetTimezone,
      locale: DEFAULT_FUEL_REPORT_LOCALE,
      rows: TRIP_REPORT_PAGE_SIZE,
      page: 1,
      sidx: 'routeName',
      sord: 'asc'
    };

    const fetchTripReport = async () => {
      setTripReportLoading(true);
      setTripReportError('');
      try {
        const response = await APIService.getFleetTripReport(payload);
        if (!isMounted) return;

        const normalizedTripReport = normalizeFleetTripReport(response);
        if (normalizedTripReport.totalRuns > 0) {
          tripReportCacheRef.current.set(cacheKey, { data: normalizedTripReport, error: '' });
          setTripReportData(normalizedTripReport);
          markReportLoaded('tripReport');
          setTripReportError('');
        } else {
          const noDataMessage = 'No trip runs were returned for the selected range.';
          tripReportCacheRef.current.set(cacheKey, { data: null, error: noDataMessage });
          setTripReportData(null);
          markReportLoaded('tripReport');
          setTripReportError(noDataMessage);
        }
      } catch (error) {
        if (isMounted) {
          console.log('fleet trip report error', error?.message || error);
          setTripReportData(null);
          setTripReportError('Unable to load trip report right now. Please try again.');
        }
      } finally {
        if (isMounted) setTripReportLoading(false);
      }
    };

    fetchTripReport();
    return () => { isMounted = false; };
  }, [activeReport, effectiveFleetTimezone, fuelDateRange.from, fuelDateRange.to, isFleetDateRangeValid, markReportLoaded, reportRefreshToken, selectedVehicle?.plate]);

  useEffect(() => {
    if (activeReport !== 'currentRuns') return () => { };
    if (!selectedVehicle?.plate) {
      setCurrentRunsData(null);
      setCurrentRunsError('');
      setCurrentRunsLoading(false);
      return () => { };
    }

    let isMounted = true;
    const cacheKey = getFleetReportCacheKey({
      registrationNumber: selectedVehicle.plate,
      from: currentRunsPage,
      to: CURRENT_RUNS_PAGE_SIZE,
      suffix: `currentRuns|refresh:${reportRefreshToken}`
    });
    const cachedResult = currentRunsCacheRef.current.get(cacheKey);
    if (cachedResult) {
      setCurrentRunsData(cachedResult.data);
      setCurrentRunsError(cachedResult.error || '');
      markReportLoaded('currentRuns');
      setCurrentRunsLoading(false);
      return () => { };
    }

    const payload = {
      registration_num: selectedVehicle.plate,
      page: currentRunsPage,
      rows: CURRENT_RUNS_PAGE_SIZE
    };

    const fetchCurrentRuns = async () => {
      setCurrentRunsLoading(true);
      setCurrentRunsError('');
      try {
        const response = await APIService.getFleetCurrentRuns(payload);
        if (!isMounted) return;

        const normalizedCurrentRuns = normalizeFleetCurrentRuns(response);
        if (normalizedCurrentRuns.rows.length) {
          currentRunsCacheRef.current.set(cacheKey, { data: normalizedCurrentRuns, error: '' });
          setCurrentRunsData(normalizedCurrentRuns);
          markReportLoaded('currentRuns');
          setCurrentRunsError('');
        } else {
          const noDataMessage = 'No current runs were returned for this vehicle.';
          currentRunsCacheRef.current.set(cacheKey, { data: null, error: noDataMessage });
          setCurrentRunsData(null);
          markReportLoaded('currentRuns');
          setCurrentRunsError(noDataMessage);
        }
      } catch (error) {
        if (isMounted) {
          console.log('fleet current runs error', error?.message || error);
          setCurrentRunsData(null);
          setCurrentRunsError('Unable to load current runs right now. Please try again.');
        }
      } finally {
        if (isMounted) setCurrentRunsLoading(false);
      }
    };

    fetchCurrentRuns();
    return () => { isMounted = false; };
  }, [activeReport, currentRunsPage, markReportLoaded, reportRefreshToken, selectedVehicle?.plate]);

  useEffect(() => {
    if (activeReport !== 'events') return () => { };
    if (!selectedVehicle?.plate) {
      setEventsData(null);
      setEventsError('');
      setEventsLoading(false);
      return () => { };
    }
    if (!isFleetDateRangeValid) {
      setEventsData(null);
      setEventsError('Choose a valid date range to load events.');
      setEventsLoading(false);
      return () => { };
    }
    let isMounted = true;
    const cacheKey = getFleetReportCacheKey({
      registrationNumber: selectedVehicle.plate,
      from: fuelDateRange.from,
      to: fuelDateRange.to,
      suffix: `eventsProfileV2|page:${eventsPage}|rows:${EVENTS_PAGE_SIZE}|tz:${effectiveFleetTimezone}|secondary:${selectedHasSecondaryEngine}|refresh:${reportRefreshToken}`
    });
    const cachedResult = eventsCacheRef.current.get(cacheKey);
    if (cachedResult) {
      setEventsData(cachedResult.data);
      setEventsError(cachedResult.error || '');
      markReportLoaded('events');
      setEventsLoading(false);
      return () => { };
    }

    const payload = {
      registration_num: selectedVehicle.plate,
      timeBegin: unixDateRange.from,
      timeEnd: unixDateRange.to,
      timezone: effectiveFleetTimezone,
      tz: effectiveFleetTimezone,
      locale: DEFAULT_FUEL_REPORT_LOCALE,
      rows: EVENTS_PAGE_SIZE,
      page: eventsPage,
      sidx: 'eventDate',
      sord: 'asc',
      showLastPage: true,
      profileSecondaryEngine: selectedHasSecondaryEngine
    };

    const fetchEvents = async () => {
      setEventsLoading(true);
      setEventsError('');
      try {
        const response = await APIService.getFleetEvents(payload);
        if (!isMounted) return;

        const normalizedEvents = normalizeFleetEvents(response, {
          hasSecondaryEngine: selectedHasSecondaryEngine
        });
        if (normalizedEvents.rows.length) {
          eventsCacheRef.current.set(cacheKey, { data: normalizedEvents, error: '' });
          setEventsData(normalizedEvents);
          markReportLoaded('events');
          setEventsError('');
        } else {
          const noDataMessage = 'No events were returned for the selected range.';
          eventsCacheRef.current.set(cacheKey, { data: null, error: noDataMessage });
          setEventsData(null);
          markReportLoaded('events');
          setEventsError(noDataMessage);
        }
      } catch (error) {
        if (isMounted) {
          console.log('fleet events error', error?.message || error);
          setEventsData(null);
          setEventsError('Unable to load events right now. Please try again.');
        }
      } finally {
        if (isMounted) setEventsLoading(false);
      }
    };

    fetchEvents();
    return () => { isMounted = false; };
  }, [activeReport, effectiveFleetTimezone, eventsPage, fuelDateRange.from, fuelDateRange.to, isFleetDateRangeValid, markReportLoaded, reportRefreshToken, selectedHasSecondaryEngine, selectedVehicle?.plate, selectedVehicleProfile, unixDateRange.from, unixDateRange.to]);

  useEffect(() => {
    if (activeReport !== 'groupWork') return () => { };
    if (!selectedVehicle?.plate) {
      setGroupWorkData(null);
      setGroupWorkError('');
      setGroupWorkLoading(false);
      return () => { };
    }
    if (!isFleetDateRangeValid) {
      setGroupWorkData(null);
      setGroupWorkError('Choose a valid date range to load group work data.');
      setGroupWorkLoading(false);
      return () => { };
    }

    let isMounted = true;
    const cacheKey = getFleetReportCacheKey({
      registrationNumber: selectedVehicle.plate,
      from: fuelDateRange.from,
      to: fuelDateRange.to,
      suffix: `groupWork|page:${groupWorkPage}|rows:${GROUP_WORK_PAGE_SIZE}|tz:${effectiveFleetTimezone}|refresh:${reportRefreshToken}`
    });
    const cachedResult = groupWorkCacheRef.current.get(cacheKey);
    if (cachedResult) {
      setGroupWorkData(cachedResult.data);
      setGroupWorkError(cachedResult.error || '');
      markReportLoaded('groupWork');
      setGroupWorkLoading(false);
      return () => { };
    }

    const payload = {
      registration_num: selectedVehicle.plate,
      timeBegin: unixDateRange.from,
      timeEnd: unixDateRange.to,
      timezone: effectiveFleetTimezone,
      tz: effectiveFleetTimezone,
      locale: DEFAULT_FUEL_REPORT_LOCALE,
      rows: GROUP_WORK_PAGE_SIZE,
      page: groupWorkPage,
      sidx: 'date',
      sord: 'asc'
    };

    const fetchGroupWork = async () => {
      setGroupWorkLoading(true);
      setGroupWorkError('');
      try {
        const response = await APIService.getFleetGroupWork(payload);
        if (!isMounted) return;

        const normalizedGroupWork = normalizeFleetGroupWork(response);
        if (normalizedGroupWork.rows.length) {
          groupWorkCacheRef.current.set(cacheKey, { data: normalizedGroupWork, error: '' });
          setGroupWorkData(normalizedGroupWork);
          markReportLoaded('groupWork');
          setGroupWorkError('');
        } else {
          const noDataMessage = 'No group work rows were returned for the selected range.';
          groupWorkCacheRef.current.set(cacheKey, { data: null, error: noDataMessage });
          setGroupWorkData(null);
          markReportLoaded('groupWork');
          setGroupWorkError(noDataMessage);
        }
      } catch (error) {
        if (isMounted) {
          console.log('fleet group work error', error?.message || error);
          setGroupWorkData(null);
          setGroupWorkError('Unable to load group work right now. Please try again.');
        }
      } finally {
        if (isMounted) setGroupWorkLoading(false);
      }
    };

    fetchGroupWork();
    return () => { isMounted = false; };
  }, [activeReport, effectiveFleetTimezone, fuelDateRange.from, fuelDateRange.to, groupWorkPage, isFleetDateRangeValid, markReportLoaded, reportRefreshToken, selectedVehicle?.plate, unixDateRange.from, unixDateRange.to]);

  useEffect(() => {
    if (activeReport !== 'logReport') return () => { };
    if (!selectedVehicle?.plate) {
      setLogReportData(null);
      setLogReportError('');
      setLogReportLoading(false);
      return () => { };
    }
    if (!isFleetDateRangeValid) {
      setLogReportData(null);
      setLogReportError('Choose a valid date range to load log report data.');
      setLogReportLoading(false);
      return () => { };
    }

    let isMounted = true;
    const cacheKey = getFleetReportCacheKey({
      registrationNumber: selectedVehicle.plate,
      from: fuelDateRange.from,
      to: fuelDateRange.to,
      suffix: `logReport|coordv4|raw|rows:${LOG_REPORT_PAGE_SIZE}|tz:${effectiveFleetTimezone}|secondary:${selectedHasSecondaryEngine}|secondaryThreshold:${selectedSecondaryEngineThreshold}|refresh:${reportRefreshToken}`
    });
    const cachedResult = logReportCacheRef.current.get(cacheKey);
    if (cachedResult) {
      setLogReportData(cachedResult.data);
      setLogReportError(cachedResult.error || '');
      markReportLoaded('logReport');
      setLogReportLoading(false);
      return () => { };
    }

    const payload = {
      registration_num: selectedVehicle.plate,
      timeBegin: unixDateRange.from,
      timeEnd: unixDateRange.to,
      timezone: effectiveFleetTimezone,
      tz: effectiveFleetTimezone,
      locale: DEFAULT_FUEL_REPORT_LOCALE,
      rows: LOG_REPORT_PAGE_SIZE,
      page: 1,
      sidx: 'eventdate',
      sord: 'asc',
      profileSecondaryEngine: selectedHasSecondaryEngine,
      raw: true
    };

    const fetchLogReport = async () => {
      setLogReportLoading(true);
      setLogReportError('');
      try {
        const response = await APIService.getFleetLogReport(payload);
        if (!isMounted) return;

        const normalizedLogReport = normalizeFleetLogReport(response, {
          rawMode: shouldUseRawLogReport,
          secondaryEngineThreshold: selectedSecondaryEngineThreshold
        });
        if (normalizedLogReport.rows.length || normalizedLogReport.hasMore) {
          logReportCacheRef.current.set(cacheKey, { data: normalizedLogReport, error: '' });
          setLogReportData(normalizedLogReport);
          markReportLoaded('logReport');
          setLogReportError('');
        } else {
          logReportCacheRef.current.set(cacheKey, { data: normalizedLogReport, error: '' });
          setLogReportData(normalizedLogReport);
          markReportLoaded('logReport');
          setLogReportError('');
        }
      } catch (error) {
        if (isMounted) {
          console.log('fleet log report error', error?.message || error);
          setLogReportData(null);
          setLogReportError('Unable to load log report right now. Please try again.');
        }
      } finally {
        if (isMounted) setLogReportLoading(false);
      }
    };

    fetchLogReport();
    return () => { isMounted = false; };
  }, [activeReport, effectiveFleetTimezone, fuelDateRange.from, fuelDateRange.to, isFleetDateRangeValid, markReportLoaded, reportRefreshToken, selectedHasSecondaryEngine, selectedSecondaryEngineThreshold, selectedVehicle?.plate, shouldUseRawLogReport, unixDateRange.from, unixDateRange.to]);

  useEffect(() => {
    if (activeReport !== 'dailyReadingSummary') return () => { };
    if (!selectedVehicle?.plate || !selectedBackendVehicleId) {
      setDailyReadingSummaryData(null);
      setDailyReadingSummaryError(selectedVehicle?.plate ? 'Vehicle details not found for daily reading summary.' : '');
      setDailyReadingSummaryLoading(false);
      return () => { };
    }
    if (!isFleetDateRangeValid) {
      setDailyReadingSummaryData(null);
      setDailyReadingSummaryError('Choose a valid date range to load daily reading summary.');
      setDailyReadingSummaryLoading(false);
      return () => { };
    }

    let isMounted = true;
    const cacheKey = getFleetReportCacheKey({
      registrationNumber: selectedVehicle.plate,
      from: dailyReadingDateRange.startDate,
      to: dailyReadingDateRange.endDate,
      suffix: `dailyReadingSummary|${selectedBackendVehicleId}|tz:${effectiveFleetTimezone}|refresh:${dailyReadingSummaryRefreshToken}`
    });

    const fetchDailyReadingSummary = async () => {
      setDailyReadingSummaryLoading(true);
      setDailyReadingSummaryError('');
      try {
        const response = await APIService.getFleetDailyReadingSummary({
          vehicle_id: selectedBackendVehicleId,
          start_time__gte: dailyReadingDateRange.startDate,
          start_time__lte: dailyReadingDateRange.endDate,
          timezone: effectiveFleetTimezone,
          tz: effectiveFleetTimezone
        });
        if (!isMounted) return;

        const normalizedDailyReadingSummary = normalizeDailyReadingSummary(response);
        if (normalizedDailyReadingSummary.dailyReadings.length || normalizedDailyReadingSummary.rangeSummary.startReading !== null || normalizedDailyReadingSummary.rangeSummary.endReading !== null) {
          dailyReadingSummaryCacheRef.current.set(cacheKey, { data: normalizedDailyReadingSummary, error: '' });
          setDailyReadingSummaryData(normalizedDailyReadingSummary);
          markReportLoaded('dailyReadingSummary');
          setDailyReadingSummaryError('');
        } else {
          const noDataMessage = 'No daily reading summary was returned for the selected range.';
          dailyReadingSummaryCacheRef.current.set(cacheKey, { data: null, error: noDataMessage });
          setDailyReadingSummaryData(null);
          markReportLoaded('dailyReadingSummary');
          setDailyReadingSummaryError(noDataMessage);
        }
      } catch (error) {
        if (isMounted) {
          console.log('fleet daily reading summary error', error?.message || error);
          setDailyReadingSummaryData(null);
          setDailyReadingSummaryError('Unable to load daily reading summary right now. Please try again.');
        }
      } finally {
        if (isMounted) setDailyReadingSummaryLoading(false);
      }
    };

    fetchDailyReadingSummary();
    return () => { isMounted = false; };
  }, [
    activeReport,
    dailyReadingDateRange.endDate,
    dailyReadingDateRange.startDate,
    dateRange.fromDateTime,
    dateRange.toDateTime,
    dateRangePreset,
    dailyReadingSummaryRefreshToken,
    effectiveFleetTimezone,
    isFleetDateRangeValid,
    markReportLoaded,
    selectedBackendVehicleId,
    selectedVehicle?.plate
  ]);

  useEffect(() => {
    if (activeReport !== 'track') return () => { };
    if (!selectedVehicle?.plate) {
      setTrackData(null);
      setTrackError('');
      setTrackLoading(false);
      return () => { };
    }
    if (!isFleetDateRangeValid) {
      setTrackData(null);
      setTrackError('Choose a valid date range to load track data.');
      setTrackLoading(false);
      return () => { };
    }

    let isMounted = true;
    const cacheKey = getFleetReportCacheKey({
      registrationNumber: selectedVehicle.plate,
      from: unixDateRange.from,
      to: unixDateRange.to,
      suffix: `track|tz:${effectiveFleetTimezone}|refresh:${reportRefreshToken}`
    });
    const cachedResult = trackReportCacheRef.current.get(cacheKey);
    if (cachedResult) {
      loadGoogleMapsConfig();
      setTrackData(cachedResult.data);
      setTrackError(cachedResult.error || '');
      markReportLoaded('track');
      setTrackLoading(false);
      return () => { };
    }
    const payload = {
      registration_num: selectedVehicle.plate,
      timeBegin: unixDateRange.from,
      timeEnd: unixDateRange.to,
      timezone: effectiveFleetTimezone,
      tz: effectiveFleetTimezone
    };

    const fetchTrack = async () => {
      loadGoogleMapsConfig();
      setTrackLoading(true);
      setTrackError('');
      try {
        const response = await APIService.getFleetTrack(payload);
        if (!isMounted) return;

        const normalizedTrack = normalizeTrackReport(response);
        const normalizedLastKnown = normalizeTrackLastKnownPosition(response)
          || normalizedTrack.points[normalizedTrack.points.length - 1]
          || null;

        if (normalizedTrack.hasData || normalizedLastKnown) {
          const reportData = {
            ...normalizedTrack,
            lastKnownPosition: normalizedLastKnown
          };
          trackReportCacheRef.current.set(cacheKey, { data: reportData, error: '' });
          setTrackData(reportData);
          markReportLoaded('track');
          setTrackError('');
        } else {
          const noDataMessage = 'No track points were returned for the selected range.';
          trackReportCacheRef.current.set(cacheKey, { data: null, error: noDataMessage });
          setTrackData(null);
          markReportLoaded('track');
          setTrackError(noDataMessage);
        }
      } catch (error) {
        if (isMounted) {
          console.log('fleet track error', error?.message || error);
          setTrackData(null);
          setTrackError('Unable to load track data right now. Please try again.');
        }
      } finally {
        if (isMounted) setTrackLoading(false);
      }
    };

    fetchTrack();
    return () => { isMounted = false; };
  }, [activeReport, effectiveFleetTimezone, isFleetDateRangeValid, loadGoogleMapsConfig, markReportLoaded, reportRefreshToken, selectedVehicle?.plate, unixDateRange.from, unixDateRange.to]);

  useEffect(() => {
    if (activeReport !== 'trackFuel') return () => { };
    if (!selectedVehicle?.plate) {
      setTrackFuelData(null);
      setTrackFuelError('');
      setTrackFuelLoading(false);
      return () => { };
    }
    if (!isFleetDateRangeValid) {
      setTrackFuelData(null);
      setTrackFuelError('Choose a valid date range to load track and fuel data.');
      setTrackFuelLoading(false);
      return () => { };
    }

    let isMounted = true;
    const cacheKey = getFleetReportCacheKey({
      registrationNumber: selectedVehicle.plate,
      from: unixDateRange.from,
      to: unixDateRange.to,
      suffix: `trackFuel|${fuelDateRange.from}|${fuelDateRange.to}|tz:${effectiveFleetTimezone}|refresh:${reportRefreshToken}`
    });
    const cachedResult = trackFuelReportCacheRef.current.get(cacheKey);
    if (cachedResult) {
      loadGoogleMapsConfig();
      setTrackFuelData(cachedResult.data);
      setTrackFuelError(cachedResult.error || '');
      markReportLoaded('trackFuel');
      setTrackFuelLoading(false);
      return () => { };
    }
    const payload = {
      registration_num: selectedVehicle.plate,
      timeBegin: unixDateRange.from,
      timeEnd: unixDateRange.to,
      timezone: effectiveFleetTimezone,
      tz: effectiveFleetTimezone
    };
    const fuelLevelPayload = {
      registration_num: selectedVehicle.plate,
      timeBegin: unixDateRange.from,
      timeEnd: unixDateRange.to,
      from: fuelDateRange.from,
      to: fuelDateRange.to,
      timezone: effectiveFleetTimezone,
      tz: effectiveFleetTimezone
    };

    const fetchTrackFuel = async () => {
      loadGoogleMapsConfig();
      setTrackFuelLoading(true);
      setTrackFuelError('');
      try {
        const [trackFuelResult, fuelLevelResult] = await Promise.allSettled([
          APIService.getFleetTrackFuel(payload),
          APIService.getFleetFuelLevel(fuelLevelPayload)
        ]);
        if (!isMounted) return;

        if (trackFuelResult.status !== 'fulfilled') throw trackFuelResult.reason;

        const normalizedTrackFuel = normalizeTrackReport(trackFuelResult.value);
        const normalizedLastKnown = normalizedTrackFuel.points[normalizedTrackFuel.points.length - 1] || null;
        const normalizedFuelLevelSeries = fuelLevelResult.status === 'fulfilled'
          ? normalizeFuelLevelSeries(fuelLevelResult.value)
          : {
            hasData: false,
            actualSeries: [],
            smoothedSeries: [],
            tankCapacity: null,
            tankNumber: null
          };

        if (normalizedTrackFuel.hasData || normalizedLastKnown) {
          const reportData = {
            ...normalizedTrackFuel,
            lastKnownPosition: normalizedLastKnown,
            fuelLevelSeries: normalizedFuelLevelSeries
          };
          trackFuelReportCacheRef.current.set(cacheKey, { data: reportData, error: '' });
          setTrackFuelData(reportData);
          markReportLoaded('trackFuel');
          setTrackFuelError('');
        } else {
          const noDataMessage = 'No track or fuel points were returned for the selected range.';
          trackFuelReportCacheRef.current.set(cacheKey, { data: null, error: noDataMessage });
          setTrackFuelData(null);
          markReportLoaded('trackFuel');
          setTrackFuelError(noDataMessage);
        }
      } catch (error) {
        if (isMounted) {
          console.log('fleet track fuel error', error?.message || error);
          setTrackFuelData(null);
          setTrackFuelError('Unable to load track and fuel data right now. Please try again.');
        }
      } finally {
        if (isMounted) setTrackFuelLoading(false);
      }
    };

    fetchTrackFuel();
    return () => { isMounted = false; };
  }, [activeReport, effectiveFleetTimezone, fuelDateRange.from, fuelDateRange.to, isFleetDateRangeValid, loadGoogleMapsConfig, markReportLoaded, reportRefreshToken, selectedVehicle?.plate, unixDateRange.from, unixDateRange.to]);

  const renderVehicleLoading = () => (
    <Stack spacing={3}>
      <Card sx={{
        p: 2.5, borderRadius: 4, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: '0 20px 40px rgba(15, 23, 42, 0.06)'
      }}
      >
        <Stack spacing={2.5}>
          <Skeleton variant="text" width={280} height={42} />
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {[1, 2, 3].map((item) => <Skeleton key={item} variant="rounded" width={120} height={32} />)}
          </Stack>
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((item) => (
              <Grid item xs={12} sm={6} xl={3} key={item}>
                <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={2.5}>
            <Grid item xs={12} xl={7}>
              <Skeleton variant="rounded" height={260} sx={{ borderRadius: 3.5 }} />
            </Grid>
            <Grid item xs={12} xl={5}>
              <Skeleton variant="rounded" height={260} sx={{ borderRadius: 3.5 }} />
            </Grid>
          </Grid>
          <Skeleton variant="rounded" height={280} sx={{ borderRadius: 3.5 }} />
        </Stack>
      </Card>
    </Stack>
  );

  const renderFleetHeaderLoading = () => (
    <Card sx={{
      p: 2.5, borderRadius: 4, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: '0 18px 34px rgba(15, 23, 42, 0.05)'
    }}
    >
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>Loading live fleet status...</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: '#64748B' }}>Pulling current state from Omnicomm. Vehicles will appear once sync completes.</Typography>
        </Box>
        <LinearProgress sx={{ height: 7, borderRadius: 999 }} />
        <Grid container spacing={1.5}>
          {[1, 2, 3, 4, 5].map((item) => (
            <Grid item xs={12} sm={6} lg={2.4} key={`summary-skeleton-${item}`}>
              <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3.5 }} />
            </Grid>
          ))}
        </Grid>
      </Stack>
    </Card>
  );

  const renderNoFleetData = () => (
    <Card sx={{
      p: 2.5, borderRadius: 4, border: '1px dashed rgba(148, 163, 184, 0.32)', boxShadow: 'none'
    }}
    >
      <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>No live fleet data found</Typography>
      <Typography variant="body2" sx={{ mt: 1, color: '#64748B' }}>Fleet status API returned no vehicles. Please check the API response or refresh.</Typography>
    </Card>
  );

  const renderNoVehicleSelected = () => (
    <Card sx={{
      p: { xs: 2.2, md: 2.8 }, borderRadius: 4, border: '1px dashed rgba(148, 163, 184, 0.32)', boxShadow: 'none', background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)'
    }}
    >
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>Pick a vehicle to unlock live insights</Typography>
          <Typography variant="body2" sx={{ mt: 0.9, color: '#64748B' }}>Use the vehicle controls to focus a single asset while keeping the map and reports as the primary workspace.</Typography>
        </Box>
      </Stack>
    </Card>
  );

  const shouldShowDateTimeFilter = Boolean(activeReportConfig?.requiresDateFilter);
  const compactFleetStatusCards = [
    {
      label: 'All',
      value: 'all',
      count: fleetCounts.total,
      color: theme.palette.primary.main
    },
    {
      label: 'A',
      value: 'active',
      count: fleetCounts.active,
      color: STATUS_META.active.color
    },
    {
      label: 'I',
      value: 'idle',
      count: fleetCounts.idle,
      color: STATUS_META.idle.color
    },
    {
      label: 'O',
      value: 'offline',
      count: fleetCounts.offline,
      color: STATUS_META.offline.color
    }
  ];

  const renderCollapsedVehicleRail = () => (
    <Card sx={{
      borderRadius: 4,
      border: '1px solid rgba(148, 163, 184, 0.16)',
      boxShadow: 'none',
      overflow: 'hidden',
      position: { lg: isTrackVisualReportActive ? 'static' : 'sticky' },
      top: { lg: 84 }
    }}
    >
      <Stack
        spacing={1.1}
        alignItems="center"
        sx={{
          p: 1.1,
          bgcolor: '#FFFFFF',
          borderBottom: '1px solid rgba(148, 163, 184, 0.14)'
        }}
      >
        <Tooltip title="Expand vehicle controls" arrow>
          <IconButton
            size="small"
            onClick={() => setVehicleRailCollapsed(false)}
            sx={{
              width: 42,
              height: 42,
              color: '#004A80',
              bgcolor: alpha('#3682AE', 0.1),
              border: '1px solid rgba(54, 130, 174, 0.18)',
              '&:hover': {
                bgcolor: alpha('#3682AE', 0.18)
              }
            }}
          >
            <KeyboardDoubleArrowRightRoundedIcon />
          </IconButton>
        </Tooltip>
        <Typography
          variant="caption"
          sx={{
            color: '#475569',
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase'
          }}
        >
          Fleet
        </Typography>
      </Stack>

      <Stack spacing={0.9} sx={{ p: 1, bgcolor: '#F8FAFC' }}>
        {compactFleetStatusCards.map((item) => (
          <ButtonBase
            key={item.label}
            onClick={() => {
              setStatusFilter(item.value);
              setVehicleRailCollapsed(false);
            }}
            sx={{
              width: '100%',
              borderRadius: 3,
              p: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
              bgcolor: statusFilter === item.value
                ? alpha(item.color, 0.16)
                : '#FFFFFF',
              border: `1px solid ${alpha(item.color, 0.24)}`
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 900, color: '#102036' }}
            >
              {item.label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 900, color: item.color }}>{item.count}</Typography>
          </ButtonBase>
        ))}

        <Card sx={{
          p: 1,
          borderRadius: 3,
          boxShadow: 'none',
          border: '1px solid rgba(148, 163, 184, 0.14)',
          bgcolor: '#FFFFFF'
        }}
        >
          <Typography variant="caption" sx={{ display: 'block', color: '#64748B', fontWeight: 700 }}>
            Selected
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 0.4,
              fontWeight: 800,
              color: '#0F172A',
              lineHeight: 1.2
            }}
          >
            {selectedVehicle?.plate || '--'}
          </Typography>
        </Card>
      </Stack>
    </Card>
  );

  const renderVehicleSelectionRail = ({ overlay = false } = {}) => (
    <Card sx={{
      borderRadius: 4,
      border: '1px solid rgba(148, 163, 184, 0.16)',
      boxShadow: 'none',
      overflow: 'hidden',
      position: { lg: overlay || isTrackVisualReportActive ? 'static' : 'sticky' },
      top: { lg: 84 }
    }}
    >
      <Box sx={{ p: 1.5, borderBottom: '1px solid rgba(148, 163, 184, 0.14)', bgcolor: '#FFFFFF' }}>
        <Stack spacing={1.2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#0F172A' }}>Vehicles</Typography>
            <Stack direction="row" spacing={0.8}>
              {isDesktopFleetShell && !overlay && (
                <Tooltip title="Collapse vehicle controls" arrow>
                  <IconButton
                    size="small"
                    onClick={() => setVehicleRailCollapsed(true)}
                    sx={{
                      width: 38,
                      height: 38,
                      color: '#004A80',
                      bgcolor: alpha('#3682AE', 0.08),
                      border: '1px solid rgba(54, 130, 174, 0.16)',
                      '&:hover': {
                        bgcolor: alpha('#3682AE', 0.16)
                      }
                    }}
                  >
                    <KeyboardDoubleArrowLeftRoundedIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={isRefreshingFleet ? 'Refreshing vehicle data' : 'Refresh vehicle data'} arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => fetchCurrentState(false)}
                    disabled={isRefreshingFleet}
                    sx={{
                      width: 38,
                      height: 38,
                      color: isRefreshingFleet ? '#1D4ED8' : '#334155',
                      bgcolor: isRefreshingFleet ? alpha('#2563EB', 0.12) : '#F8FAFC',
                      border: `1px solid ${isRefreshingFleet ? alpha('#2563EB', 0.24) : 'rgba(148, 163, 184, 0.18)'}`,
                      boxShadow: isRefreshingFleet ? '0 10px 22px rgba(37, 99, 235, 0.18)' : 'none',
                      transition: 'transform 160ms ease, background-color 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
                      '&:hover': {
                        bgcolor: alpha('#2563EB', 0.1),
                        borderColor: alpha('#2563EB', 0.28),
                        transform: 'translateY(-1px)',
                        boxShadow: '0 12px 24px rgba(37, 99, 235, 0.16)'
                      }
                    }}
                  >
                    <RefreshRoundedIcon
                      sx={{
                        fontSize: 20,
                        animation: isRefreshingFleet ? 'fleetSpin 0.9s linear infinite' : 'none',
                        '@keyframes fleetSpin': {
                          from: { transform: 'rotate(0deg)' },
                          to: { transform: 'rotate(360deg)' }
                        }
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.8}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: isRefreshingFleet ? '#2563EB' : '#10B981',
                boxShadow: `0 0 0 4px ${alpha(isRefreshingFleet ? '#2563EB' : '#10B981', 0.12)}`
              }}
            />
            <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700 }}>
              {isRefreshingFleet ? 'Refreshing live vehicle and map data...' : refreshStatusLabel}
            </Typography>
          </Stack>
          <TextField
            fullWidth
            size="small"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search vehicle or site"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ color: '#94A3B8' }} />
                </InputAdornment>
              )
            }}
          />
          <Stack direction="row" spacing={0.8} sx={{ flexWrap: 'wrap', gap: 0.8 }}>
            {[
              ['all', 'All', fleetCounts.total, theme.palette.primary.main],
              ['active', 'Active', fleetCounts.active, STATUS_META.active.color],
              ['idle', 'Idle', fleetCounts.idle, STATUS_META.idle.color],
              ['offline', 'Offline', fleetCounts.offline, STATUS_META.offline.color]
            ].map(([value, label, count, color]) => {
              const isActiveFilter = statusFilter === value;
              const activeTextColor = value === 'all'
                ? '#1E40AF'
                : STATUS_META[value].accent;

              return (
                <Chip
                  key={value}
                  clickable
                  size="small"
                  label={`${label} (${count})`}
                  onClick={() => setStatusFilter(value)}
                  variant={isActiveFilter ? 'filled' : 'outlined'}
                  sx={{
                    fontWeight: 700,
                    borderColor: alpha(color, 0.4),
                    bgcolor: isActiveFilter ? alpha(color, 0.16) : '#FFFFFF',
                    color: isActiveFilter ? activeTextColor : '#334155'
                  }}
                />
              );
            })}
          </Stack>
        </Stack>
      </Box>

      <Box sx={{
        p: 1,
        maxHeight: { xs: 420, lg: overlay ? 'calc(100vh - 260px)' : 'calc(100vh - 280px)' },
        overflowY: 'auto',
        bgcolor: '#F8FAFC'
      }}
      >
        {vehicleSiteGroups.length ? (
          <Stack spacing={1}>
            {vehicleSiteGroups.map((siteGroup) => {
              const isSiteGroupExpanded = searchText.trim()
                ? true
                : !collapsedSiteGroupIds.includes(siteGroup.id);
              const expandIcon = isSiteGroupExpanded
                ? <KeyboardArrowDownRoundedIcon sx={{ fontSize: 20 }} />
                : <KeyboardArrowRightRoundedIcon sx={{ fontSize: 20 }} />;
              const statusSummary = [
                siteGroup.counts.active ? `${siteGroup.counts.active} active` : null,
                siteGroup.counts.idle ? `${siteGroup.counts.idle} idle` : null,
                siteGroup.counts.offline ? `${siteGroup.counts.offline} offline` : null
              ].filter(Boolean).join(' / ');

              return (
                <Box key={siteGroup.id}>
                  <ButtonBase
                    onClick={() => handleSiteGroupToggle(siteGroup.id)}
                    sx={{
                      width: '100%',
                      display: 'block',
                      textAlign: 'left',
                      borderRadius: 2
                    }}
                  >
                    <Box
                      sx={{
                        px: 1,
                        py: 0.9,
                        borderRadius: 2,
                        border: '1px solid rgba(148, 163, 184, 0.18)',
                        bgcolor: '#FFFFFF',
                        transition: 'background-color 150ms ease, border-color 150ms ease',
                        '&:hover': {
                          bgcolor: alpha('#3682AE', 0.05),
                          borderColor: alpha('#3682AE', 0.24)
                        }
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: 1.5,
                            display: 'grid',
                            placeItems: 'center',
                            flexShrink: 0,
                            color: '#004A80',
                            bgcolor: alpha('#3682AE', 0.1)
                          }}
                        >
                          <PlaceRoundedIcon sx={{ fontSize: 17 }} />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            noWrap
                            variant="body2"
                            sx={{
                              color: '#0F172A',
                              fontWeight: 900,
                              lineHeight: 1.2
                            }}
                          >
                            {siteGroup.siteName}
                          </Typography>
                          <Typography
                            noWrap
                            variant="caption"
                            sx={{
                              display: 'block',
                              mt: 0.25,
                              color: '#64748B',
                              fontWeight: 700
                            }}
                          >
                            {siteGroup.vehicles.length}
                            {' '}
                            vehicle
                            {siteGroup.vehicles.length === 1 ? '' : 's'}
                            {statusSummary ? ` - ${statusSummary}` : ''}
                          </Typography>
                        </Box>
                        <Box sx={{ color: '#64748B', flexShrink: 0, lineHeight: 0 }}>
                          {expandIcon}
                        </Box>
                      </Stack>
                    </Box>
                  </ButtonBase>

                  {isSiteGroupExpanded && (
                    <Stack
                      spacing={0.8}
                      sx={{
                        mt: 0.8,
                        pl: 1.25,
                        borderLeft: '2px solid rgba(54, 130, 174, 0.16)'
                      }}
                    >
                      {siteGroup.vehicles.map((vehicle) => (
                        <VehicleListItem
                          key={vehicle.id}
                          vehicle={vehicle}
                          selected={selectedVehicleIds.includes(vehicle.id)}
                          onClick={() => handleVehicleSelect(vehicle.id)}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Card sx={{
            p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.24)', boxShadow: 'none'
          }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0F172A' }}>{vehicles.length ? 'No vehicles matched' : 'No live vehicles available'}</Typography>
            <Typography variant="body2" sx={{ mt: 0.75, color: '#64748B' }}>Try another search or status filter.</Typography>
          </Card>
        )}
      </Box>
    </Card>
  );

  const renderVehicleSelectionHoverRail = () => (
    <Box
      sx={{
        display: { xs: 'none', lg: 'block' },
        position: 'fixed',
        left: 0,
        top: 112,
        bottom: 24,
        width: 24,
        zIndex: theme.zIndex.drawer + 1,
        pointerEvents: 'auto',
        '&:hover .fleetVehicleHoverPanel, &:focus-within .fleetVehicleHoverPanel': {
          opacity: 1,
          visibility: 'visible',
          pointerEvents: 'auto',
          transform: 'translateX(0)'
        },
        '&:hover .fleetVehicleHoverTab, &:focus-within .fleetVehicleHoverTab': {
          opacity: 0,
          transform: 'translateX(-12px)'
        }
      }}
    >
      <Tooltip title="Vehicle tree" placement="right" arrow>
        <ButtonBase
          className="fleetVehicleHoverTab"
          aria-label="Open vehicle tree"
          sx={{
            position: 'absolute',
            left: 0,
            top: 8,
            width: 24,
            height: 96,
            borderRadius: '0 12px 12px 0',
            bgcolor: '#FFFFFF',
            color: '#004A80',
            border: '1px solid rgba(54, 130, 174, 0.24)',
            borderLeft: 0,
            boxShadow: '0 12px 28px rgba(15, 23, 42, 0.14)',
            transition: 'opacity 180ms ease, transform 180ms ease, background-color 180ms ease',
            '&:hover': {
              bgcolor: alpha('#3682AE', 0.08)
            }
          }}
        >
          <KeyboardDoubleArrowRightRoundedIcon sx={{ fontSize: 20 }} />
        </ButtonBase>
      </Tooltip>

      <Box
        className="fleetVehicleHoverPanel"
        sx={{
          position: 'absolute',
          left: 12,
          top: 0,
          width: 360,
          maxWidth: 'calc(100vw - 32px)',
          opacity: 0,
          visibility: 'hidden',
          pointerEvents: 'none',
          transform: 'translateX(-384px)',
          transition: 'opacity 180ms ease, transform 180ms ease, visibility 180ms ease',
          filter: 'drop-shadow(0 20px 42px rgba(15, 23, 42, 0.20))'
        }}
      >
        {renderVehicleSelectionRail({ overlay: true })}
      </Box>
    </Box>
  );

  const renderTrackReport = () => {
    const {
      currentPoint,
      endPoint,
      totalPoints
    } = trackSummary;
    const reportTitle = isTrackFuelReportActive ? 'Track + Fuel' : 'Track';
    const invalidRangeLabel = isTrackFuelReportActive ? 'track and fuel data' : 'track data';
    const lastKnownTimeLabel = formatTrackTimestamp(
      currentPoint?.timestamp || currentPoint?.eventDate || endPoint?.timestamp,
      formatSyncDate(selectedVehicle?.lastSyncAt, selectedVehicle?.lastSyncMinutes)
    );
    const activeIgnitionGroup = selectedTrackEventGroup
      ? trackIgnitionGroups.find((group) => group.id === selectedTrackEventGroup.id) || null
      : null;
    const activeIgnitionEvents = activeIgnitionGroup?.events || [];
    const selectedLocationPoint = selectedTrackPoint?.point || null;
    const selectedLocationTitle = selectedTrackPoint?.title || 'Location point';
    const playbackSliderMax = Math.max(totalPoints - 1, 0);
    const playbackTimestampLabel = formatTrackTimestamp(
      trackPlaybackPoint?.timestamp || trackPlaybackPoint?.eventDate || trackPlaybackPoint?.rawTimestamp,
      lastKnownTimeLabel
    );
    const isTrackAwaitingResponse = shouldShowFirstLoadSkeleton({
      isLoading: activeTrackLoading,
      error: activeTrackError,
      data: activeTrackData
    });

    const renderTrackMapBody = () => {
      if (isMapKeyLoading) {
        return (
          <Stack spacing={1.2}>
            <Typography variant="body2" sx={{ color: '#475569', fontWeight: 700 }}>Loading map...</Typography>
            <LinearProgress sx={{ height: 8, borderRadius: 999 }} />
          </Stack>
        );
      }

      if (mapError) {
        return (
          <Stack spacing={1.1} alignItems="flex-start">
            <Typography variant="body2" sx={{ color: '#B91C1C', fontWeight: 700 }}>{mapError}</Typography>
            <Button variant="contained" size="small" onClick={handleRetryLocationMap}>Retry map load</Button>
          </Stack>
        );
      }

      if (!googleMapsKey) {
        return (
          <Stack spacing={1.2}>
            <Typography variant="body2" sx={{ color: '#475569', fontWeight: 700 }}>Loading map...</Typography>
            <LinearProgress sx={{ height: 8, borderRadius: 999 }} />
          </Stack>
        );
      }

      return (
        <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
          <Box ref={trackMapContainerRef} sx={TRACK_MAP_CONTAINER_STYLE} />

          {isTrackVisualReportActive && (
            <>
              {activeIgnitionGroup && (
                <Box sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  zIndex: 2,
                  width: { xs: 'calc(100% - 32px)', sm: 280, md: 270 },
                  maxHeight: { xs: 260, md: 250 },
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.97)',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  boxShadow: '0 10px 24px rgba(15, 23, 42, 0.14)',
                  backdropFilter: 'blur(10px)'
                }}
                >
                  <Box sx={{ p: 1, borderBottom: '1px solid rgba(148, 163, 184, 0.14)' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={0.8}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ color: '#0284C7', fontWeight: 800, lineHeight: 1.1 }}>
                          {selectedVehicle?.plate}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mt: 0.25 }}>
                          {formatCoordinateLabel(activeIgnitionGroup.latitude)}
                          {', '}
                          {formatCoordinateLabel(activeIgnitionGroup.longitude)}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => setSelectedTrackEventGroup(null)}
                        sx={{ color: '#64748B', mt: -0.2, mr: -0.3 }}
                      >
                        <CloseRoundedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                      <Chip
                        size="small"
                        label={`${activeIgnitionGroup.eventCount} events`}
                        sx={{
                          bgcolor: '#FFF7ED',
                          color: '#C2410C',
                          fontWeight: 700,
                          height: 22
                        }}
                      />
                      <Chip
                        size="small"
                        label={`${activeIgnitionGroup.ignitionOnCount} on`}
                        sx={{
                          bgcolor: '#DBEAFE',
                          color: '#1D4ED8',
                          fontWeight: 700,
                          height: 22
                        }}
                      />
                      <Chip
                        size="small"
                        label={`${activeIgnitionGroup.ignitionOffCount} off`}
                        sx={{
                          bgcolor: '#E0E7FF',
                          color: '#4338CA',
                          fontWeight: 700,
                          height: 22
                        }}
                      />
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: 'auto',
                      p: 0.8
                    }}
                  >
                    <Stack spacing={0.55}>
                      {activeIgnitionEvents.map((event) => (
                        <Box
                          key={event.id}
                          sx={{
                            px: 0.85,
                            py: 0.62,
                            borderRadius: 1.6,
                            border: '1px solid rgba(148, 163, 184, 0.14)',
                            bgcolor: '#FFFFFF'
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" spacing={1}>
                            <Typography variant="caption" sx={{ color: '#334155', fontWeight: 700 }}>
                              {event.type === 5 ? 'Ignition On' : 'Ignition Off'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700, whiteSpace: 'nowrap' }}>
                              {formatTrackTimestamp(event.timestamp || event.eventDateValue)}
                            </Typography>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              )}

              {selectedLocationPoint && (
                <Box sx={{
                  position: 'absolute',
                  top: activeIgnitionGroup ? { xs: 288, md: 278 } : 16,
                  right: 16,
                  zIndex: 2,
                  width: { xs: 'calc(100% - 32px)', sm: 280, md: 270 },
                  maxHeight: { xs: 250, md: 238 },
                  overflow: 'hidden',
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.97)',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  boxShadow: '0 10px 24px rgba(15, 23, 42, 0.14)',
                  backdropFilter: 'blur(10px)'
                }}
                >
                  <Box sx={{ p: 0.9 }}>
                    <Stack direction="row" justifyContent="space-between" spacing={0.7} alignItems="flex-start">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
                          {selectedLocationTitle}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#475569', display: 'block', mt: 0.15 }}>
                          {formatCoordinateLabel(selectedLocationPoint.latitude)}
                          {', '}
                          {formatCoordinateLabel(selectedLocationPoint.longitude)}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedTrackPoint(null);
                          setSelectedTrackPointDetails(null);
                          setSelectedTrackPointDetailsError('');
                        }}
                        sx={{ color: '#64748B', mt: -0.2, mr: -0.3 }}
                      >
                        <CloseRoundedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#64748B',
                        fontWeight: 700,
                        mt: 0.45,
                        display: 'block'
                      }}
                    >
                      Location details
                    </Typography>
                    <Box
                      sx={{
                        mt: 0.55,
                        maxHeight: { xs: 165, md: 152 },
                        overflowY: 'auto',
                        pr: 0.35
                      }}
                    >
                      {(() => {
                        if (selectedTrackPointDetailsLoading) {
                          return (
                            <Stack spacing={0.7}>
                              <LinearProgress sx={{ height: 5, borderRadius: 999 }} />
                              <Typography variant="caption" sx={{ color: '#64748B' }}>
                                Loading...
                              </Typography>
                            </Stack>
                          );
                        }

                        if (selectedTrackPointDetailsError) {
                          return (
                            <Typography variant="caption" sx={{ display: 'block', color: '#B91C1C' }}>
                              {selectedTrackPointDetailsError}
                            </Typography>
                          );
                        }

                        return (
                          <Stack spacing={0.45}>
                            <Typography variant="caption" sx={{ color: '#334155' }}>
                              Time:
                              {' '}
                              {formatTrackTimestamp(
                                selectedLocationPoint.timestamp
                                || selectedLocationPoint.eventDate
                                || selectedLocationPoint.rawTimestamp,
                                '--'
                              )}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#334155' }}>
                              Speed:
                              {' '}
                              {Number.isFinite(Number(selectedLocationPoint.speed)) ? `${Number(selectedLocationPoint.speed)} km/h` : '--'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#334155' }}>
                              Fuel level:
                              {' '}
                              {formatFuelVolume(selectedLocationPoint.fuelVolume)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#334155' }}>
                              Ignition:
                              {' '}
                              {getTrackBooleanLabel(selectedLocationPoint.ignition, 'On', 'Off')}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#334155' }}>
                              Address:
                              {' '}
                              {selectedTrackPointDetails?.address || '--'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#334155' }}>
                              Engine:
                              {' '}
                              {selectedTrackPointDetails?.engineWork || '--'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#334155' }}>
                              Fuel temp:
                              {' '}
                              {selectedTrackPointDetails?.tempFuel !== null && selectedTrackPointDetails?.tempFuel !== undefined ? `${selectedTrackPointDetails.tempFuel} deg C` : '--'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#334155' }}>
                              Voltage:
                              {' '}
                              {selectedTrackPointDetails?.voltage !== null && selectedTrackPointDetails?.voltage !== undefined ? `${selectedTrackPointDetails.voltage} V` : '--'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#334155' }}>
                              Mileage CAN:
                              {' '}
                              {selectedTrackPointDetails?.mileageCAN !== null && selectedTrackPointDetails?.mileageCAN !== undefined ? `${selectedTrackPointDetails.mileageCAN}` : '--'}
                            </Typography>
                          </Stack>
                        );
                      })()}
                    </Box>
                  </Box>
                </Box>
              )}

              {totalPoints > 1 && (
                <Box sx={{
                  position: 'absolute',
                  top: 14,
                  left: 14,
                  zIndex: 2,
                  width: { xs: 'calc(100% - 28px)', sm: 360, md: 420 },
                  px: 1,
                  py: 0.8,
                  bgcolor: 'rgba(255,255,255,0.95)',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  borderRadius: 2,
                  boxShadow: '0 10px 24px rgba(15, 23, 42, 0.12)',
                  backdropFilter: 'blur(8px)'
                }}
                >
                  <Stack spacing={0.7}>
                    <Stack direction="row" spacing={0.8} alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={0.3} alignItems="center">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setTrackPlaybackActive(false);
                            setTrackPlaybackIndex((previousIndex) => Math.max(previousIndex - 1, 0));
                          }}
                        >
                          <SkipPreviousRoundedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (!totalPoints) return;
                            if (!isTrackPlaybackActive && clampedTrackPlaybackIndex >= playbackSliderMax) {
                              setTrackPlaybackIndex(0);
                            }
                            setTrackPlaybackActive((previousValue) => !previousValue);
                          }}
                          sx={{
                            width: 34,
                            height: 34,
                            bgcolor: '#0F172A',
                            color: '#FFFFFF',
                            '&:hover': { bgcolor: '#1E293B' }
                          }}
                        >
                          {isTrackPlaybackActive ? <PauseRoundedIcon fontSize="small" /> : <PlayArrowRoundedIcon fontSize="small" />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setTrackPlaybackActive(false);
                            setTrackPlaybackIndex((previousIndex) => Math.min(previousIndex + 1, playbackSliderMax));
                          }}
                        >
                          <SkipNextRoundedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setTrackPlaybackActive(false);
                            setTrackPlaybackIndex(0);
                          }}
                        >
                          <StopRoundedIcon fontSize="small" />
                        </IconButton>
                      </Stack>

                      <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {playbackTimestampLabel}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
                        Playback speed
                      </Typography>
                      <Stack direction="row" spacing={0.6} alignItems="center" useFlexGap flexWrap="wrap" justifyContent="flex-end">
                        {TRACK_PLAYBACK_SPEED_OPTIONS.map((option) => {
                          const isActiveSpeed = trackPlaybackSpeed === option.value;
                          return (
                            <Chip
                              key={option.value}
                              size="small"
                              clickable
                              label={option.label}
                              onClick={() => setTrackPlaybackSpeed(option.value)}
                              sx={{
                                height: 24,
                                fontWeight: 700,
                                borderRadius: 999,
                                bgcolor: isActiveSpeed ? '#F97316' : alpha('#F97316', 0.1),
                                color: isActiveSpeed ? '#FFFFFF' : '#C2410C',
                                border: `1px solid ${isActiveSpeed ? '#F97316' : alpha('#F97316', 0.22)}`,
                                '&:hover': {
                                  bgcolor: isActiveSpeed ? '#EA580C' : alpha('#F97316', 0.18)
                                }
                              }}
                            />
                          );
                        })}
                      </Stack>
                    </Stack>

                    <Box sx={{ width: '100%' }}>
                      <Box
                        component="input"
                        type="range"
                        min={0}
                        max={playbackSliderMax}
                        step={1}
                        value={clampedTrackPlaybackIndex}
                        onChange={(event) => {
                          setTrackPlaybackActive(false);
                          setTrackPlaybackIndex(Number(event.target.value) || 0);
                        }}
                        sx={{
                          width: '100%',
                          height: 4,
                          borderRadius: 999,
                          cursor: 'pointer',
                          accentColor: '#F97316',
                          '&::-webkit-slider-thumb': {
                            WebkitAppearance: 'none',
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            background: '#F97316',
                            border: '2px solid #FFFFFF',
                            boxShadow: '0 0 0 1px rgba(249, 115, 22, 0.3)'
                          }
                        }}
                      />
                    </Box>
                  </Stack>
                </Box>
              )}
            </>
          )}

          {activeTrackError && (
            <Box sx={{
              position: 'absolute', inset: 0, bgcolor: 'rgba(248, 250, 252, 0.86)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2
            }}
            >
              <Stack spacing={1.1} alignItems="center" sx={{ maxWidth: 420, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 800 }}>
                  {reportTitle}
                  {' unavailable'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748B' }}>{activeTrackError}</Typography>
              </Stack>
            </Box>
          )}

          {!activeTrackError && (activeTrackLoading || isTrackAwaitingResponse) && (
            <Box sx={{
              position: 'absolute', inset: 0, bgcolor: 'rgba(248, 250, 252, 0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none'
            }}
            >
              <Stack spacing={1} alignItems="center" sx={{ minWidth: 220 }}>
                <Typography variant="body2" sx={{ color: '#475569', fontWeight: 700 }}>
                  {isTrackFuelReportActive ? 'Loading route and fuel data...' : 'Loading route data...'}
                </Typography>
                <LinearProgress sx={{ width: '100%', height: 7, borderRadius: 999 }} />
              </Stack>
            </Box>
          )}

          {!activeTrackError && !activeTrackLoading && !isTrackAwaitingResponse && !totalPoints && !currentPoint && (
            <Box sx={{
              position: 'absolute', inset: 0, bgcolor: 'rgba(248, 250, 252, 0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
            >
              <Typography variant="body2" sx={{ color: '#475569', fontWeight: 700 }}>
                {isTrackFuelReportActive
                  ? 'No mappable route points were returned for this range.'
                  : 'No mappable track points were returned for this range.'}
              </Typography>
            </Box>
          )}

          {isMapScriptLoading && (
            <Box sx={{
              position: 'absolute', inset: 0, bgcolor: 'rgba(248, 250, 252, 0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none'
            }}
            >
              <Stack spacing={1} alignItems="center" sx={{ minWidth: 220 }}>
                <Typography variant="body2" sx={{ color: '#475569', fontWeight: 700 }}>Loading route map...</Typography>
                <LinearProgress sx={{ width: '100%', height: 7, borderRadius: 999 }} />
              </Stack>
            </Box>
          )}
        </Box>
      );
    };

    if (!selectedVehicle?.plate) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Choose a vehicle</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            Select a vehicle to load
            {' '}
            {invalidRangeLabel}
            .
          </Typography>
        </Card>
      );
    }

    if (!isFleetDateRangeValid) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Invalid date range</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            {dateRangeValidationMessage}
            {' '}
            Then choose a range to load
            {' '}
            {invalidRangeLabel}
            .
          </Typography>
        </Card>
      );
    }

    if (isTrackReportActive) {
      return (
        <Box sx={{
          height: { xs: 560, lg: 780 },
          borderRadius: 3.25,
          border: '1px solid rgba(148, 163, 184, 0.16)',
          overflow: 'hidden',
          position: 'relative',
          bgcolor: '#F8FAFC'
        }}
        >
          {renderTrackMapBody()}
        </Box>
      );
    }

    return (
      <Stack spacing={1.5}>
        <Card sx={{
          p: 0,
          borderRadius: 3.25,
          border: '1px solid rgba(148, 163, 184, 0.16)',
          boxShadow: 'none',
          overflow: 'hidden'
        }}
        >
          <Box sx={{
            height: { xs: 420, lg: 560 },
            borderRadius: 2.5,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#F8FAFC',
            position: 'relative'
          }}
          >
            {renderTrackMapBody()}
          </Box>
        </Card>

        <Card sx={{
          p: 0,
          borderRadius: 3.4,
          border: '1px solid rgba(148, 163, 184, 0.16)',
          boxShadow: '0 18px 44px rgba(15, 23, 42, 0.04)',
          overflow: 'hidden',
          bgcolor: '#FFFFFF',
          backgroundImage: 'linear-gradient(180deg, rgba(248, 250, 252, 0.85) 0%, rgba(255, 255, 255, 1) 100%)'
        }}
        >
          <Box sx={{ p: { xs: 1, md: 1.3 } }}>
            {trackFuelChartOptions ? (
              <Box sx={{ height: 440 }}>
                <ReactECharts options={trackFuelChartOptions} onEvents={trackFuelChartEvents} />
              </Box>
            ) : (
              <Stack
                spacing={0.8}
                alignItems="center"
                justifyContent="center"
                sx={{ minHeight: 260, color: '#64748B', textAlign: 'center' }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {activeTrackLoading ? 'Loading chart data...' : 'No fuel chart points were returned for this range.'}
                </Typography>
                <Typography variant="caption">{lastKnownTimeLabel}</Typography>
              </Stack>
            )}
          </Box>
        </Card>
      </Stack>
    );
  };

  const renderAnalyticsReport = () => {
    const isAnalyticsAwaitingResponse = shouldShowFirstLoadSkeleton({
      isLoading: isAnalyticsLoading,
      error: analyticsError,
      data: analyticsData
    });

    if (!selectedVehicle?.plate) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Choose a vehicle</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            Select a vehicle to load dashboard.
          </Typography>
        </Card>
      );
    }

    if (isAnalyticsLoading || isAnalyticsAwaitingResponse) {
      return (
        <Grid container spacing={1.5}>
          <Grid item xs={12}><Skeleton variant="rounded" height={96} sx={{ borderRadius: 3 }} /></Grid>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} xl={3} key={`analytics-card-skeleton-${item}`}>
              <Skeleton variant="rounded" height={116} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
          <Grid item xs={12} xl={7}><Skeleton variant="rounded" height={340} sx={{ borderRadius: 3 }} /></Grid>
          <Grid item xs={12} xl={5}><Skeleton variant="rounded" height={340} sx={{ borderRadius: 3 }} /></Grid>
        </Grid>
      );
    }

    if (analyticsError) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Dashboard unavailable</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>{analyticsError}</Typography>
        </Card>
      );
    }

    if (!analyticsData) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>No dashboard found</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            No dashboard data was returned for
            {' '}
            {selectedVehicle.plate}
            .
          </Typography>
        </Card>
      );
    }

    const analyticsSummaryCards = analyticsData.summary?.cards || [];
    const analyticsCharts = analyticsData.charts || {};
    const dailyUtilizationRows = analyticsCharts.dailyUtilization || [];
    const cumulativeRows = analyticsCharts.cumulativeUtilization || [];
    const ignitionBreakdownRows = analyticsCharts.ignitionBreakdown || [];
    const engineBreakdownRows = analyticsCharts.engineBreakdown || [];
    const hourlyRows = analyticsCharts.hourlyHeatmap || [];
    const topLocationRows = analyticsCharts.topLocations || [];
    const locationPointRows = analyticsCharts.locationPoints || [];
    const dataQualityRows = analyticsCharts.dataQuality || [];
    const analyticsTableColumns = analyticsData.table?.columns || [];
    const analyticsTableRows = analyticsData.table?.rows || [];
    const readingUnit = analyticsData.vehicle?.readingUnit || dailyUtilizationRows.find((row) => row.unit)?.unit || '';
    const dateRangeLabel = analyticsData.dateRange?.label
      || (analyticsData.dateRange?.startDate && analyticsData.dateRange?.endDate
        ? `${formatDailyReadingDate(analyticsData.dateRange.startDate)} - ${formatDailyReadingDate(analyticsData.dateRange.endDate)}`
        : `Last ${analyticsPeriodDays} days`);
    const hasEngineBreakdown = engineBreakdownRows.some((row) => getAnalyticsMetricValue(row, 'value') > 0);
    const visibleRangeOptions = analyticsData.rangeOptions?.length
      ? analyticsData.rangeOptions
      : ANALYTICS_PERIOD_OPTIONS.map((days) => ({ value: days, label: `Last ${days} days` }));

    const renderAnalyticsChartCard = (title, subtitle, chartOptions, isEmpty = false) => (
      <Card sx={{
        p: 1.4, borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none', height: '100%'
      }}
      >
        <Stack spacing={1.1} sx={{ height: '100%' }}>
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#0F172A', fontWeight: 800 }}>{title}</Typography>
            {subtitle && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block', mt: 0.3, color: '#64748B', fontWeight: 700
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{
            height: 284, minHeight: 284, display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
          >
            {isEmpty ? (
              <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 700, textAlign: 'center' }}>
                No chart data for this period.
              </Typography>
            ) : (
              <ReactECharts options={chartOptions} />
            )}
          </Box>
        </Stack>
      </Card>
    );

    return (
      <Stack spacing={1.35}>
        <Card sx={{
          p: 1.25, borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.18)', boxShadow: 'none', bgcolor: '#F8FAFC'
        }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 900 }}>
                {analyticsData.vehicle?.registrationNumber || selectedVehicle.plate}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block', mt: 0.35, color: '#64748B', fontWeight: 700
                }}
              >
                {dateRangeLabel}
              </Typography>
            </Box>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.7 }}>
              {visibleRangeOptions.map((option) => {
                const optionValue = Number(option.value);
                const isSelected = optionValue === Number(analyticsPeriodDays);
                return (
                  <Chip
                    key={option.value}
                    label={option.label || `Last ${optionValue} days`}
                    clickable
                    onClick={() => {
                      primeReportLoading('analytics');
                      setAnalyticsPeriodDays(optionValue);
                    }}
                    variant={isSelected ? 'filled' : 'outlined'}
                    sx={{
                      fontWeight: 800,
                      color: isSelected ? '#FFFFFF' : '#334155',
                      bgcolor: isSelected ? '#0F172A' : '#FFFFFF',
                      borderColor: 'rgba(148, 163, 184, 0.4)'
                    }}
                  />
                );
              })}
            </Stack>
          </Stack>
        </Card>

        <Grid container spacing={1}>
          {analyticsSummaryCards.map((card, index) => {
            const accentColor = card.color || ['#2563EB', '#10B981', '#F97316', '#7C3AED'][index % 4];
            return (
              <Grid item xs={12} sm={6} xl={3} key={card.key || card.label || `analytics-card-${index + 1}`}>
                <Card sx={{
                  p: 1.35,
                  borderRadius: 3,
                  border: `1px solid ${alpha(accentColor, 0.16)}`,
                  boxShadow: 'none',
                  bgcolor: alpha(accentColor, 0.055),
                  minHeight: 112
                }}
                >
                  <Stack spacing={0.7}>
                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 800 }}>
                      {card.label || '--'}
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#0F172A', fontWeight: 900, lineHeight: 1.25 }}>
                      {card.value ?? '--'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700 }}>
                      {card.caption || ''}
                    </Typography>
                  </Stack>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Grid container spacing={1.25}>
          <Grid item xs={12} xl={7}>
            {renderAnalyticsChartCard(
              'Daily utilization',
              readingUnit ? `Daily reading delta with ignition and engine hours (${readingUnit})` : 'Daily reading delta with ignition and engine hours',
              buildAnalyticsDailyUtilizationOptions(dailyUtilizationRows, readingUnit),
              !dailyUtilizationRows.length
            )}
          </Grid>
          <Grid item xs={12} xl={5}>
            {renderAnalyticsChartCard(
              'Cumulative usage',
              readingUnit ? `Running total in ${readingUnit}` : 'Running total',
              buildAnalyticsCumulativeOptions(cumulativeRows, readingUnit),
              !cumulativeRows.length
            )}
          </Grid>
          <Grid item xs={12} lg={hasEngineBreakdown ? 4 : 6}>
            {renderAnalyticsChartCard(
              'Ignition split',
              'On versus off hours',
              buildAnalyticsBreakdownOptions(ignitionBreakdownRows),
              !ignitionBreakdownRows.length
            )}
          </Grid>
          {hasEngineBreakdown && (
            <Grid item xs={12} lg={4}>
              {renderAnalyticsChartCard(
                'Engine split',
                'Engine signal hours',
                buildAnalyticsBreakdownOptions(engineBreakdownRows),
                !engineBreakdownRows.length
              )}
            </Grid>
          )}
          <Grid item xs={12} lg={hasEngineBreakdown ? 4 : 6}>
            {renderAnalyticsChartCard(
              'Hourly activity',
              'Ignition and engine hours by hour',
              buildAnalyticsHourlyOptions(hourlyRows),
              !hourlyRows.length
            )}
          </Grid>
        </Grid>

        <Grid container spacing={1.25}>
          <Grid item xs={12} xl={5}>
            <Card sx={{
              p: 1.4, borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none', height: '100%'
            }}
            >
              <Stack spacing={1.1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Typography variant="subtitle2" sx={{ color: '#0F172A', fontWeight: 800 }}>Location summary</Typography>
                  <Chip
                    size="small"
                    label={`${locationPointRows.length} points`}
                    sx={{ bgcolor: alpha('#0891B2', 0.1), color: '#0E7490', fontWeight: 800 }}
                  />
                </Stack>
                {topLocationRows.length ? (
                  <Stack spacing={0.8}>
                    {topLocationRows.slice(0, 5).map((row, index) => (
                      <Box
                        key={row.id || row.name || `top-location-${index + 1}`}
                        sx={{
                          p: 1,
                          borderRadius: 2,
                          border: '1px solid rgba(148, 163, 184, 0.14)',
                          bgcolor: '#FFFFFF'
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 800 }}>
                              {row.name || row.address || 'Location'}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', color: '#64748B', fontWeight: 700 }}>
                              {row.address || [row.latitude, row.longitude].filter(Boolean).join(', ') || '--'}
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ color: '#334155', fontWeight: 800, whiteSpace: 'nowrap' }}>
                            {row.count ?? row.visits ?? 0}
                            {' '}
                            point
                            {Number(row.count ?? row.visits ?? 0) === 1 ? '' : 's'}
                          </Typography>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 700 }}>No location clusters found for this period.</Typography>
                )}
              </Stack>
            </Card>
          </Grid>
          <Grid item xs={12} xl={7}>
            <Card sx={{
              p: 1.4, borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none', height: '100%'
            }}
            >
              <Stack spacing={1.1}>
                <Typography variant="subtitle2" sx={{ color: '#0F172A', fontWeight: 800 }}>Data quality</Typography>
                <Grid container spacing={0.8}>
                  {dataQualityRows.length ? dataQualityRows.map((row, index) => (
                    <Grid item xs={12} sm={6} md={4} key={row.key || row.label || `data-quality-${index + 1}`}>
                      <Box sx={{
                        p: 1,
                        borderRadius: 2,
                        border: '1px solid rgba(148, 163, 184, 0.14)',
                        bgcolor: '#FFFFFF',
                        minHeight: 72
                      }}
                      >
                        <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 800 }}>{row.label || row.name || '--'}</Typography>
                        <Typography variant="body2" sx={{ mt: 0.45, color: '#0F172A', fontWeight: 900 }}>
                          {row.value ?? '--'}
                        </Typography>
                      </Box>
                    </Grid>
                  )) : (
                    <Grid item xs={12}>
                      <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 700 }}>No data quality metrics returned.</Typography>
                    </Grid>
                  )}
                </Grid>
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {analyticsTableColumns.length > 0 && analyticsTableRows.length > 0 && (
          <Card sx={{
            borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none', overflow: 'hidden'
          }}
          >
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    {analyticsTableColumns.map((column) => (
                      <TableCell
                        key={column.key}
                        sx={{
                          py: 1.1, px: 1.2, color: '#475569', fontWeight: 800, borderBottom: '1px solid rgba(148, 163, 184, 0.16)', whiteSpace: 'nowrap'
                        }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analyticsTableRows.map((row, index) => {
                    const displayRow = row.displayRow && typeof row.displayRow === 'object' ? row.displayRow : row;
                    return (
                      <TableRow key={row.id || `analytics-table-${index + 1}`} hover>
                        {analyticsTableColumns.map((column) => (
                          <TableCell
                            key={column.key}
                            sx={{
                              py: 1.1,
                              px: 1.2,
                              whiteSpace: ['startLocation', 'endLocation', 'location'].includes(column.key) ? 'normal' : 'nowrap',
                              minWidth: ['startLocation', 'endLocation', 'location'].includes(column.key) ? 220 : undefined
                            }}
                          >
                            {displayRow[column.key] ?? ''}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
      </Stack>
    );
  };

  const renderAnalyticsDashboardReport = () => {
    const isAnalyticsAwaitingResponse = shouldShowFirstLoadSkeleton({
      isLoading: isAnalyticsLoading,
      error: analyticsError,
      data: analyticsData
    });

    if (!selectedVehicle?.plate) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Choose a vehicle</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            Select a vehicle to load dashboard.
          </Typography>
        </Card>
      );
    }

    if ((isAnalyticsLoading || isAnalyticsAwaitingResponse) && !analyticsData) {
      return (
        <Stack spacing={1.25}>
          <Card sx={{
            p: 1.25, borderRadius: 2, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none', bgcolor: '#F8FAFC'
          }}
          >
            <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
              {ANALYTICS_DASHBOARD_RANGE_OPTIONS.map((option) => (
                <Skeleton key={option.value} variant="rounded" width={74} height={32} sx={{ borderRadius: 999 }} />
              ))}
            </Stack>
          </Card>
          <Grid container spacing={1}>
            {[1, 2, 3, 4].map((item) => (
              <Grid item xs={12} sm={6} xl={3} key={`analytics-stat-loading-${item}`}>
                <Card sx={{
                  p: 1.4,
                  borderRadius: 2,
                  border: '1px solid rgba(148, 163, 184, 0.14)',
                  boxShadow: 'none',
                  overflow: 'hidden',
                  position: 'relative',
                  minHeight: 116,
                  animation: 'analyticsStatLoad 900ms ease both',
                  animationDelay: `${item * 90}ms`,
                  '@keyframes analyticsStatLoad': {
                    '0%': { opacity: 0.45, transform: 'translateY(8px)' },
                    '100%': { opacity: 1, transform: 'translateY(0)' }
                  }
                }}
                >
                  <Skeleton variant="text" width="48%" />
                  <Skeleton variant="text" width="72%" height={36} />
                  <Skeleton variant="rounded" width="100%" height={7} sx={{ mt: 1.2, borderRadius: 999 }} />
                </Card>
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={1.25}>
            <Grid item xs={12} xl={7}><Skeleton variant="rounded" height={360} sx={{ borderRadius: 2 }} /></Grid>
            <Grid item xs={12} xl={5}><Skeleton variant="rounded" height={360} sx={{ borderRadius: 2 }} /></Grid>
          </Grid>
        </Stack>
      );
    }

    if (analyticsError && !analyticsData) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Dashboard unavailable</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>{analyticsError}</Typography>
        </Card>
      );
    }

    if (!analyticsData) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>No dashboard found</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            No dashboard data was returned for
            {' '}
            {selectedVehicle.plate}
            .
          </Typography>
        </Card>
      );
    }

    const statisticsReport = analyticsData.statistics || {};
    const vehicleStats = statisticsReport?.vehicleData || {};
    const fuelStats = vehicleStats?.fuel || vehicleStats;
    const totalsStats = statisticsReport?.totals || {};
    const periodMileageStats = statisticsReport?.periodMileage || vehicleStats?.cmw || totalsStats?.cmw || {};
    const analyticsVehicleType = statisticsReport?.vehicleType || 'Movable';
    const analyticsIsNonMovable = String(analyticsVehicleType).trim().toLowerCase() === 'non movable';
    const analyticsReadingUnit = statisticsReport?.derivedReadingUnit || (analyticsIsNonMovable ? 'hr' : 'km');
    const analyticsEndReading = statisticsReport?.derivedEndReading ?? (
      analyticsIsNonMovable ? vehicleStats.engineHoursMeterReading : periodMileageStats.mileageAtPeriodEnd
    );
    const engineOperationSeconds = totalsStats.totalWorked ?? vehicleStats.worked;
    const fuelRows = sortLatestRows(analyticsData.fuelEvents?.rows || [], ['eventDate', 'endDate', 'startDate']);
    const latestRefilling = fuelRows.find((row) => row.type === 1);
    const latestDraining = fuelRows.find((row) => row.type === 3);
    const eventRows = sortLatestRows(analyticsData.events?.rows || [], ['eventDate']);
    const latestEvents = eventRows.slice(0, 6);
    const tripReport = analyticsData.tripReport || {};
    const dailySummary = analyticsData.dailyReadingSummary || null;
    const dailyTotal = getDailyReadingSummaryTotal(dailySummary);
    const dailyMetricConfig = getDailyReadingSummaryMetricConfig(dailySummary);
    const analyticsRangeLabel = analyticsData.range?.label || getAnalyticsDashboardRangeLabel(analyticsRangePreset);
    const refreshedLabel = formatRefreshStatusLabel(analyticsData.refreshedAt, refreshStatusTick);
    const partialErrors = Object.values(analyticsData.errors || {}).filter(Boolean);
    const analyticsTrackPoints = analyticsData.track?.points || [];
    const analyticsTrackPointCount = analyticsTrackPoints.length;
    const latestTrackPoint = analyticsTrackPoints[analyticsTrackPoints.length - 1] || null;
    const analyticsCurrentPoint = [
      trackSummary.currentPoint,
      analyticsData.track?.lastKnownPosition,
      latestTrackPoint
    ].find((point) => Number.isFinite(point?.latitude) && Number.isFinite(point?.longitude));
    const currentLocationTimestamp = analyticsCurrentPoint?.timestamp
      || analyticsCurrentPoint?.eventDate
      || analyticsCurrentPoint?.rawTimestamp;
    let currentLocationDetails = 'No track returned for this period.';
    if (analyticsCurrentPoint) {
      currentLocationDetails = [
        analyticsTrackPointCount > 1 ? `${analyticsTrackPointCount} track points` : null,
        formatTrackTimestamp(currentLocationTimestamp),
        `${formatCoordinateLabel(analyticsCurrentPoint.latitude)}, ${formatCoordinateLabel(analyticsCurrentPoint.longitude)}`,
        Number.isFinite(analyticsCurrentPoint.fuelVolume) ? `Fuel ${formatFuelVolume(analyticsCurrentPoint.fuelVolume)}` : null
      ].filter(Boolean).join(' | ');
    } else if (analyticsTrackPointCount > 1) {
      currentLocationDetails = `${analyticsTrackPointCount} track points`;
    }
    const currentLocationSpeed = Number(analyticsCurrentPoint?.speed);
    let currentLocationStatus = 'No position';
    if (analyticsCurrentPoint) {
      currentLocationStatus = Number.isFinite(currentLocationSpeed) && currentLocationSpeed > 0
        ? `${currentLocationSpeed} km/h`
        : 'Stopped';
    }

    const statCards = [
      {
        label: analyticsIsNonMovable ? `Worked, ${analyticsReadingUnit}` : 'Mileage, km',
        value: formatDecimalValue(totalsStats.averageMileage ?? vehicleStats.mileage, 2),
        caption: `End reading: ${formatDecimalValue(analyticsEndReading, 2)} ${analyticsReadingUnit}`,
        color: '#2563EB',
        icon: SummarizeRoundedIcon
      },
      {
        label: 'Fuel consumption',
        value: formatTenthsValue(totalsStats.totalConsumption ?? fuelStats.fuelConsumption),
        caption: `Initial ${formatTenthsValue(fuelStats.startVolume)} | Final ${formatTenthsValue(fuelStats.endVolume)}`,
        color: '#16A34A',
        icon: LocalGasStationRoundedIcon
      },
      {
        label: 'Engine operation',
        value: formatHhMmSs(engineOperationSeconds),
        caption: `Off time ${formatHhMmSs(totalsStats.totalLayUpTime ?? vehicleStats.layUp)}`,
        color: '#F97316',
        icon: TimelineRoundedIcon
      },
      {
        label: 'Max speed',
        value: `${formatDecimalValue(totalsStats.maxSpeed ?? vehicleStats.maxSpeed, 1)} km/h`,
        caption: `Efficiency ${formatDecimalValue(totalsStats.totalMileagePerLitre ?? vehicleStats.mileagePerLitre, 2)} km/L`,
        color: '#0F172A',
        icon: DirectionsRoundedIcon
      }
    ];

    const renderFuelMomentCard = (title, row, color) => {
      if (!row) return null;
      return (
        <Card sx={{
          p: 1.3,
          borderRadius: 2,
          border: `1px solid ${alpha(color, 0.18)}`,
          boxShadow: 'none',
          bgcolor: alpha(color, 0.055),
          minHeight: 132,
          overflow: 'hidden',
          position: 'relative',
          '&:before': {
            content: '""',
            position: 'absolute',
            left: -32,
            bottom: 18,
            width: 86,
            height: 86,
            borderRadius: '50%',
            bgcolor: alpha(color, 0.11),
            animation: 'fuelPulse 1800ms ease-in-out infinite'
          },
          '@keyframes fuelPulse': {
            '0%, 100%': { transform: 'scale(0.9)', opacity: 0.5 },
            '50%': { transform: 'scale(1.08)', opacity: 0.9 }
          }
        }}
        >
          <Stack spacing={0.65} sx={{ position: 'relative' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Typography variant="caption" sx={{ color: '#696969', fontWeight: 900 }}>{title}</Typography>
              <Chip size="small" label={row.typeLabel} sx={{ bgcolor: alpha(color, 0.12), color, fontWeight: 800 }} />
            </Stack>
            <Typography variant="h6" sx={{ color: '#1D1D1D', fontWeight: 900 }}>
              {formatFuelVolume(row.volume)}
            </Typography>
            <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700 }}>
              {formatTrackTimestamp(row.eventDate)}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#64748B', lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
              }}
            >
              {row.address}
            </Typography>
          </Stack>
        </Card>
      );
    };

    const summaryMetricLabel = dailySummary ? dailyMetricConfig.label : 'Reading total';

    return (
      <Stack spacing={1.35}>
        <Card sx={{
          p: 1.25, borderRadius: 2, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none', bgcolor: '#F8FAFC'
        }}
        >
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', lg: 'center' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ color: '#1D1D1D', fontWeight: 900 }}>
                {analyticsData.vehicle?.registrationNumber || selectedVehicle.plate}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block', mt: 0.35, color: '#696969', fontWeight: 700
                }}
              >
                {analyticsRangeLabel}
                {' | '}
                {refreshedLabel}
                {isAnalyticsRefreshing ? ' | Refreshing' : ''}
              </Typography>
            </Box>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.7 }}>
              {ANALYTICS_DASHBOARD_RANGE_OPTIONS.map((option) => {
                const isSelected = option.value === analyticsRangePreset;
                return (
                  <Chip
                    key={option.value}
                    label={option.label}
                    clickable
                    onClick={() => {
                      primeReportLoading('analytics');
                      if (option.value !== analyticsRangePreset) setAnalyticsData(null);
                      setAnalyticsRangePreset(option.value);
                      setAnalyticsRefreshToken(0);
                    }}
                    variant={isSelected ? 'filled' : 'outlined'}
                    sx={{
                      fontWeight: 800,
                      borderRadius: 999,
                      color: isSelected ? '#FFFFFF' : '#0F172A',
                      bgcolor: isSelected ? '#0F172A' : '#FFFFFF',
                      borderColor: alpha('#0F172A', 0.26),
                      '&:hover': { bgcolor: isSelected ? '#1E293B' : '#F8FAFC' }
                    }}
                  />
                );
              })}
            </Stack>
          </Stack>
          {partialErrors.length > 0 && (
            <Typography
              variant="caption"
              sx={{
                display: 'block', mt: 0.85, color: '#B45309', fontWeight: 700
              }}
            >
              {partialErrors.join(' ')}
            </Typography>
          )}
        </Card>

        <Grid container spacing={1}>
          {statCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <Grid item xs={12} sm={6} xl={3} key={card.label}>
                <Card sx={{
                  p: 1.45,
                  borderRadius: 2,
                  border: `1px solid ${alpha(card.color, 0.16)}`,
                  boxShadow: 'none',
                  minHeight: 126,
                  bgcolor: '#FFFFFF',
                  backgroundImage: `linear-gradient(135deg, ${alpha(card.color, 0.08)} 0%, rgba(255,255,255,0.96) 68%)`,
                  animation: 'analyticsStatEnter 420ms ease both',
                  animationDelay: `${index * 70}ms`,
                  '@keyframes analyticsStatEnter': {
                    '0%': { opacity: 0, transform: 'translateY(10px)' },
                    '100%': { opacity: 1, transform: 'translateY(0)' }
                  }
                }}
                >
                  <Stack direction="row" justifyContent="space-between" spacing={1.2}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: '#696969', fontWeight: 800 }}>{card.label}</Typography>
                      <Typography
                        variant="h5"
                        sx={{
                          mt: 0.6, color: '#0F172A', fontWeight: 900, lineHeight: 1.18, letterSpacing: 0
                        }}
                      >
                        {card.value}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block', mt: 0.8, color: '#475569', fontWeight: 700, lineHeight: 1.3
                        }}
                      >
                        {card.caption}
                      </Typography>
                    </Box>
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 38, height: 38, borderRadius: 2, bgcolor: alpha(card.color, 0.12), color: card.color
                      }}
                    >
                      <Icon fontSize="small" />
                    </Avatar>
                  </Stack>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Grid container spacing={1.25}>
          <Grid item xs={12} xl={7}>
            <Card sx={{
              borderRadius: 2, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none', overflow: 'hidden', bgcolor: '#FFFFFF'
            }}
            >
              <Box sx={{ p: 1.25, borderBottom: '1px solid rgba(148, 163, 184, 0.14)' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#1D1D1D', fontWeight: 900 }}>Track</Typography>
                    <Typography variant="caption" sx={{ color: '#696969', fontWeight: 700 }}>
                      {currentLocationDetails}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={currentLocationStatus}
                    sx={{ bgcolor: alpha('#2563EB', 0.1), color: '#1D4ED8', fontWeight: 800 }}
                  />
                </Stack>
              </Box>
              <Box sx={{ height: { xs: 330, lg: 410 }, position: 'relative', bgcolor: '#F8FAFC' }}>
                <Box ref={trackMapContainerRef} sx={TRACK_MAP_CONTAINER_STYLE} />
                {(!googleMapsKey || isMapKeyLoading || isMapScriptLoading) && (
                  <Box sx={{
                    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(248, 250, 252, 0.84)'
                  }}
                  >
                    <Stack spacing={1} alignItems="center" sx={{ width: 240 }}>
                      <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 800 }}>Loading tracking map...</Typography>
                      <LinearProgress sx={{ width: '100%', height: 8, borderRadius: 999 }} />
                    </Stack>
                  </Box>
                )}
                {!analyticsCurrentPoint && !analyticsTrackPointCount && !isMapKeyLoading && !isMapScriptLoading && (
                  <Box sx={{
                    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, bgcolor: 'rgba(248,250,252,0.82)'
                  }}
                  >
                    <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 800, textAlign: 'center' }}>
                      No track returned for this period.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} xl={5}>
            <Card sx={{
              p: 1.3, borderRadius: 2, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none', height: '100%', bgcolor: '#FFFFFF'
            }}
            >
              <Stack spacing={1.05}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Typography variant="subtitle2" sx={{ color: '#1D1D1D', fontWeight: 900 }}>Latest events</Typography>
                  <Chip size="small" label={`${analyticsData.events?.totalRecords || latestEvents.length} total`} sx={{ bgcolor: alpha('#2563EB', 0.1), color: '#1D4ED8', fontWeight: 800 }} />
                </Stack>
                {latestEvents.length ? latestEvents.map((row, index) => (
                  <Box
                    key={row.id}
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      border: '1px solid rgba(148, 163, 184, 0.14)',
                      bgcolor: index === 0 ? '#EFF6FF' : '#FFFFFF',
                      animation: 'eventSlideIn 360ms ease both',
                      animationDelay: `${index * 55}ms`,
                      '@keyframes eventSlideIn': {
                        '0%': { opacity: 0, transform: 'translateX(8px)' },
                        '100%': { opacity: 1, transform: 'translateX(0)' }
                      }
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ color: '#1D1D1D', fontWeight: 850 }}>
                          {row.eventTypeLabel}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#64748B', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                          }}
                        >
                          {row.parameterText || row.address}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: '#696969', fontWeight: 800, whiteSpace: 'nowrap' }}>
                        {row.dateTime}
                      </Typography>
                    </Stack>
                  </Box>
                )) : (
                  <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 700 }}>No events returned for this period.</Typography>
                )}
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {(latestRefilling || latestDraining) && (
          <Grid container spacing={1.25}>
            {latestRefilling && (
              <Grid item xs={12} md={6}>
                {renderFuelMomentCard('Latest refilling', latestRefilling, '#16A34A')}
              </Grid>
            )}
            {latestDraining && (
              <Grid item xs={12} md={6}>
                {renderFuelMomentCard('Latest draining', latestDraining, '#DC2626')}
              </Grid>
            )}
          </Grid>
        )}

        <Grid container spacing={1.25}>
          <Grid item xs={12} xl={6}>
            <Card sx={{
              p: 1.35, borderRadius: 2, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none', minHeight: 218, bgcolor: '#FFFFFF'
            }}
            >
              <Stack spacing={1.2}>
                <Typography variant="subtitle2" sx={{ color: '#1D1D1D', fontWeight: 900 }}>Trip report summary</Typography>
                <Grid container spacing={0.8}>
                  {[
                    ['Routes', tripReport.totalRoutes || 0],
                    ['Trips', tripReport.totalRuns || 0],
                    ['Violations', tripReport.totalViolatedRuns || 0],
                    ['Mileage', `${formatTripMetricValue(sumTripMetric(tripReport, 'totalMileage'), 2)} km`]
                  ].map(([label, value]) => (
                    <Grid item xs={6} key={label}>
                      <Box sx={{
                        p: 1,
                        borderRadius: 1.5,
                        bgcolor: label === 'Violations' ? alpha('#DC2626', 0.08) : '#F8FAFC',
                        border: '1px solid rgba(148, 163, 184, 0.12)'
                      }}
                      >
                        <Typography variant="caption" sx={{ color: '#696969', fontWeight: 800 }}>{label}</Typography>
                        <Typography variant="h6" sx={{ mt: 0.4, color: '#0F172A', fontWeight: 900 }}>{value}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <Box sx={{
                  height: 5,
                  borderRadius: 999,
                  bgcolor: alpha('#2563EB', 0.12),
                  overflow: 'hidden',
                  position: 'relative',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    width: tripReport.totalRuns ? '62%' : '18%',
                    borderRadius: 999,
                    bgcolor: '#2563EB',
                    animation: 'tripProgress 1700ms ease-in-out infinite'
                  },
                  '@keyframes tripProgress': {
                    '0%, 100%': { opacity: 0.6, transform: 'translateX(-10%)' },
                    '50%': { opacity: 1, transform: 'translateX(18%)' }
                  }
                }}
                />
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} xl={6}>
            <Card sx={{
              p: 1.35, borderRadius: 2, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none', minHeight: 218, bgcolor: '#0F172A', color: '#FFFFFF'
            }}
            >
              <Stack spacing={1.2}>
                <Typography variant="subtitle2" sx={{ color: '#FFFFFF', fontWeight: 900 }}>Consolidated summary</Typography>
                <Grid container spacing={0.8}>
                  {[
                    ['Readings', dailySummary?.dailyReadings?.length || 0],
                    ['Start', formatTripMetricValue(dailySummary?.rangeSummary?.startReading, 1)],
                    ['End', formatTripMetricValue(dailySummary?.rangeSummary?.endReading, 1)],
                    [summaryMetricLabel, formatTripMetricValue(dailyTotal, 1)]
                  ].map(([label, value]) => (
                    <Grid item xs={6} key={label}>
                      <Box sx={{
                        p: 1,
                        borderRadius: 1.5,
                        bgcolor: alpha('#FFFFFF', 0.1),
                        border: `1px solid ${alpha('#FFFFFF', 0.14)}`,
                        animation: 'summaryCountIn 520ms ease both',
                        '@keyframes summaryCountIn': {
                          '0%': { opacity: 0, transform: 'scale(0.96)' },
                          '100%': { opacity: 1, transform: 'scale(1)' }
                        }
                      }}
                      >
                        <Typography variant="caption" sx={{ color: '#CBD5E1', fontWeight: 800 }}>{label}</Typography>
                        <Typography variant="h6" sx={{ mt: 0.4, color: '#FFFFFF', fontWeight: 900 }}>{value}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            </Card>
          </Grid>
        </Grid>
      </Stack>
    );
  };

  const renderLocationReport = () => {
    const invalidCoordinatesCount = locationSelectedVehicles.length - locationMapMarkers.length;

    const renderLoadingState = () => (
      <Stack spacing={1.2}>
        <Typography variant="body2" sx={{ color: '#475569', fontWeight: 700 }}>Loading map...</Typography>
        <LinearProgress sx={{ height: 8, borderRadius: 999 }} />
      </Stack>
    );

    const renderMapBody = () => {
      if (isMapKeyLoading) return renderLoadingState();
      if (mapError) {
        return (
          <Stack spacing={1.1} alignItems="flex-start">
            <Typography variant="body2" sx={{ color: '#B91C1C', fontWeight: 700 }}>{mapError}</Typography>
            <Button variant="contained" size="small" onClick={handleRetryLocationMap}>Retry map load</Button>
          </Stack>
        );
      }
      if (!googleMapsKey) return renderLoadingState();

      // FIX: Cost Optimization - Keep Map container mounted even if there are no selections
      return (
        <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
          <Box ref={locationMapContainerRef} sx={LOCATION_MAP_CONTAINER_STYLE} />

          {!locationMapMarkers.length && (
            <Box sx={{
              position: 'absolute', inset: 0, bgcolor: 'rgba(248, 250, 252, 0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
            >
              <Typography variant="body2" sx={{ color: '#475569', fontWeight: 700 }}>Select a vehicle to view its location on the map.</Typography>
            </Box>
          )}

          {isMapScriptLoading && (
            <Box sx={{
              position: 'absolute', inset: 0, bgcolor: 'rgba(248, 250, 252, 0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none'
            }}
            >
              <Stack spacing={1} alignItems="center" sx={{ minWidth: 220 }}>
                <Typography variant="body2" sx={{ color: '#475569', fontWeight: 700 }}>Loading map...</Typography>
                <LinearProgress sx={{ width: '100%', height: 7, borderRadius: 999 }} />
              </Stack>
            </Box>
          )}
        </Box>
      );
    };

    return (
      <Grid container spacing={1.5}>
        <Grid item xs={12}>
          <Card sx={{
            p: 1.5, borderRadius: 3.25, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none'
          }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ color: '#0F172A', fontWeight: 800 }}>Live Location Map</Typography>
              <Stack direction="row" spacing={0.7}>
                <Chip size="small" label={`${locationMapMarkers.length} mapped`} sx={{ bgcolor: alpha('#2563EB', 0.1), color: '#1E40AF', fontWeight: 700 }} />
                {invalidCoordinatesCount > 0 && <Chip size="small" label={`${invalidCoordinatesCount} missing`} sx={{ bgcolor: alpha('#D97706', 0.12), color: '#92400E', fontWeight: 700 }} />}
              </Stack>
            </Stack>
            <Box sx={{
              height: { xs: 420, md: 520 }, borderRadius: 2.5, border: '1px solid rgba(148, 163, 184, 0.18)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F8FAFC'
            }}
            >
              {renderMapBody()}
            </Box>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderStatisticsReport = () => {
    const fromMoment = moment(dateRange.fromDateTime, DATE_TIME_INPUT_FORMAT, true);
    const toMoment = moment(dateRange.toDateTime, DATE_TIME_INPUT_FORMAT, true);
    const selectedRangeLabel = fromMoment.isValid() && toMoment.isValid()
      ? `${fromMoment.format('DD MMM YYYY, hh:mm A')} to ${toMoment.format('DD MMM YYYY, hh:mm A')}`
      : 'Custom range';
    const isStatisticsAwaitingResponse = shouldShowFirstLoadSkeleton({
      isLoading: isStatisticsLoading,
      error: statisticsError,
      data: statisticsData
    });

    if (!isFleetDateRangeValid) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Invalid date range</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            {dateRangeValidationMessage}
            {' '}
            Then load statistics.
          </Typography>
        </Card>
      );
    }

    if (isStatisticsLoading || isStatisticsAwaitingResponse) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}><Skeleton variant="rounded" height={190} sx={{ borderRadius: 3 }} /></Grid>
        </Grid>
      );
    }

    if (statisticsError) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Statistics unavailable</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>{statisticsError}</Typography>
        </Card>
      );
    }

    if (!statisticsData) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>No statistics found</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            No records were returned for
            {' '}
            {selectedVehicle?.plate}
            {' '}
            in the selected range.
          </Typography>
        </Card>
      );
    }

    const vehicleStats = statisticsData?.vehicleData || {};
    const fuelStats = vehicleStats?.fuel || vehicleStats;
    const totalsStats = statisticsData?.totals || {};
    const periodMileageStats = statisticsData?.periodMileage || vehicleStats?.cmw || totalsStats?.cmw || {};
    const vehicleType = statisticsData?.vehicleType || 'Movable';
    const isNonMovableVehicle = String(vehicleType).trim().toLowerCase() === 'non movable';
    const derivedEndReading = statisticsData?.derivedEndReading;
    const derivedReadingUnit = statisticsData?.derivedReadingUnit || (isNonMovableVehicle ? 'hr' : 'km');
    const engineOperationSeconds = totalsStats.totalWorked ?? vehicleStats.worked;
    const engineOffSeconds = totalsStats.totalLayUpTime ?? vehicleStats.layUp;
    const engineOperationPercent = resolveStatisticsDurationPercent({
      seconds: engineOperationSeconds,
      rawPercent: totalsStats.totalWorkedPercent ?? vehicleStats.workedPercent,
      fromDateTime: dateRange.fromDateTime,
      toDateTime: dateRange.toDateTime
    });
    const engineOffPercent = resolveStatisticsDurationPercent({
      seconds: engineOffSeconds,
      rawPercent: totalsStats.totalLayUpTimePercent ?? vehicleStats.layUpPercent,
      fromDateTime: dateRange.fromDateTime,
      toDateTime: dateRange.toDateTime
    });
    const secondaryEngineStats = Array.isArray(vehicleStats?.uniDataList)
      ? vehicleStats.uniDataList[0]
      : null;

    const renderStatisticsSection = (title, rows) => (
      <Card sx={{
        p: 1.45, borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none', height: '100%'
      }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0F172A', mb: 1 }}>
          {title}
        </Typography>
        <Grid container spacing={0.8}>
          {rows.map(([label, value]) => (
            <Grid item xs={12} sm={6} key={`${title}-${label}`}>
              <Box sx={{
                p: 0.85, borderRadius: 1.8, border: '1px solid rgba(148, 163, 184, 0.14)', bgcolor: '#FFFFFF', minHeight: 74
              }}
              >
                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700, lineHeight: 1.28 }}>
                  {label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.45, color: '#0F172A', fontWeight: 800, lineHeight: 1.32
                  }}
                >
                  {value}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Card>
    );

    const movementAndOperationRows = [
      ['Mileage, km', formatDecimalValue(totalsStats.averageMileage ?? vehicleStats.mileage, 2)],
      ['Total mileage at the beginning of the period, km', formatDecimalValue(periodMileageStats.mileageAtPeriodBegin, 2)],
      [
        isNonMovableVehicle
          ? `Engine hours meter reading, ${derivedReadingUnit}`
          : 'Total mileage at the end of the period, km',
        formatDecimalValue(isNonMovableVehicle ? (derivedEndReading ?? vehicleStats.engineHoursMeterReading) : (derivedEndReading ?? periodMileageStats.mileageAtPeriodEnd), 2)
      ],
      ['Engine operation time, hh:mm:ss (% of report period)', formatDurationWithPercent(engineOperationSeconds, engineOperationPercent)],
      ['Engine off time, hh:mm:ss (% of report period)', formatDurationWithPercent(engineOffSeconds, engineOffPercent)],
      ['Maximum speed, km/h', formatDecimalValue(totalsStats.maxSpeed ?? vehicleStats.maxSpeed, 1)]
    ];

    const mainTankRows = [
      ['Initial volume, l', formatTenthsValue(fuelStats.startVolume)],
      ['Refueling volume, l', formatTenthsValue(totalsStats.totalRefuelling ?? fuelStats.refuelling)],
      ['Actual mileage per 1 l, km', formatDecimalValue(totalsStats.totalMileagePerLitre ?? vehicleStats.mileagePerLitre, 2)],
      ['Final volume, l', formatTenthsValue(fuelStats.endVolume)],
      ['Draining volume, l', formatTenthsValue(totalsStats.totalDraining ?? fuelStats.draining)],
      ['Fuel consumption per engine hour, l', formatTenthsValue(totalsStats.consPerMHEngineOn ?? vehicleStats.fuelConsPerMotorhour)],
      ['Actual consumption, l', formatTenthsValue(totalsStats.totalConsumption ?? fuelStats.fuelConsumption)]
    ];

    const secondaryEngineRows = [
      ['Operation time, hh:mm:ss', formatDurationWithPercent(secondaryEngineStats?.univInputOnTime, secondaryEngineStats?.univInputOnTimePercent)],
      ['Consumption with auxiliary equipment ON, l', formatTenthsValue(secondaryEngineStats?.univInputOnConsumption)],
      ['Consumption with auxiliary equipment ON per engine hour, l', formatTenthsValue(secondaryEngineStats?.univInputHourConsumption)],
      ['Downtime, hh:mm:ss', formatDurationWithPercent(secondaryEngineStats?.univInputOffTime, secondaryEngineStats?.univInputOffTimePercent)]
    ];

    return (
      <Stack spacing={1.25}>
        <Card sx={{
          p: 1.2, borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.18)', boxShadow: 'none', bgcolor: '#F8FAFC'
        }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={0.7}
            justifyContent="space-between"
            alignItems={{ md: 'center' }}
          >
            <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 800 }}>
              <Box
                component="button"
                type="button"
                onClick={handleStatisticsVehicleClick}
                sx={{
                  p: 0,
                  m: 0,
                  border: 0,
                  background: 'transparent',
                  font: 'inherit',
                  color: '#111111',
                  fontWeight: 800,
                  cursor: 'pointer',
                  textDecoration: 'none',
                  transition: 'color 150ms ease, text-decoration-color 150ms ease',
                  textUnderlineOffset: '3px',
                  textDecorationColor: 'transparent',
                  '&:hover': {
                    color: '#171717',
                    textDecoration: 'underline',
                    textDecorationColor: alpha('#171717', 0.55)
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${alpha('#0070F3', 0.45)}`,
                    outlineOffset: 2,
                    borderRadius: 1
                  }
                }}
              >
                {statisticsData?.vehicleData?.name || selectedVehicle?.plate}
              </Box>
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
              {selectedRangeLabel}
            </Typography>
          </Stack>
          {vehicleDetailsLinkError && (
            <Typography variant="caption" sx={{ color: '#B91C1C', fontWeight: 700 }}>
              {vehicleDetailsLinkError}
            </Typography>
          )}
        </Card>

        <Grid container spacing={1}>
          <Grid item xs={12} lg={6}>
            {renderStatisticsSection('Movement and operation', movementAndOperationRows)}
          </Grid>
          <Grid item xs={12} lg={6}>
            {renderStatisticsSection('Fuel (main tank)', mainTankRows)}
          </Grid>
          <Grid item xs={12}>
            {renderStatisticsSection('Activity Secondary Engine', secondaryEngineRows)}
          </Grid>
        </Grid>
      </Stack>
    );
  };

  const renderFuelReport = () => {
    const fromMoment = moment(dateRange.fromDateTime, DATE_TIME_INPUT_FORMAT, true);
    const toMoment = moment(dateRange.toDateTime, DATE_TIME_INPUT_FORMAT, true);
    const selectedRangeLabel = fromMoment.isValid() && toMoment.isValid()
      ? `${fromMoment.format('DD MMM YYYY, hh:mm A')} to ${toMoment.format('DD MMM YYYY, hh:mm A')}`
      : 'Custom range';
    const isFuelAwaitingResponse = shouldShowFirstLoadSkeleton({
      isLoading: isFuelEventsLoading,
      error: fuelEventsError,
      data: fuelEventsData
    });

    if (!selectedVehicle?.plate) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Choose a vehicle</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            Select a vehicle to load refilling and draining events.
          </Typography>
        </Card>
      );
    }

    if (!isFleetDateRangeValid) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Invalid date range</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            {dateRangeValidationMessage}
            {' '}
            Then load fuel events.
          </Typography>
        </Card>
      );
    }

    if (isFuelEventsLoading || isFuelAwaitingResponse) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}><Skeleton variant="rounded" height={190} sx={{ borderRadius: 3 }} /></Grid>
          <Grid item xs={12}><Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} /></Grid>
        </Grid>
      );
    }

    if (fuelEventsError) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Fuel events unavailable</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>{fuelEventsError}</Typography>
        </Card>
      );
    }

    const fuelRows = fuelEventsData?.rows || [];
    if (!fuelRows.length) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>No fuel events found</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            No records were returned for
            {' '}
            {selectedVehicle.plate}
            {' '}
            in the selected range.
          </Typography>
        </Card>
      );
    }

    const refillingRows = fuelRows.filter((row) => row.type === 1);
    const drainingRows = fuelRows.filter((row) => row.type === 3);
    const totalRefillingVolume = refillingRows.reduce((total, row) => total + (Number(row.volume) || 0), 0);
    const totalDrainingVolume = drainingRows.reduce((total, row) => total + (Number(row.volume) || 0), 0);
    const selectedFuelEventTitle = selectedFuelEventMapRow?.typeLabel || 'Fuel event';
    const selectedFuelEventCoordinates = selectedFuelEventMapRow
      ? `${formatCoordinateLabel(selectedFuelEventMapRow.latitude)}, ${formatCoordinateLabel(selectedFuelEventMapRow.longitude)}`
      : '--';
    const handleFuelReportExport = () => {
      exportFleetWorkbook({
        sheetName: 'Fuel Events',
        fileNamePrefix: 'fuel_events',
        vehicleLabel: selectedVehicle.plate,
        rangeLabel: selectedRangeLabel,
        headers: ['Event time', 'Type', 'Volume (L)', 'Tank', 'Duration', 'Address'],
        rows: fuelRows.map((row) => ([
          formatTrackTimestamp(row.eventDate),
          row.typeLabel,
          Number.isFinite(Number(row.volume)) ? Number(row.volume) : '',
          row.tankNumber,
          row.durationMinutes !== null ? formatDuration(row.durationMinutes) : '--',
          row.address
        ]))
      });
    };

    return (
      <Stack spacing={1.25}>
        <Card sx={{
          p: 1.2, borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.18)', boxShadow: 'none', bgcolor: '#F8FAFC'
        }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={0.7}
            justifyContent="space-between"
            alignItems={{ md: 'center' }}
          >
            <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 800 }}>
              {selectedVehicle.plate}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
                {selectedRangeLabel}
              </Typography>
              <Button
                variant="contained"
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={handleFuelReportExport}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: 40,
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                Export
              </Button>
            </Stack>
          </Stack>
        </Card>

        <Grid container spacing={1}>
          <Grid item xs={12} md={4}>
            <DetailMetricCard
              label="Total events"
              value={`${fuelRows.length}`}
              caption={`${fuelEventsData?.totalRecords || fuelRows.length} records returned`}
              icon={LocalGasStationRoundedIcon}
              color="#EA580C"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DetailMetricCard
              label="Refilling"
              value={formatFuelVolume(totalRefillingVolume)}
              caption={`${refillingRows.length} events`}
              icon={LocalGasStationRoundedIcon}
              color="#16A34A"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DetailMetricCard
              label="Draining"
              value={formatFuelVolume(totalDrainingVolume)}
              caption={`${drainingRows.length} events`}
              icon={LocalGasStationRoundedIcon}
              color="#DC2626"
            />
          </Grid>
        </Grid>

        {selectedFuelEventMapRow?.sourceReport === 'fuel' && (
          <Card sx={{
            p: 1.1,
            borderRadius: 3,
            border: '1px solid rgba(148, 163, 184, 0.16)',
            boxShadow: 'none',
            overflow: 'hidden'
          }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 0.9 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 800 }}>
                  Event location map
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                  {selectedFuelEventTitle}
                  {' | '}
                  {formatTrackTimestamp(selectedFuelEventMapRow.eventDate)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                  {selectedFuelEventCoordinates}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setSelectedFuelEventMapRow(null);
                  setFuelEventMapError('');
                  clearFuelEventMapArtifacts();
                }}
              >
                Close
              </Button>
            </Stack>

            <Box sx={{
              height: { xs: 250, md: 290 },
              borderRadius: 2.2,
              border: '1px solid rgba(148, 163, 184, 0.18)',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#F8FAFC',
              position: 'relative'
            }}
            >
              <Box ref={fuelEventMapContainerRef} sx={LOCATION_MAP_CONTAINER_STYLE} />

              {(isMapKeyLoading || isMapScriptLoading || !googleMapsKey) && (
                <Box sx={{
                  position: 'absolute',
                  inset: 0,
                  bgcolor: 'rgba(248, 250, 252, 0.82)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none'
                }}
                >
                  <Stack spacing={1} alignItems="center" sx={{ minWidth: 220 }}>
                    <Typography variant="body2" sx={{ color: '#475569', fontWeight: 700 }}>Loading map...</Typography>
                    <LinearProgress sx={{ width: '100%', height: 7, borderRadius: 999 }} />
                  </Stack>
                </Box>
              )}

              {(fuelEventMapError || mapError) && (
                <Box sx={{
                  position: 'absolute',
                  inset: 0,
                  bgcolor: 'rgba(248, 250, 252, 0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 1.2
                }}
                >
                  <Typography variant="body2" sx={{ color: '#B91C1C', fontWeight: 700, textAlign: 'center' }}>
                    {fuelEventMapError || mapError}
                  </Typography>
                </Box>
              )}
            </Box>
          </Card>
        )}

        <Card sx={{
          borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none', overflow: 'hidden'
        }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                  {['Event time', 'Type', 'Volume', 'Tank', 'Duration', 'Address', 'Map'].map((label) => (
                    <TableCell
                      key={label}
                      sx={{
                        py: 1.1, px: 1.4, color: '#475569', fontWeight: 800, borderBottom: '1px solid rgba(148, 163, 184, 0.16)'
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {fuelRows.map((row) => {
                  const isRefilling = row.type === 1;
                  const isDraining = row.type === 3;
                  const hasCoordinates = Number.isFinite(Number(row.latitude)) && Number.isFinite(Number(row.longitude));
                  let chipColor = '#475569';
                  let chipBackground = alpha('#64748B', 0.12);

                  if (isRefilling) {
                    chipColor = '#166534';
                    chipBackground = alpha('#16A34A', 0.12);
                  } else if (isDraining) {
                    chipColor = '#991B1B';
                    chipBackground = alpha('#DC2626', 0.12);
                  }

                  return (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ py: 1.15, px: 1.4, verticalAlign: 'top' }}>
                        <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 700 }}>
                          {formatTrackTimestamp(row.eventDate)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748B' }}>
                          {row.vehicleName}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.15, px: 1.4, verticalAlign: 'top' }}>
                        <Chip
                          size="small"
                          label={row.typeLabel}
                          sx={{ bgcolor: chipBackground, color: chipColor, fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1.15, px: 1.4, verticalAlign: 'top' }}>
                        <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 800 }}>
                          {formatFuelVolume(row.volume)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.15, px: 1.4, verticalAlign: 'top' }}>
                        <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 700 }}>
                          Tank
                          {' '}
                          {row.tankNumber}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.15, px: 1.4, verticalAlign: 'top' }}>
                        <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 700 }}>
                          {row.durationMinutes !== null ? formatDuration(row.durationMinutes) : '--'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748B' }}>
                          Start:
                          {' '}
                          {formatTrackTimestamp(row.startDate)}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.15,
                          px: 1.4,
                          verticalAlign: 'top',
                          maxWidth: 420
                        }}
                      >
                        <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.45 }}>
                          {row.address}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.15,
                          px: 1.4,
                          verticalAlign: 'top',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {hasCoordinates ? (
                          <Button
                            size="small"
                            variant={selectedFuelEventMapRow?.id === row.id ? 'contained' : 'outlined'}
                            color="primary"
                            startIcon={<PlaceRoundedIcon fontSize="small" />}
                            onClick={() => {
                              setFuelEventMapError('');
                              setSelectedFuelEventMapRow({
                                ...row,
                                latitude: Number(row.latitude),
                                longitude: Number(row.longitude)
                              });
                            }}
                            sx={{ textTransform: 'none', fontWeight: 700 }}
                          >
                            Locate
                          </Button>
                        ) : (
                          <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700 }}>
                            --
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Stack>
    );
  };

  const renderTripReport = () => {
    const fromMoment = moment(dateRange.fromDateTime, DATE_TIME_INPUT_FORMAT, true);
    const toMoment = moment(dateRange.toDateTime, DATE_TIME_INPUT_FORMAT, true);
    const selectedRangeLabel = fromMoment.isValid() && toMoment.isValid()
      ? `${fromMoment.format('DD MMM YYYY, hh:mm A')} to ${toMoment.format('DD MMM YYYY, hh:mm A')}`
      : 'Custom range';
    const isTripReportAwaitingResponse = shouldShowFirstLoadSkeleton({
      isLoading: isTripReportLoading,
      error: tripReportError,
      data: tripReportData
    });

    if (!selectedVehicle?.plate) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Choose a vehicle</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            Select a vehicle to load trip report details.
          </Typography>
        </Card>
      );
    }

    if (!isFleetDateRangeValid) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Invalid date range</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            {dateRangeValidationMessage}
            {' '}
            Then load trip report.
          </Typography>
        </Card>
      );
    }

    if (isTripReportLoading || isTripReportAwaitingResponse) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}><Skeleton variant="rounded" height={170} sx={{ borderRadius: 3 }} /></Grid>
          <Grid item xs={12}><Skeleton variant="rounded" height={420} sx={{ borderRadius: 3 }} /></Grid>
        </Grid>
      );
    }

    if (tripReportError) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Trip report unavailable</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>{tripReportError}</Typography>
        </Card>
      );
    }

    if (!tripReportData?.totalRuns) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>No trip records found</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            No trip report rows were returned for
            {' '}
            {selectedVehicle.plate}
            {' '}
            in the selected range.
          </Typography>
        </Card>
      );
    }

    const tableRows = [];
    (tripReportData?.vehicles || []).forEach((vehicle) => {
      const vehicleRuns = vehicle.routes.reduce((totalRuns, route) => totalRuns + route.runs.length, 0);

      vehicle.routes.forEach((route) => {
        route.runs.forEach((run) => {
          const controlPoints = run.controlPoints.length ? run.controlPoints : [null];
          controlPoints.forEach((controlPoint, controlPointIndex) => {
            tableRows.push({
              id: `trip-row-${vehicle.id}-${route.id}-${run.id}-${controlPoint?.id || controlPointIndex}`,
              rowType: 'controlPoint',
              vehicleName: vehicle.name,
              routeName: route.name,
              tripStart: run.actualStartTime,
              tripEnd: run.actualFinishTime,
              statusLabel: run.statusLabel,
              statusColor: run.statusColor,
              statusBackground: run.statusBackground,
              pointNumber: controlPoint?.number || '--',
              controlPointName: controlPoint?.name || '--',
              visitTime: controlPoint?.visitTime ?? null,
              violationText: controlPoint?.violationText || '--',
              mileage: controlPoint?.mileage,
              fuelConsumption: controlPoint?.fuelConsumption,
              fuelVolume: controlPoint?.fuelVolume,
              refuelingVolume: controlPoint?.refuelingVolume,
              drainingVolume: controlPoint?.drainingVolume,
              mileagePerLitre: controlPoint?.mileagePerLitre
            });
          });

          tableRows.push({
            id: `trip-run-summary-${vehicle.id}-${route.id}-${run.id}`,
            rowType: 'runSummary',
            summaryText: getTripRunSummaryNote(run.summary),
            mileage: run.summary?.totalMileage,
            fuelConsumption: run.summary?.fuelConsumption,
            fuelVolume: run.summary?.fuelVolume,
            refuelingVolume: run.summary?.refuelVolume,
            drainingVolume: run.summary?.dischargeVolume,
            mileagePerLitre: run.summary?.mileagePerLitre
          });
        });

        const routeTotalRuns = Number(route.summary?.totalRuns) || route.runs.length;
        const routeViolatedRuns = Number(route.summary?.violatedRuns) || 0;
        tableRows.push({
          id: `trip-route-summary-${vehicle.id}-${route.id}`,
          rowType: 'routeSummary',
          summaryText: `Route total: ${route.name} | Trips: ${routeTotalRuns} | Trips with violations: ${routeViolatedRuns}`,
          mileage: route.summary?.totalMileage,
          fuelConsumption: route.summary?.fuelConsumption,
          fuelVolume: route.summary?.fuelVolume,
          refuelingVolume: route.summary?.refuelVolume,
          drainingVolume: route.summary?.dischargeVolume,
          mileagePerLitre: route.summary?.mileagePerLitre
        });
      });

      tableRows.push({
        id: `trip-vehicle-summary-${vehicle.id}`,
        rowType: 'vehicleSummary',
        summaryText: `Vehicle total: ${vehicle.name} | Routes: ${vehicle.routes.length} | Trips: ${vehicleRuns} | Trips with violations: ${Number(vehicle.summary?.violatedRuns) || 0}`,
        mileage: vehicle.summary?.totalMileage,
        fuelConsumption: vehicle.summary?.fuelConsumption,
        fuelVolume: vehicle.summary?.fuelVolume,
        refuelingVolume: vehicle.summary?.refuelVolume,
        drainingVolume: vehicle.summary?.dischargeVolume,
        mileagePerLitre: vehicle.summary?.mileagePerLitre
      });
    });
    const handleTripReportExport = () => {
      exportFleetWorkbook({
        sheetName: 'Trip Report',
        fileNamePrefix: 'trip_report',
        vehicleLabel: selectedVehicle.plate,
        rangeLabel: selectedRangeLabel,
        headers: [
          'Vehicle',
          'Route',
          'Start of trip',
          'End of trip',
          'Status',
          'No.',
          'Control point',
          'Time of visiting',
          'Violations',
          'Mileage, km',
          'Fuel consumption, l',
          'Fuel volume, l',
          'Refueling volume, l',
          'Draining volume, l',
          'Mileage per 1 l, km'
        ],
        rows: tableRows.map((row) => {
          if (row.rowType !== 'controlPoint') {
            return [
              row.summaryText,
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              formatTripMetricValue(row.mileage, 2),
              formatTripMetricValue(row.fuelConsumption, 1),
              formatTripMetricValue(row.fuelVolume, 1),
              formatTripMetricValue(row.refuelingVolume, 1),
              formatTripMetricValue(row.drainingVolume, 1),
              formatTripMetricValue(row.mileagePerLitre, 2)
            ];
          }
          return [
            row.vehicleName,
            row.routeName,
            formatTrackTimestamp(row.tripStart),
            formatTrackTimestamp(row.tripEnd),
            row.statusLabel,
            row.pointNumber,
            row.controlPointName,
            formatTrackTimestamp(row.visitTime),
            row.violationText,
            formatTripMetricValue(row.mileage, 2),
            formatTripMetricValue(row.fuelConsumption, 1),
            formatTripMetricValue(row.fuelVolume, 1),
            formatTripMetricValue(row.refuelingVolume, 1),
            formatTripMetricValue(row.drainingVolume, 1),
            formatTripMetricValue(row.mileagePerLitre, 2)
          ];
        })
      });
    };

    return (
      <Stack spacing={1.25}>
        <Card sx={{
          p: 1.2, borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.18)', boxShadow: 'none', bgcolor: '#F8FAFC'
        }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={0.7}
            justifyContent="space-between"
            alignItems={{ md: 'center' }}
          >
            <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 800 }}>
              {selectedVehicle.plate}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
                {selectedRangeLabel}
              </Typography>
              <Button
                variant="contained"
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={handleTripReportExport}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: 40,
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                Export
              </Button>
            </Stack>
          </Stack>
        </Card>

        <Grid container spacing={1}>
          <Grid item xs={12} md={4}>
            <DetailMetricCard
              label="Total routes"
              value={`${tripReportData.totalRoutes}`}
              caption={`${tripReportData.totalVehicles} vehicle(s)`}
              icon={RouteRoundedIcon}
              color="#2563EB"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DetailMetricCard
              label="Total trips"
              value={`${tripReportData.totalRuns}`}
              caption="Runs in selected period"
              icon={ReceiptLongRoundedIcon}
              color="#0F766E"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DetailMetricCard
              label="Trips with violations"
              value={`${tripReportData.totalViolatedRuns}`}
              caption="Runs with skipped control points"
              icon={TimelineRoundedIcon}
              color="#DC2626"
            />
          </Grid>
        </Grid>

        <Card sx={{
          borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none', overflow: 'hidden'
        }}
        >
          <TableContainer sx={{ maxHeight: 620 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                  {[
                    'Vehicle',
                    'Route',
                    'Start of trip',
                    'End of trip',
                    'Status',
                    'No.',
                    'Control point',
                    'Time of visiting',
                    'Violations',
                    'Mileage, km',
                    'Fuel consumption, l',
                    'Fuel volume, l',
                    'Refueling volume, l',
                    'Draining volume, l',
                    'Mileage per 1 l, km'
                  ].map((label) => (
                    <TableCell
                      key={label}
                      sx={{
                        py: 1.1, px: 1.2, color: '#475569', fontWeight: 800, borderBottom: '1px solid rgba(148, 163, 184, 0.16)', whiteSpace: 'nowrap'
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows.map((row) => {
                  if (row.rowType !== 'controlPoint') {
                    const backgroundColorMap = {
                      runSummary: alpha('#0EA5E9', 0.07),
                      routeSummary: alpha('#16A34A', 0.09),
                      vehicleSummary: alpha('#EA580C', 0.1)
                    };
                    return (
                      <TableRow key={row.id} sx={{ bgcolor: backgroundColorMap[row.rowType] || alpha('#64748B', 0.08) }}>
                        <TableCell colSpan={9} sx={{ py: 1.05, px: 1.2 }}>
                          <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 700 }}>
                            {row.summaryText}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.05, px: 1.2, fontWeight: 700 }}>{formatTripMetricValue(row.mileage, 2)}</TableCell>
                        <TableCell sx={{ py: 1.05, px: 1.2, fontWeight: 700 }}>{formatTripMetricValue(row.fuelConsumption, 1)}</TableCell>
                        <TableCell sx={{ py: 1.05, px: 1.2, fontWeight: 700 }}>{formatTripMetricValue(row.fuelVolume, 1)}</TableCell>
                        <TableCell sx={{ py: 1.05, px: 1.2, fontWeight: 700 }}>{formatTripMetricValue(row.refuelingVolume, 1)}</TableCell>
                        <TableCell sx={{ py: 1.05, px: 1.2, fontWeight: 700 }}>{formatTripMetricValue(row.drainingVolume, 1)}</TableCell>
                        <TableCell sx={{ py: 1.05, px: 1.2, fontWeight: 700 }}>{formatTripMetricValue(row.mileagePerLitre, 2)}</TableCell>
                      </TableRow>
                    );
                  }

                  return (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{row.vehicleName}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{row.routeName}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2, whiteSpace: 'nowrap' }}>{formatTrackTimestamp(row.tripStart)}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2, whiteSpace: 'nowrap' }}>{formatTrackTimestamp(row.tripEnd)}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2, whiteSpace: 'nowrap' }}>
                        <Chip
                          size="small"
                          label={row.statusLabel}
                          sx={{ bgcolor: row.statusBackground, color: row.statusColor, fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2, whiteSpace: 'nowrap' }}>{row.pointNumber}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{row.controlPointName}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2, whiteSpace: 'nowrap' }}>{formatTrackTimestamp(row.visitTime)}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{row.violationText}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{formatTripMetricValue(row.mileage, 2)}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{formatTripMetricValue(row.fuelConsumption, 1)}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{formatTripMetricValue(row.fuelVolume, 1)}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{formatTripMetricValue(row.refuelingVolume, 1)}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{formatTripMetricValue(row.drainingVolume, 1)}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{formatTripMetricValue(row.mileagePerLitre, 2)}</TableCell>
                    </TableRow>
                  );
                })}
                {!tableRows.length && (
                  <TableRow>
                    <TableCell colSpan={15} align="center" sx={{ py: 6 }}>
                      <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 700 }}>
                        No trip rows available for this selection.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Stack>
    );
  };

  const renderDailyReadingSummaryReport = () => {
    const isDailyReadingSummaryAwaitingResponse = shouldShowFirstLoadSkeleton({
      isLoading: isDailyReadingSummaryLoading,
      error: dailyReadingSummaryError,
      data: dailyReadingSummaryData
    });

    if (!selectedVehicle?.plate) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Choose a vehicle</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            Select a vehicle to load daily reading summary.
          </Typography>
        </Card>
      );
    }

    if (!isFleetDateRangeValid) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Invalid date range</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            {dateRangeValidationMessage}
            {' '}
            Then load daily reading summary.
          </Typography>
        </Card>
      );
    }

    if (isDailyReadingSummaryLoading || isDailyReadingSummaryAwaitingResponse) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}><Skeleton variant="rounded" height={160} sx={{ borderRadius: 3 }} /></Grid>
          <Grid item xs={12}><Skeleton variant="rounded" height={420} sx={{ borderRadius: 3 }} /></Grid>
        </Grid>
      );
    }

    if (dailyReadingSummaryError) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Daily reading summary unavailable</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>{dailyReadingSummaryError}</Typography>
        </Card>
      );
    }

    const dailyReadings = dailyReadingSummaryData?.dailyReadings || [];
    if (!dailyReadings.length) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>No daily readings found</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            No daily reading summary rows were returned for
            {' '}
            {selectedVehicle.plate}
            {' '}
            in the selected period.
          </Typography>
        </Card>
      );
    }

    const selectedRangeLabel = dailyReadingSummaryData?.startDate && dailyReadingSummaryData?.endDate
      ? `${formatDailyReadingDate(dailyReadingSummaryData.startDate)} to ${formatDailyReadingDate(dailyReadingSummaryData.endDate)}`
      : 'Custom range';
    const summaryMetricConfig = getDailyReadingSummaryMetricConfig(dailyReadingSummaryData);
    const firstRowStartReading = dailyReadings[0]?.startReading ?? dailyReadingSummaryData?.rangeSummary?.startReading;
    const lastRowEndReading = dailyReadings[dailyReadings.length - 1]?.endReading ?? dailyReadingSummaryData?.rangeSummary?.endReading;
    const totalSummaryMetric = dailyReadings.reduce((total, row) => {
      const parsedValue = Number(row?.[summaryMetricConfig.valueKey]);
      if (!Number.isFinite(parsedValue)) return total;
      return total + parsedValue;
    }, 0);
    const handleDailyReadingSummaryExport = () => {
      const exportRows = [
        [`${dailyReadingSummaryData?.vehicleNumber || selectedVehicle.plate} - ${dailyReadingSummaryData?.vehicleName || '--'}`, '', '', ''],
        [selectedRangeLabel, '', '', ''],
        ['', '', '', ''],
        ['Date', 'Start reading', 'End reading', summaryMetricConfig.label],
        ...dailyReadings.map((row) => ([
          formatDailyReadingDate(row.date),
          Number.isFinite(Number(row.startReading)) ? Number(row.startReading) : '',
          Number.isFinite(Number(row.endReading)) ? Number(row.endReading) : '',
          Number.isFinite(Number(row[summaryMetricConfig.valueKey])) ? Number(row[summaryMetricConfig.valueKey]) : ''
        ])),
        [
          'Total',
          Number.isFinite(Number(firstRowStartReading)) ? Number(firstRowStartReading) : '',
          Number.isFinite(Number(lastRowEndReading)) ? Number(lastRowEndReading) : '',
          Number.isFinite(Number(totalSummaryMetric)) ? Number(totalSummaryMetric) : ''
        ]
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Summary');
      worksheet['!cols'] = [
        { wch: 18 },
        { wch: 16 },
        { wch: 16 },
        { wch: 20 }
      ];

      const fileName = `summary_${(dailyReadingSummaryData?.vehicleNumber || selectedVehicle.plate || 'vehicle').replace(/\s+/g, '_')}_${moment().format(DATE_FORMAT.reportDateTime)}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    };

    return (
      <Stack spacing={1.25}>
        <Card sx={{
          p: 1.2, borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.18)', boxShadow: 'none', bgcolor: '#F8FAFC'
        }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={0.7} justifyContent="space-between" alignItems={{ md: 'center' }}>
            <Box>
              <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 800 }}>
                {dailyReadingSummaryData?.vehicleNumber || selectedVehicle.plate}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
                {dailyReadingSummaryData?.vehicleName || '--'}
                {' • '}
                {dailyReadingSummaryData?.vehicleType || '--'}
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
                {selectedRangeLabel}
              </Typography>
              <Button
                variant="contained"
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={handleDailyReadingSummaryExport}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: 40,
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                Export
              </Button>
            </Stack>
          </Stack>
        </Card>

        <Card sx={{
          borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none', overflow: 'hidden'
        }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                  {[
                    'Date',
                    'Start reading',
                    'End reading',
                    summaryMetricConfig.label
                  ].map((label) => (
                    <TableCell
                      key={label}
                      sx={{
                        py: 1.1, px: 1.2, color: '#475569', fontWeight: 800, borderBottom: '1px solid rgba(148, 163, 184, 0.16)', whiteSpace: 'nowrap'
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {dailyReadings.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ py: 1.1, px: 1.2, whiteSpace: 'nowrap' }}>{formatDailyReadingDate(row.date)}</TableCell>
                    <TableCell sx={{ py: 1.1, px: 1.2 }}>{formatTripMetricValue(row.startReading, 1)}</TableCell>
                    <TableCell sx={{ py: 1.1, px: 1.2 }}>{formatTripMetricValue(row.endReading, 1)}</TableCell>
                    <TableCell sx={{ py: 1.1, px: 1.2 }}>{formatTripMetricValue(row[summaryMetricConfig.valueKey], 1)}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: alpha('#0F172A', 0.04) }}>
                  <TableCell sx={{ py: 1.15, px: 1.2, fontWeight: 800 }}>Total</TableCell>
                  <TableCell sx={{ py: 1.15, px: 1.2, fontWeight: 800 }}>{formatTripMetricValue(firstRowStartReading, 1)}</TableCell>
                  <TableCell sx={{ py: 1.15, px: 1.2, fontWeight: 800 }}>{formatTripMetricValue(lastRowEndReading, 1)}</TableCell>
                  <TableCell sx={{ py: 1.15, px: 1.2, fontWeight: 800 }}>{formatTripMetricValue(totalSummaryMetric, 1)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Stack>
    );
  };

  const renderCurrentRunsReport = () => {
    const isCurrentRunsAwaitingResponse = shouldShowFirstLoadSkeleton({
      isLoading: isCurrentRunsLoading,
      error: currentRunsError,
      data: currentRunsData
    });

    if (!selectedVehicle?.plate) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Choose a vehicle</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            Select a vehicle to load current runs.
          </Typography>
        </Card>
      );
    }

    if (isCurrentRunsLoading || isCurrentRunsAwaitingResponse) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}><Skeleton variant="rounded" height={160} sx={{ borderRadius: 3 }} /></Grid>
          <Grid item xs={12}><Skeleton variant="rounded" height={420} sx={{ borderRadius: 3 }} /></Grid>
        </Grid>
      );
    }

    if (currentRunsError) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Current runs unavailable</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>{currentRunsError}</Typography>
        </Card>
      );
    }

    const currentRunsRows = currentRunsData?.rows || [];
    if (!currentRunsRows.length) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>No current runs found</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            No current run records were returned for
            {' '}
            {selectedVehicle.plate}
            .
          </Typography>
        </Card>
      );
    }

    const totalCurrentRuns = Number(currentRunsData?.total) || currentRunsRows.length;
    const totalCurrentRunsPages = Math.max(1, Math.ceil(totalCurrentRuns / CURRENT_RUNS_PAGE_SIZE));
    const canGoPrev = currentRunsPage > 1;
    const canGoNext = currentRunsPage < totalCurrentRunsPages;
    const handleCurrentRunsExport = () => {
      exportFleetWorkbook({
        sheetName: 'Current Runs',
        fileNamePrefix: 'current_runs',
        vehicleLabel: selectedVehicle.plate,
        rangeLabel: `${totalCurrentRuns} current run${totalCurrentRuns > 1 ? 's' : ''}`,
        headers: [
          'Vehicle',
          'Route',
          'Run ID',
          'Route ID',
          'Actual start',
          'Planned start',
          'Planned finish',
          'Vehicle last data',
          'Last RCI event time',
          'Last RCI event',
          'Control point'
        ],
        rows: currentRunsRows.map((row) => ([
          row.vehicleName,
          row.routeName,
          row.runId,
          row.routeId,
          formatTrackTimestamp(row.actualRunStartTime),
          formatTrackTimestamp(row.plannedRunStartTime),
          formatTrackTimestamp(row.plannedRunFinishTime),
          formatTrackTimestamp(row.vehicleLastDataTime),
          formatTrackTimestamp(row.rciLastEventTime),
          row.rciLastEventName,
          row.controlPointName
        ]))
      });
    };

    return (
      <Stack spacing={1.25}>
        <Card sx={{
          p: 1.2, borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.18)', boxShadow: 'none', bgcolor: '#F8FAFC'
        }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={0.7} justifyContent="space-between" alignItems={{ md: 'center' }}>
            <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 800 }}>
              {selectedVehicle.plate}
            </Typography>
            <Stack direction="row" spacing={0.8} alignItems="center">
              <Chip
                size="small"
                label={`${totalCurrentRuns} current run${totalCurrentRuns > 1 ? 's' : ''}`}
                sx={{ bgcolor: alpha('#2563EB', 0.1), color: '#1E40AF', fontWeight: 700 }}
              />
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
                Page
                {' '}
                {currentRunsPage}
                {' / '}
                {totalCurrentRunsPages}
              </Typography>
              <Button
                variant="contained"
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={handleCurrentRunsExport}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: 40,
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                Export
              </Button>
            </Stack>
          </Stack>
        </Card>

        <Card sx={{
          borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none', overflow: 'hidden'
        }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                  {[
                    'Vehicle',
                    'Route',
                    'Run ID',
                    'Route ID',
                    'Actual start',
                    'Planned start',
                    'Planned finish',
                    'Vehicle last data',
                    'Last RCI event time',
                    'Last RCI event',
                    'Control point'
                  ].map((label) => (
                    <TableCell
                      key={label}
                      sx={{
                        py: 1.1, px: 1.2, color: '#475569', fontWeight: 800, borderBottom: '1px solid rgba(148, 163, 184, 0.16)', whiteSpace: 'nowrap'
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {currentRunsRows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ py: 1.1, px: 1.2 }}>{row.vehicleName}</TableCell>
                    <TableCell sx={{ py: 1.1, px: 1.2 }}>{row.routeName}</TableCell>
                    <TableCell sx={{ py: 1.1, px: 1.2 }}>{row.runId}</TableCell>
                    <TableCell sx={{ py: 1.1, px: 1.2 }}>{row.routeId}</TableCell>
                    <TableCell sx={{ py: 1.1, px: 1.2, whiteSpace: 'nowrap' }}>{formatTrackTimestamp(row.actualRunStartTime)}</TableCell>
                    <TableCell sx={{ py: 1.1, px: 1.2, whiteSpace: 'nowrap' }}>{formatTrackTimestamp(row.plannedRunStartTime)}</TableCell>
                    <TableCell sx={{ py: 1.1, px: 1.2, whiteSpace: 'nowrap' }}>{formatTrackTimestamp(row.plannedRunFinishTime)}</TableCell>
                    <TableCell sx={{ py: 1.1, px: 1.2, whiteSpace: 'nowrap' }}>{formatTrackTimestamp(row.vehicleLastDataTime)}</TableCell>
                    <TableCell sx={{ py: 1.1, px: 1.2, whiteSpace: 'nowrap' }}>{formatTrackTimestamp(row.rciLastEventTime)}</TableCell>
                    <TableCell sx={{ py: 1.1, px: 1.2 }}>{row.rciLastEventName}</TableCell>
                    <TableCell sx={{ py: 1.1, px: 1.2 }}>{row.controlPointName}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            disabled={!canGoPrev}
            onClick={() => {
              primeReportLoading('currentRuns');
              setCurrentRunsPage((page) => Math.max(1, page - 1));
            }}
          >
            Previous
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={!canGoNext}
            onClick={() => {
              primeReportLoading('currentRuns');
              setCurrentRunsPage((page) => page + 1);
            }}
          >
            Next
          </Button>
        </Stack>
      </Stack>
    );
  };

  const renderEventsReport = () => {
    const isEventsAwaitingResponse = shouldShowFirstLoadSkeleton({
      isLoading: isEventsLoading,
      error: eventsError,
      data: eventsData
    });

    if (!selectedVehicle?.plate) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Choose a vehicle</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            Select a vehicle to load event history.
          </Typography>
        </Card>
      );
    }

    if (!isFleetDateRangeValid) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Invalid date range</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            {dateRangeValidationMessage}
            {' '}
            Then load events.
          </Typography>
        </Card>
      );
    }

    if (isEventsLoading || isEventsAwaitingResponse) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}><Skeleton variant="rounded" height={160} sx={{ borderRadius: 3 }} /></Grid>
          <Grid item xs={12}><Skeleton variant="rounded" height={420} sx={{ borderRadius: 3 }} /></Grid>
        </Grid>
      );
    }

    if (eventsError) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Events unavailable</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>{eventsError}</Typography>
        </Card>
      );
    }

    const eventRows = eventsData?.rows || [];
    if (!eventRows.length) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>No events found</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            No event records were returned for
            {' '}
            {selectedVehicle.plate}
            {' '}
            in the selected period.
          </Typography>
        </Card>
      );
    }

    const totalEventRecords = Number(eventsData?.totalRecords) || eventRows.length;
    const totalEventPages = Math.max(
      1,
      Number(eventsData?.totalPages) || Math.ceil(totalEventRecords / EVENTS_PAGE_SIZE)
    );
    const eventColumns = eventsData?.columns?.length ? eventsData.columns : EVENTS_REPORT_COLUMNS;
    const canGoPrev = eventsPage > 1;
    const canGoNext = eventsPage < totalEventPages;
    const selectedEventsMapRow = selectedFuelEventMapRow?.sourceReport === 'events'
      ? selectedFuelEventMapRow
      : null;

    return (
      <Stack spacing={1.25}>
        <Card sx={{
          p: 1.2, borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.18)', boxShadow: 'none', bgcolor: '#F8FAFC'
        }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={0.7} justifyContent="space-between" alignItems={{ md: 'center' }}>
            <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 800 }}>
              {selectedVehicle.plate}
            </Typography>
            <Stack direction="row" spacing={0.8} alignItems="center">
              <Chip
                size="small"
                label={`${totalEventRecords} event${totalEventRecords > 1 ? 's' : ''}`}
                sx={{ bgcolor: alpha('#0891B2', 0.12), color: '#155E75', fontWeight: 700 }}
              />
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
                Page
                {' '}
                {eventsPage}
                {' / '}
                {totalEventPages}
              </Typography>
            </Stack>
          </Stack>
        </Card>

        <Card sx={{
          borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none', overflow: 'hidden'
        }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                  {eventColumns.map((column) => (
                    <TableCell
                      key={column.key}
                      sx={{
                        py: 1.1, px: 1.2, color: '#475569', fontWeight: 800, borderBottom: '1px solid rgba(148, 163, 184, 0.16)', whiteSpace: 'nowrap'
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {eventRows.map((row) => {
                  const hasCoordinates = Number.isFinite(Number(row.latitude)) && Number.isFinite(Number(row.longitude));
                  const isSelectedMapRow = selectedEventsMapRow?.id === row.id;
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ py: 1.1, px: 1.2, whiteSpace: 'nowrap' }}>{row.displayRow.serialNumber}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2, whiteSpace: 'nowrap' }}>{row.displayRow.vehicle}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2, whiteSpace: 'nowrap' }}>{row.displayRow.dateTime}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2, whiteSpace: 'nowrap' }}>{row.displayRow.event}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2, minWidth: 210 }}>{row.displayRow.parameters}</TableCell>
                      <TableCell sx={{
                        py: 1.1, px: 1.2, minWidth: 260, maxWidth: 360, whiteSpace: 'normal', color: '#334155'
                      }}
                      >
                        {hasCoordinates ? (
                          <ButtonBase
                            onClick={() => {
                              setFuelEventMapError('');
                              setSelectedFuelEventMapRow({
                                ...row,
                                latitude: Number(row.latitude),
                                longitude: Number(row.longitude),
                                typeLabel: row.eventTypeLabel,
                                sourceReport: 'events'
                              });
                            }}
                            sx={{
                              alignItems: 'flex-start',
                              borderRadius: 1.5,
                              color: isSelectedMapRow ? '#1D4ED8' : '#334155',
                              display: 'inline-flex',
                              fontSize: '0.875rem',
                              fontWeight: isSelectedMapRow ? 800 : 600,
                              gap: 0.6,
                              justifyContent: 'flex-start',
                              lineHeight: 1.45,
                              p: 0.4,
                              textAlign: 'left',
                              '&:hover': {
                                bgcolor: alpha('#2563EB', 0.08),
                                color: '#1D4ED8'
                              }
                            }}
                          >
                            <PlaceRoundedIcon sx={{ fontSize: 17, mt: 0.15, flexShrink: 0 }} />
                            <Box component="span">{row.displayRow.address}</Box>
                          </ButtonBase>
                        ) : (
                          row.displayRow.address
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            disabled={!canGoPrev}
            onClick={() => {
              primeReportLoading('events');
              setEventsPage((page) => Math.max(1, page - 1));
            }}
          >
            Previous
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={!canGoNext}
            onClick={() => {
              primeReportLoading('events');
              setEventsPage((page) => page + 1);
            }}
          >
            Next
          </Button>
        </Stack>
      </Stack>
    );
  };

  const renderLogReport = () => {
    const isLogReportAwaitingResponse = shouldShowFirstLoadSkeleton({
      isLoading: isLogReportLoading,
      error: logReportError,
      data: logReportData
    });

    if (!selectedVehicle?.plate) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Choose a vehicle</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            Select a vehicle to load device log history.
          </Typography>
        </Card>
      );
    }

    if (!isFleetDateRangeValid) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Invalid date range</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            {dateRangeValidationMessage}
            {' '}
            Then load log report data.
          </Typography>
        </Card>
      );
    }

    if (isLogReportLoading || isLogReportAwaitingResponse) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}><Skeleton variant="rounded" height={160} sx={{ borderRadius: 3 }} /></Grid>
          <Grid item xs={12}><Skeleton variant="rounded" height={420} sx={{ borderRadius: 3 }} /></Grid>
        </Grid>
      );
    }

    if (logReportError) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Log report unavailable</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>{logReportError}</Typography>
        </Card>
      );
    }

    const logRows = logReportData?.rows || [];
    const hasMoreLoadedLogRows = Boolean(logReportData?.hasMore && logReportData?.nextCursor);
    if (!logRows.length && !hasMoreLoadedLogRows) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>No log rows found</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            No log rows were returned for
            {' '}
            {selectedVehicle.plate}
            {' '}
            in this date/time range. Change the date/time filter to load another period.
          </Typography>
        </Card>
      );
    }

    const logColumns = logReportData?.columns?.length ? logReportData.columns : [];
    const fromMoment = moment(dateRange.fromDateTime, DATE_TIME_INPUT_FORMAT, true);
    const toMoment = moment(dateRange.toDateTime, DATE_TIME_INPUT_FORMAT, true);
    const selectedRangeLabel = fromMoment.isValid() && toMoment.isValid()
      ? `${fromMoment.format('DD MMM YYYY, hh:mm A')} - ${toMoment.format('DD MMM YYYY, hh:mm A')}`
      : 'Custom range';
    const loadedLogRecords = Number(logReportData?.loadedRecords) || logRows.length;
    const isRawLogReport = Boolean(logReportData?.raw);
    const hasMoreLogRows = isRawLogReport && hasMoreLoadedLogRows;
    const isStatusColumnVisible = (key) => logColumns.some((column) => column.key === key);
    const matchesLogStatusFilter = (row, key) => {
      const filterValue = logReportStatusFilters[key] || 'all';
      return filterValue === 'all' || row[key] === filterValue;
    };
    const filteredLogRows = logRows.filter((row) => (
      matchesLogStatusFilter(row, 'ignitionStatus')
      && matchesLogStatusFilter(row, 'engineStatus')
      && matchesLogStatusFilter(row, 'secondaryEngineStatus')
    ));

    const handleLogStatusFilterChange = (key, value) => {
      setLogReportStatusFilters((filters) => ({
        ...filters,
        [key]: value
      }));
    };

    const renderLogStatusFilter = (key, label) => {
      if (!isStatusColumnVisible(key)) return null;
      return (
        <Stack key={key} direction="row" alignItems="center" spacing={0.8} sx={{ flexWrap: 'wrap', gap: 0.7 }}>
          <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 800 }}>
            {label}
          </Typography>
          {['all', 'On', 'Off', 'Unknown'].map((value) => (
            <Chip
              key={value}
              clickable
              size="small"
              label={value === 'all' ? 'All' : value}
              onClick={() => handleLogStatusFilterChange(key, value)}
              variant={logReportStatusFilters[key] === value ? 'filled' : 'outlined'}
              sx={{
                height: 26,
                fontWeight: 700,
                color: logReportStatusFilters[key] === value ? '#FFFFFF' : '#334155',
                bgcolor: logReportStatusFilters[key] === value ? '#0F172A' : '#FFFFFF',
                borderColor: 'rgba(148, 163, 184, 0.38)'
              }}
            />
          ))}
        </Stack>
      );
    };

    const handleLogReportExport = async () => {
      setLogReportExporting(true);
      setLogReportError('');
      try {
        const response = await APIService.getFleetLogReport({
          registration_num: selectedVehicle.plate,
          timeBegin: unixDateRange.from,
          timeEnd: unixDateRange.to,
          timezone: effectiveFleetTimezone,
          tz: effectiveFleetTimezone,
          locale: DEFAULT_FUEL_REPORT_LOCALE,
          rows: LOG_REPORT_PAGE_SIZE,
          page: 1,
          sidx: 'eventdate',
          sord: 'asc',
          export: true
        });
        const normalizedExport = normalizeFleetLogReport(response);
        if (!normalizedExport.rows.length) {
          setLogReportError('No log rows were returned for export.');
          return;
        }
        const exportColumns = normalizedExport.columns?.length ? normalizedExport.columns : logColumns;
        exportFleetWorkbook({
          sheetName: 'Log Report',
          fileNamePrefix: 'log_report',
          vehicleLabel: selectedVehicle.plate,
          rangeLabel: selectedRangeLabel,
          headers: exportColumns.map((column) => column.label),
          rows: normalizedExport.rows.map((row) => exportColumns.map((column) => row.displayRow[column.key] ?? ''))
        });
      } catch (error) {
        console.log('fleet log report export error', error?.message || error);
        setLogReportError(error?.message || 'Unable to export log report. Please reduce the date range and try again.');
      } finally {
        setLogReportExporting(false);
      }
    };

    const handleLogReportLoadMore = async () => {
      if (!isRawLogReport || !logReportData?.nextCursor || isLogReportLoadingMore) return;
      setLogReportLoadingMore(true);
      setLogReportError('');
      try {
        const response = await APIService.getFleetLogReport({
          registration_num: selectedVehicle.plate,
          timeBegin: unixDateRange.from,
          timeEnd: unixDateRange.to,
          timezone: effectiveFleetTimezone,
          tz: effectiveFleetTimezone,
          locale: DEFAULT_FUEL_REPORT_LOCALE,
          rows: LOG_REPORT_PAGE_SIZE,
          page: 1,
          sidx: 'eventdate',
          sord: 'asc',
          raw: true,
          profileSecondaryEngine: selectedHasSecondaryEngine,
          cursor: logReportData.nextCursor
        });
        const nextLogReport = normalizeFleetLogReport(response, {
          rawMode: true,
          secondaryEngineThreshold: selectedSecondaryEngineThreshold
        });
        if (!nextLogReport.rows.length) {
          setLogReportData((currentData) => ({
            ...currentData,
            hasMore: false,
            nextCursor: ''
          }));
          return;
        }
        setLogReportData((currentData) => mergeFleetLogReportData(currentData, nextLogReport));
      } catch (error) {
        console.log('fleet log report load more error', error?.message || error);
        setLogReportError(error?.message || 'Unable to load more log rows. Please try again.');
      } finally {
        setLogReportLoadingMore(false);
      }
    };

    const logLocationColumnKeys = ['startLocation', 'endLocation', 'location'];
    const renderLogLocationCell = (row, column) => {
      const cellValue = row.displayRow[column.key] ?? '';
      const hasCoordinates = Number.isFinite(Number(row.latitude)) && Number.isFinite(Number(row.longitude));
      if (!logLocationColumnKeys.includes(column.key) || !hasCoordinates) return cellValue;

      const isSelectedMapRow = selectedFuelEventMapRow?.sourceReport === 'logReport'
        && selectedFuelEventMapRow?.id === `${row.id}-${column.key}`;

      return (
        <ButtonBase
          onClick={() => {
            setFuelEventMapError('');
            setSelectedFuelEventMapRow({
              ...row,
              id: `${row.id}-${column.key}`,
              latitude: Number(row.latitude),
              longitude: Number(row.longitude),
              vehicleName: row.vehicle || selectedVehicle?.plate || '--',
              typeLabel: column.label || 'Log location',
              eventDate: row.eventDate || row.dateTime,
              address: cellValue || row.address || '--',
              parameterText: row.parameterText,
              sourceReport: 'logReport'
            });
          }}
          sx={{
            alignItems: 'flex-start',
            borderRadius: 1.5,
            color: isSelectedMapRow ? '#1D4ED8' : '#334155',
            display: 'inline-flex',
            fontSize: '0.875rem',
            fontWeight: isSelectedMapRow ? 800 : 600,
            gap: 0.6,
            justifyContent: 'flex-start',
            lineHeight: 1.45,
            p: 0.4,
            textAlign: 'left',
            '&:hover': {
              bgcolor: alpha('#2563EB', 0.08),
              color: '#1D4ED8'
            }
          }}
        >
          <PlaceRoundedIcon sx={{ fontSize: 17, mt: 0.15, flexShrink: 0 }} />
          <Box component="span">{cellValue || '--'}</Box>
        </ButtonBase>
      );
    };

    return (
      <Stack spacing={1.25}>
        <Card sx={{
          p: 1.2, borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.18)', boxShadow: 'none', bgcolor: '#F8FAFC'
        }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={0.7} justifyContent="space-between" alignItems={{ md: 'center' }}>
            <Stack spacing={0.25}>
              <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 800 }}>
                {selectedVehicle.plate}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
                {selectedRangeLabel}
              </Typography>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Chip
                size="small"
                label={`${loadedLogRecords} ${isRawLogReport ? 'raw row' : 'interval'}${loadedLogRecords > 1 ? 's' : ''} loaded`}
                sx={{ bgcolor: alpha('#0F766E', 0.1), color: '#115E59', fontWeight: 700 }}
              />
              {hasMoreLogRows && (
                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
                  More rows available
                </Typography>
              )}
              <Button
                variant="contained"
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={handleLogReportExport}
                disabled={isLogReportExporting}
                sx={{
                  borderRadius: 2, textTransform: 'none', fontWeight: 600, minHeight: 40, whiteSpace: 'nowrap'
                }}
              >
                {isLogReportExporting ? 'Exporting...' : 'Export'}
              </Button>
            </Stack>
          </Stack>
        </Card>

        <Card sx={{
          p: 1.2, borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none', bgcolor: '#FFFFFF'
        }}
        >
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} alignItems={{ lg: 'center' }} justifyContent="space-between">
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {renderLogStatusFilter('ignitionStatus', 'Ignition')}
              {renderLogStatusFilter('engineStatus', 'Main engine')}
              {renderLogStatusFilter('secondaryEngineStatus', 'Secondary')}
            </Stack>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
              Showing
              {' '}
              {filteredLogRows.length}
              {' '}
              of
              {' '}
              {logRows.length}
              {' '}
              loaded rows
            </Typography>
          </Stack>
        </Card>

        <Card sx={{
          borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none', overflow: 'hidden'
        }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                  {logColumns.map((column) => (
                    <TableCell
                      key={column.key}
                      sx={{
                        py: 1.1, px: 1.2, color: '#475569', fontWeight: 800, borderBottom: '1px solid rgba(148, 163, 184, 0.16)', whiteSpace: 'nowrap'
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLogRows.map((row) => (
                  <TableRow key={row.id} hover>
                    {logColumns.map((column) => (
                      <TableCell
                        key={column.key}
                        sx={{
                          py: 1.1,
                          px: 1.2,
                          whiteSpace: ['startLocation', 'endLocation', 'location'].includes(column.key) ? 'normal' : 'nowrap',
                          minWidth: ['startLocation', 'endLocation', 'location'].includes(column.key) ? 220 : undefined
                        }}
                      >
                        {renderLogLocationCell(row, column)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
          {filteredLogRows.length === 0 && (
            <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 700, mr: 'auto' }}>
              No loaded rows match the selected filters.
            </Typography>
          )}
          <Button
            size="small"
            variant="outlined"
            disabled={!hasMoreLogRows || isLogReportLoadingMore}
            onClick={handleLogReportLoadMore}
          >
            {isLogReportLoadingMore ? 'Loading...' : 'Load next 100'}
          </Button>
        </Stack>
      </Stack>
    );
  };

  const renderGroupWorkReport = () => {
    const isGroupWorkAwaitingResponse = shouldShowFirstLoadSkeleton({
      isLoading: isGroupWorkLoading,
      error: groupWorkError,
      data: groupWorkData
    });

    if (!selectedVehicle?.plate) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Choose a vehicle</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            Select a vehicle to load group work details.
          </Typography>
        </Card>
      );
    }

    if (!isFleetDateRangeValid) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Invalid date range</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            {dateRangeValidationMessage}
            {' '}
            Then load group work data.
          </Typography>
        </Card>
      );
    }

    if (isGroupWorkLoading || isGroupWorkAwaitingResponse) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}><Skeleton variant="rounded" height={160} sx={{ borderRadius: 3 }} /></Grid>
          <Grid item xs={12}><Skeleton variant="rounded" height={420} sx={{ borderRadius: 3 }} /></Grid>
        </Grid>
      );
    }

    if (groupWorkError) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Group work unavailable</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>{groupWorkError}</Typography>
        </Card>
      );
    }

    const groupWorkRows = groupWorkData?.rows || [];
    if (!groupWorkRows.length) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>No group work rows found</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            No group work records were returned for
            {' '}
            {selectedVehicle.plate}
            {' '}
            in the selected period.
          </Typography>
        </Card>
      );
    }

    const totalGroupWorkRecords = Number(groupWorkData?.totalRecords) || groupWorkRows.length;
    const totalGroupWorkPages = Math.max(
      1,
      Number(groupWorkData?.totalPages) || Math.ceil(totalGroupWorkRecords / GROUP_WORK_PAGE_SIZE)
    );
    const canGoPrev = groupWorkPage > 1;
    const canGoNext = groupWorkPage < totalGroupWorkPages;

    return (
      <Stack spacing={1.25}>
        <Card sx={{
          p: 1.2, borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.18)', boxShadow: 'none', bgcolor: '#F8FAFC'
        }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={0.7} justifyContent="space-between" alignItems={{ md: 'center' }}>
            <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 800 }}>
              {selectedVehicle.plate}
            </Typography>
            <Stack direction="row" spacing={0.8} alignItems="center">
              <Chip
                size="small"
                label={`${totalGroupWorkRecords} row${totalGroupWorkRecords > 1 ? 's' : ''}`}
                sx={{ bgcolor: alpha('#6D28D9', 0.1), color: '#5B21B6', fontWeight: 700 }}
              />
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
                Page
                {' '}
                {groupWorkPage}
                {' / '}
                {totalGroupWorkPages}
              </Typography>
            </Stack>
          </Stack>
        </Card>

        <Card sx={{
          borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none', overflow: 'hidden'
        }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                  {[
                    'Vehicle',
                    'Date',
                    'Shift',
                    'Start',
                    'End',
                    'Work time',
                    'Work %',
                    'Move time',
                    'Mileage, km',
                    'Mileage/l',
                    'Consumption, l',
                    'On move, l',
                    'W/O move, l',
                    'Refuel, l',
                    'Drain, l',
                    'Max speed',
                    'Group'
                  ].map((label) => (
                    <TableCell
                      key={label}
                      sx={{
                        py: 1.1, px: 1.2, color: '#475569', fontWeight: 800, borderBottom: '1px solid rgba(148, 163, 184, 0.16)', whiteSpace: 'nowrap'
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {groupWorkRows.map((row) => {
                  const workPercentLabel = row.workTimePercent && row.workTimePercent !== '--' ? `${row.workTimePercent}%` : '--';
                  if (row.isSummaryRow) {
                    return (
                      <TableRow
                        key={row.id}
                        sx={{
                          bgcolor: alpha('#6D28D9', 0.08)
                        }}
                      >
                        <TableCell
                          colSpan={5}
                          sx={{
                            py: 1.1, px: 1.2, fontWeight: 700, color: '#312E81'
                          }}
                        >
                          Shift summary
                        </TableCell>
                        <TableCell sx={{ py: 1.1, px: 1.2, fontWeight: 700 }}>{formatHhMmSs(row.workTime)}</TableCell>
                        <TableCell sx={{ py: 1.1, px: 1.2, fontWeight: 700 }}>{workPercentLabel}</TableCell>
                        <TableCell sx={{ py: 1.1, px: 1.2, fontWeight: 700 }}>{formatHhMmSs(row.movementTime)}</TableCell>
                        <TableCell sx={{ py: 1.1, px: 1.2, fontWeight: 700 }}>{formatGroupWorkMetric(row.mileage, 2)}</TableCell>
                        <TableCell sx={{ py: 1.1, px: 1.2, fontWeight: 700 }}>{formatGroupWorkMetric(row.mileagePerLitre, 2)}</TableCell>
                        <TableCell sx={{ py: 1.1, px: 1.2, fontWeight: 700 }}>{formatGroupWorkMetric(row.consumption, 1)}</TableCell>
                        <TableCell sx={{ py: 1.1, px: 1.2, fontWeight: 700 }}>{formatGroupWorkMetric(row.fuelConsumptionOnMove, 1)}</TableCell>
                        <TableCell sx={{ py: 1.1, px: 1.2, fontWeight: 700 }}>{formatGroupWorkMetric(row.fuelConsumptionWithoutMovement, 1)}</TableCell>
                        <TableCell sx={{ py: 1.1, px: 1.2, fontWeight: 700 }}>{formatGroupWorkMetric(row.refuelVolume, 1)}</TableCell>
                        <TableCell sx={{ py: 1.1, px: 1.2, fontWeight: 700 }}>{formatGroupWorkMetric(row.drainingVolume, 1)}</TableCell>
                        <TableCell sx={{ py: 1.1, px: 1.2, fontWeight: 700 }}>{formatGroupWorkMetric(row.maxSpeed, 0)}</TableCell>
                        <TableCell sx={{ py: 1.1, px: 1.2, fontWeight: 700 }}>{row.groupName}</TableCell>
                      </TableRow>
                    );
                  }

                  return (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{row.vehicleName}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2, whiteSpace: 'nowrap' }}>{formatTrackTimestamp(row.date)}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{row.shiftNumber}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2, whiteSpace: 'nowrap' }}>{formatTrackTimestamp(row.startTime)}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2, whiteSpace: 'nowrap' }}>{formatTrackTimestamp(row.endTime)}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{formatHhMmSs(row.workTime)}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{workPercentLabel}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{formatHhMmSs(row.movementTime)}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{formatGroupWorkMetric(row.mileage, 2)}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{formatGroupWorkMetric(row.mileagePerLitre, 2)}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{formatGroupWorkMetric(row.consumption, 1)}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{formatGroupWorkMetric(row.fuelConsumptionOnMove, 1)}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{formatGroupWorkMetric(row.fuelConsumptionWithoutMovement, 1)}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{formatGroupWorkMetric(row.refuelVolume, 1)}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{formatGroupWorkMetric(row.drainingVolume, 1)}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{formatGroupWorkMetric(row.maxSpeed, 0)}</TableCell>
                      <TableCell sx={{ py: 1.1, px: 1.2 }}>{row.groupName}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            disabled={!canGoPrev}
            onClick={() => {
              primeReportLoading('groupWork');
              setGroupWorkPage((page) => Math.max(1, page - 1));
            }}
          >
            Previous
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={!canGoNext}
            onClick={() => {
              primeReportLoading('groupWork');
              setGroupWorkPage((page) => page + 1);
            }}
          >
            Next
          </Button>
        </Stack>
      </Stack>
    );
  };

  const renderActiveReport = () => {
    if (activeReport === 'analytics') {
      if (analyticsData?.mode === 'dashboard' || !analyticsData) {
        return renderAnalyticsDashboardReport();
      }
      return renderAnalyticsReport();
    }
    if (activeReport === 'location') return renderLocationReport();
    if (activeReport === 'statistics') return renderStatisticsReport();
    if (activeReport === 'fuel') return renderFuelReport();
    if (activeReport === 'tripReport') return renderTripReport();
    if (activeReport === 'dailyReadingSummary') return renderDailyReadingSummaryReport();
    if (activeReport === 'currentRuns') return renderCurrentRunsReport();
    if (activeReport === 'events') return renderEventsReport();
    if (activeReport === 'logReport') return renderLogReport();
    if (activeReport === 'groupWork') return renderGroupWorkReport();
    if (activeReport === 'trackFuel') return renderTrackReport();
    return renderTrackReport();
  };

  const renderEventsLocationDialog = () => {
    const selectedEventsMapRow = ['events', 'logReport'].includes(selectedFuelEventMapRow?.sourceReport)
      && selectedFuelEventMapRow.sourceReport === activeReport
      ? selectedFuelEventMapRow
      : null;
    const selectedEventsMapCoordinates = selectedEventsMapRow
      ? `${formatCoordinateLabel(selectedEventsMapRow.latitude)}, ${formatCoordinateLabel(selectedEventsMapRow.longitude)}`
      : '--';
    const selectedLocationSourceLabel = selectedEventsMapRow?.sourceReport === 'logReport' ? 'Log location' : 'Event location';

    const handleClose = () => {
      setSelectedFuelEventMapRow(null);
      setFuelEventMapError('');
      setEventLocationDialogReadyToken(0);
      clearFuelEventMapArtifacts();
    };

    return (
      <Dialog
        fullWidth
        maxWidth="md"
        open={Boolean(selectedEventsMapRow)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
        TransitionProps={{
          onEntered: () => setEventLocationDialogReadyToken((token) => token + 1)
        }}
      >
        <DialogTitle sx={{ p: 1.6, pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ color: '#0F172A', fontWeight: 900 }}>
                {selectedLocationSourceLabel}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                {selectedEventsMapRow?.eventTypeLabel || selectedEventsMapRow?.typeLabel || 'Event'}
                {' | '}
                {formatTrackTimestamp(selectedEventsMapRow?.eventDate)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                {selectedEventsMapCoordinates}
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleClose} aria-label="Close event location">
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 1.6, pt: 0 }}>
          <Box sx={{
            height: { xs: 320, md: 480 },
            borderRadius: 2.2,
            border: '1px solid rgba(148, 163, 184, 0.18)',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#F8FAFC',
            position: 'relative'
          }}
          >
            <Box ref={eventsLocationMapContainerRef} sx={LOCATION_MAP_CONTAINER_STYLE} />

            {(isMapKeyLoading || isMapScriptLoading || !googleMapsKey) && (
              <Box sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(248, 250, 252, 0.82)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none'
              }}
              >
                <Stack spacing={1} alignItems="center" sx={{ minWidth: 220 }}>
                  <Typography variant="body2" sx={{ color: '#475569', fontWeight: 700 }}>Loading map...</Typography>
                  <LinearProgress sx={{ width: '100%', height: 7, borderRadius: 999 }} />
                </Stack>
              </Box>
            )}

            {(fuelEventMapError || mapError) && (
              <Box sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(248, 250, 252, 0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 1.2
              }}
              >
                <Typography variant="body2" sx={{ color: '#B91C1C', fontWeight: 700, textAlign: 'center' }}>
                  {fuelEventMapError || mapError}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    );
  };
  const eventsLocationDialog = renderEventsLocationDialog();

  const renderDateTimeFilter = () => (
    <Card sx={{
      p: 1.75, borderRadius: 3.25, border: '1px solid rgba(148, 163, 184, 0.2)', boxShadow: 'none', bgcolor: '#F8FAFC'
    }}
    >
      <Stack spacing={1.35}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0F172A' }}>Date & Time Filter</Typography>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.45,
                color: !isFleetDateRangeValid ? '#B45309' : '#64748B',
                fontWeight: 700
              }}
            >
              Maximum date range:
              {' '}
              {MAX_DATE_RANGE_DAYS}
              {' '}
              days
              {!isFleetDateRangeValid ? ` • ${dateRangeValidationMessage}` : ''}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={0.8} sx={{ flexWrap: 'wrap', gap: 0.8 }}>
          {(activeReport === 'logReport'
            ? DATE_RANGE_PRESETS
            : DATE_RANGE_PRESETS.filter((preset) => preset.value !== 'lastHour')
          ).map((preset) => (
            <Chip
              key={preset.value}
              label={preset.label}
              clickable
              onClick={() => handleDatePresetChange(preset.value)}
              variant={dateRangePreset === preset.value ? 'filled' : 'outlined'}
              sx={{
                fontWeight: 700, color: dateRangePreset === preset.value ? '#FFFFFF' : '#334155', bgcolor: dateRangePreset === preset.value ? '#0F172A' : '#FFFFFF', borderColor: 'rgba(148, 163, 184, 0.4)'
              }}
            />
          ))}
        </Stack>

        <Grid container spacing={1.25}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="From date & time"
              type="datetime-local"
              value={dateRange.fromDateTime}
              onChange={(event) => handleDateRangeChange('fromDateTime', event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: minAllowedFromDateTime,
                max: dateRange.toDateTime || undefined
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="To date & time"
              type="datetime-local"
              value={dateRange.toDateTime}
              onChange={(event) => handleDateRangeChange('toDateTime', event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: dateRange.fromDateTime || undefined,
                max: maxAllowedToDateTime
              }}
            />
          </Grid>
        </Grid>
      </Stack>
    </Card>
  );

  const renderSelectedWorkspace = () => {
    const openTabsMeta = openReportTabs
      .map((tabValue) => REPORT_TABS.find((tab) => tab.value === tabValue))
      .filter(Boolean);
    const recentReportsMeta = openTabsMeta.filter((tab) => tab.value !== activeReport).slice(-4).reverse();
    const ActiveReportIcon = activeReportConfig?.icon || InsightsRoundedIcon;

    return (
      <Card sx={{
        px: { xs: 1.5, md: 2 },
        pt: 0,
        pb: { xs: 1.5, md: 2 },
        borderRadius: 4,
        border: '1px solid rgba(148, 163, 184, 0.16)',
        boxShadow: 'none'
      }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 4,
            mb: 1.6
          }}
        >
          <Stack spacing={1}>
            <Card sx={{
              p: 1,
              borderRadius: 3.5,
              boxShadow: '0 16px 36px rgba(15, 23, 42, 0.08)',
              border: '1px solid rgba(148, 163, 184, 0.16)',
              bgcolor: alpha('#FFFFFF', 0.94),
              backdropFilter: 'blur(14px)'
            }}
            >
              <Stack spacing={1}>
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.2} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2.25,
                        bgcolor: '#0F172A',
                        color: '#FFFFFF'
                      }}
                    >
                      <ActiveReportIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={0.8} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.6 }}>
                        <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 900, textAlign: 'center' }}>
                          {activeReportConfig?.label || 'Report'}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700, display: 'block' }}>
                        {activeReportLoadingStatus.isLoading
                          ? 'Refreshing latest data...'
                          : formatRefreshStatusLabel(reportLastLoadedAt[activeReport], refreshStatusTick)}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Tooltip title={activeReportLoadingStatus.isLoading ? 'Refreshing current report' : 'Refresh current report'} arrow>
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={activeReportLoadingStatus.isLoading ? <CircularProgress size={15} thickness={6} /> : <RefreshRoundedIcon fontSize="small" />}
                          onClick={handleRefreshActiveReport}
                          disabled={activeReportLoadingStatus.isLoading || (!selectedVehicle?.plate && activeReport !== 'location')}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 800,
                            minHeight: 40,
                            textTransform: 'none',
                            whiteSpace: 'nowrap',
                            bgcolor: '#FFFFFF'
                          }}
                        >
                          Refresh
                        </Button>
                      </span>
                    </Tooltip>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Report view"
                      value={activeReport}
                      onChange={(event) => handleOpenReportTab(event.target.value)}
                      SelectProps={{ native: true }}
                      sx={{
                        minWidth: { lg: 260 },
                        maxWidth: { lg: 320 },
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#FFFFFF'
                        }
                      }}
                    >
                      {REPORT_TABS.map((tab) => (
                        <option key={tab.value} value={tab.value}>
                          {tab.label}
                        </option>
                      ))}
                    </TextField>
                  </Stack>
                </Stack>

                {recentReportsMeta.length > 0 && (
                  <Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.8 }}>
                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 800 }}>
                      Recent
                    </Typography>
                    {recentReportsMeta.map((tab) => (
                      <Chip
                        key={tab.value}
                        clickable
                        label={tab.label}
                        onClick={() => handleReportChange(null, tab.value)}
                        onDelete={openTabsMeta.length > 1 ? (event) => handleCloseReportTab(event, tab.value) : undefined}
                        deleteIcon={<CloseRoundedIcon sx={{ fontSize: 16 }} />}
                        sx={{
                          borderRadius: 999,
                          fontWeight: 700,
                          color: '#334155',
                          bgcolor: '#FFFFFF',
                          border: '1px solid rgba(148, 163, 184, 0.28)',
                          '& .MuiChip-label': {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center'
                          }
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Stack>
            </Card>

            {shouldShowDateTimeFilter && renderDateTimeFilter()}

            {activeReportLoadingStatus.isLoading && (
              <Card sx={{
                px: 1.2, py: 1, borderRadius: 2.5, border: '1px solid rgba(37, 99, 235, 0.16)', boxShadow: 'none', bgcolor: alpha('#2563EB', 0.05)
              }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CircularProgress size={16} thickness={6} />
                  <Typography variant="body2" sx={{ color: '#1E3A8A', fontWeight: 800 }}>
                    Loading
                    {' '}
                    {activeReportLoadingStatus.label}
                    ...
                  </Typography>
                </Stack>
                <LinearProgress sx={{ mt: 0.9, height: 6, borderRadius: 999 }} />
              </Card>
            )}
          </Stack>
        </Box>
        {renderActiveReport()}
      </Card>
    );
  };

  // FIX: Cost Optimization - Workspace Render Layer
  function renderFleetWorkspace() {
    if (isFleetLoading || isVehicleLoading) return renderVehicleLoading();
    if (!vehicles.length) return renderNoFleetData();

    // We intentionally return the workspace even if 0 vehicles are selected while in Location tab.
    // This allows the map to stay alive in DOM.
    if (isLocationSelectionMode || selectedVehicle) {
      return renderSelectedWorkspace();
    }

    return renderNoVehicleSelected();
  }

  return (
    <Box sx={{
      minHeight: '100vh', bgcolor: '#F8FAFC', pt: 0, pb: 8
    }}
    >
      <Container
        maxWidth={false}
        disableGutters
        sx={{
          px: {
            xs: 1.25,
            sm: 1.75,
            md: 2.2,
            xl: 2.8
          }
        }}
      >
        <ScreenHeader title="Fleet Management" />
        <Stack spacing={2.2}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                lg: fleetShellColumns,
                xl: fleetShellColumns
              },
              gap: {
                xs: 2,
                md: 2.2
              },
              alignItems: 'flex-start'
            }}
          >
            {!shouldUseHoverVehicleRail && (
              <Box sx={{ minWidth: 0 }}>
                {isFleetLoading && renderFleetHeaderLoading()}
                {!isFleetLoading && isDesktopFleetShell && isVehicleRailCollapsed && renderCollapsedVehicleRail()}
                {!isFleetLoading && (!isDesktopFleetShell || !isVehicleRailCollapsed) && renderVehicleSelectionRail()}
              </Box>
            )}
            {!isFleetLoading && shouldUseHoverVehicleRail && renderVehicleSelectionHoverRail()}
            <Box sx={{ minWidth: 0 }}>
              {renderFleetWorkspace()}
            </Box>
          </Box>
        </Stack>
      </Container>
      {eventsLocationDialog}
    </Box>
  );
};

export default FleetManagement;
